// Словари интерфейса. RU — источник истины по набору ключей: EN и ZH обязаны
// покрывать ровно те же ключи, за этим следит тип TranslationKey.
//
// Плейсхолдеры вида {name} подставляются в t() вторым аргументом.

export const LANGS = ['ru', 'en', 'es', 'el', 'zh'] as const;
export type Lang = (typeof LANGS)[number];

/** Подпись языка в переключателе: флаг + название на самом языке. */
export const LANG_META: Record<Lang, { flag: string; name: string }> = {
  ru: { flag: '🇷🇺', name: 'Русский' },
  en: { flag: '🇬🇧', name: 'English' },
  es: { flag: '🇪🇸', name: 'Español' },
  el: { flag: '🇬🇷', name: 'Ελληνικά' },
  zh: { flag: '🇨🇳', name: '中文' },
};

export const RU = {
  // ── Оболочка ────────────────────────────────────────────────────────────
  'app.brand': 'Планируй поездку',
  'app.language': 'Язык интерфейса',
  'app.menu': 'Меню',
  // Разделы бургер-меню: настройки поездки, вида и аккаунта — разные вещи.
  'menu.section.trip': 'Поездка',
  'menu.section.trips': 'Поездки',
  'menu.section.view': 'Вид',
  'menu.section.account': 'Аккаунт',
  'app.library': 'Библиотека блоков',
  'app.board': 'Варианты поездки',
  'app.compare': 'Сравнение вариантов',
  'app.panel.hide': 'Скрыть: {name}',
  'app.panel.show': 'Показать: {name}',
  'app.panel.width': 'Ширина панели «{name}»',

  // ── Тулбар ──────────────────────────────────────────────────────────────
  'toolbar.travelers': 'Количество путешественников',
  'toolbar.travelers.dec': 'Уменьшить количество путешественников',
  'toolbar.travelers.inc': 'Увеличить количество путешественников',
  'toolbar.currency': 'Валюта',
  'toolbar.currency.tip': 'Валюта всех сумм и сравнения',
  'toolbar.rates': 'Валюты и курсы',
  'toolbar.rates.tip': 'Валюты и курсы — задаются к {base}',

  // ── Персонализация ──────────────────────────────────────────────────────
  // Названия тем (Linen, Darkula, …) — имена собственные и не переводятся.
  'person.title': 'Персонализация',
  'person.tip': 'Персонализация: тема оформления и размер шрифта',
  'person.theme': 'Тема оформления',
  'person.mode.light': 'светлая',
  'person.mode.dark': 'тёмная',
  'person.size': 'Размер шрифта',
  'person.size.compact': 'Компактный',
  'person.size.dense': 'Плотный',
  'person.size.default': 'Обычный',
  'person.size.large': 'Крупный',
  'person.size.larger': 'Крупнее',
  'person.size.xlarge': 'Очень крупный',
  'person.size.huge': 'Максимальный',
  'person.space': 'Отступы',
  'person.space.tight': 'Тесные',
  'person.space.dense': 'Плотные',
  'person.space.default': 'Обычные',
  'person.space.roomy': 'Просторные',
  'person.space.wide': 'Свободные',
  'person.space.max': 'Максимальные',
  'person.note': 'Настройки внешнего вида хранятся только в этом браузере: в состояние поездки и на сервер они не попадают.',

  // ── Справочник валют ────────────────────────────────────────────────────
  'currencies.title': 'Валюты',
  'currencies.hint': 'Курс — сколько {base} в одной единице валюты. Базовая валюта {base} всегда равна 1 и не удаляется.',
  'currencies.code': 'Код',
  'currencies.symbol': 'Символ',
  'currencies.rate': 'Курс к {base}',
  'currencies.actions': 'Действия',
  'currencies.symbolOf': 'Символ валюты {code}',
  'currencies.rateOf': 'Курс валюты {code}',
  'currencies.add': 'Добавить',
  'currencies.remove.base': 'Базовую валюту удалить нельзя',
  'currencies.remove.display': 'Валюта выбрана для отображения',
  'currencies.remove.used': 'Используется в ценах: {count}',
  'currencies.error.rate': 'Курс должен быть числом больше 0',
  'currencies.add.currency': 'Валюта',
  'currencies.add.rateLabel': '1 {code} = ? {base}',
  'currencies.add.empty': 'Все валюты из списка уже добавлены.',
  'currency.name.USD': 'Доллар США',
  'currency.name.EUR': 'Евро',
  'currency.name.GBP': 'Фунт стерлингов',
  'currency.name.CHF': 'Швейцарский франк',
  'currency.name.JPY': 'Японская иена',
  'currency.name.CNY': 'Китайский юань',
  'currency.name.TRY': 'Турецкая лира',
  'currency.name.AED': 'Дирхам ОАЭ',
  'currency.name.GEL': 'Грузинский лари',
  'currency.name.AMD': 'Армянский драм',
  'currency.name.RUB': 'Российский рубль',
  'currency.name.KZT': 'Казахстанский тенге',
  'currency.name.PLN': 'Польский злотый',
  'currency.name.CZK': 'Чешская крона',
  'currency.name.HUF': 'Венгерский форинт',
  'currency.name.RON': 'Румынский лей',
  'currency.name.BGN': 'Болгарский лев',
  'currency.name.RSD': 'Сербский динар',
  'currency.name.SEK': 'Шведская крона',
  'currency.name.NOK': 'Норвежская крона',
  'currency.name.DKK': 'Датская крона',
  'currency.name.ILS': 'Израильский новый шекель',
  'currency.name.THB': 'Тайский бат',
  'currency.name.VND': 'Вьетнамский донг',
  'currency.name.INR': 'Индийская рупия',
  'currency.name.IDR': 'Индонезийская рупия',
  'currency.name.MYR': 'Малайзийский ринггит',
  'currency.name.SGD': 'Сингапурский доллар',
  'currency.name.EGP': 'Египетский фунт',
  'currency.name.MAD': 'Марокканский дирхам',

  // ── Библиотека ──────────────────────────────────────────────────────────
  'library.title': 'Библиотека',
  'library.count.one': '{count} блок',
  'library.count.few': '{count} блока',
  'library.count.many': '{count} блоков',
  'library.count.other': '{count} блока',
  'library.add': '+ Блок',
  'library.search': 'Поиск по библиотеке',
  'library.search.placeholder': 'Поиск по библиотеке…',
  'library.empty': 'Библиотека пуста',
  'library.empty.hint': 'Добавьте первый блок кнопкой «+ Блок» или разберите ссылку.',
  'library.nothingFound': 'Ничего не найдено',
  'library.converted': 'конвертировано',
  'library.edit': 'Изменить',
  'library.delete': 'Удалить',
  'library.clone': 'Клонировать',
  'library.delete.title': 'Удалить блок?',
  'library.delete.used.one': 'Блок будет убран из {count} варианта. Действие нельзя отменить.',
  'library.delete.used.few': 'Блок будет убран из {count} вариантов. Действие нельзя отменить.',
  'library.delete.used.many': 'Блок будет убран из {count} вариантов. Действие нельзя отменить.',
  'library.delete.used.other': 'Блок будет убран из {count} вариантов. Действие нельзя отменить.',
  'library.delete.unused': 'Этот блок сейчас не используется ни в одном варианте. Действие нельзя отменить.',
  'library.locked': 'Блок входит в заблокированный вариант',
  'library.addTo': 'Добавить в…',
  'library.addTo.pick': 'Выбор варианта',
  'library.addTo.none': 'Сначала создайте вариант',
  'library.addTo.locked': 'Вариант заблокирован',

  // ── Варианты ────────────────────────────────────────────────────────────
  // Заголовок карточки «Варианты» — только для мобильного вида: там у зоны
  // канваса появляется своя шапка, как у библиотеки и итога (карточки разъехались
  // визуально). На десктопе не показывается — секции достаточно aria-label из
  // App.tsx, второй подписи там не нужно.
  'board.title': 'Варианты',
  'variant.add': '+ Вариант',
  // Подпись кнопки на узком экране: рядом с «Магией» ей достаётся только
  // ~20% ширины ряда, «+ Вариант» туда не помещается. Полное имя кнопки
  // остаётся доступным через aria-label — сокращаем только то, что видно.
  'variant.add.short': '+',
  'variant.add.max': 'Максимум {count} варианта',
  'variant.empty.title': 'Пока нет ни одного варианта',
  'variant.empty.hint': 'Создайте вариант, чтобы начать собирать маршрут путешествия.',
  'variant.name': 'Название варианта',
  'variant.rename': 'Переименовать',
  'variant.collapse': 'Свернуть вариант',
  'variant.expand': 'Развернуть вариант',
  'variant.order': 'Порядок варианта',
  'variant.moveUp': 'Переместить вариант выше',
  'variant.moveDown': 'Переместить вариант ниже',
  'variant.lock': 'Заблокировать вариант от изменений',
  'variant.unlock': 'Вариант заблокирован — нажмите, чтобы разрешить изменения',
  'variant.locked': 'Вариант заблокирован',
  'variant.menu': 'Меню варианта',
  'variant.color': 'Цвет варианта',
  'variant.colorOf': 'Цвет {color}',
  'variant.clone': 'Клонировать вариант',
  'variant.cloneName': '{name} — копия',
  'variant.delete': 'Удалить вариант',
  'variant.delete.title': 'Удалить вариант?',
  'variant.delete.body': 'Вариант «{name}» будет удалён. Блоки останутся в библиотеке. Действие нельзя отменить.',
  'variant.defaultName': 'Вариант {n}',

  // ── Таймлайн ────────────────────────────────────────────────────────────
  'timeline.empty.title': 'Перетащите сюда первый сегмент',
  'timeline.empty.hint': 'Или используйте кнопку «Добавить в…» на карточке блока в библиотеке слева.',
  'timeline.copyHere': 'копировать в вариант',
  'timeline.remove': 'Убрать',
  'timeline.moveUp': 'Переместить вверх',
  'timeline.moveDown': 'Переместить вниз',
  'timeline.lockedNote': 'заблокировано',
  'timeline.lockedNote.tip': 'Вариант заблокирован — снимите замок в шапке',
  'timeline.pairPrice': 'в цене пары',
  'timeline.dropToRemove': 'Отпустите здесь, чтобы убрать блок из варианта',
  'timeline.layover': 'стыковка {duration}',
  'timeline.layover.unknown': 'время стыковки неизвестно: нет таймзоны',
  'timeline.layover.transferCounted': 'переезд учтён',
  'timeline.layover.lodgingCounted': 'ночёвка учтена',
  'timeline.layover.bothCounted': 'переезд и ночёвка учтены',
  'timeline.stay': 'на месте {duration} — отдых, путешествие или дела',

  // ── Сводка варианта ─────────────────────────────────────────────────────
  'summary.rides.one': '{count} переезд',
  'summary.rides.few': '{count} переезда',
  'summary.rides.many': '{count} переездов',
  'summary.rides.other': '{count} переезда',
  'summary.nights.one': '{count} ночь',
  'summary.nights.few': '{count} ночи',
  'summary.nights.many': '{count} ночей',
  'summary.nights.other': '{count} ночи',

  // ── Итоги ───────────────────────────────────────────────────────────────
  'totals.withBaggage': 'с багажом',
  'totals.perTraveler': 'на человека',
  'totals.flightTime': 'Налёт',
  'totals.doorToDoor': 'Дверь-в-дверь',
  'totals.nights': 'Ночей',
  'totals.breakdown': 'Разбивка по типам',
  'totals.converted': 'конвертировано',
  'totals.warnings.one': '{count} предупреждение',
  'totals.warnings.few': '{count} предупреждения',
  'totals.warnings.many': '{count} предупреждений',
  'totals.warnings.other': '{count} предупреждения',

  // ── Сравнение ───────────────────────────────────────────────────────────
  'labels.hour': 'ч',
  'labels.minute': 'м',
  'labels.perNight': '/ночь',
  'field.fromIata': 'Откуда (IATA)',
  'field.toIata': 'Куда (IATA)',
  'field.departLocal': 'Вылет (местное время)',
  'field.arriveLocal': 'Прилёт (местное время)',
  'field.priceScope': 'Тип цены',
  'field.linked': 'Парный рейс туда-обратно',
  'form.saveChanges': 'Сохранить',
  'form.create': 'Создать',
  'form.none': '— нет —',
  'form.pickFlight': '— выберите рейс —',
  'baggage.noFlights': 'В библиотеке пока нет ни одного рейса — сначала добавьте перелёт.',
  'compare.best': '{value}, лучшее значение',
  'compare.title': 'Сравнение вариантов',
  // Число вариантов в шапке-аккордеоне мобильного «Итога» — как у библиотеки
  // (library.count): «Итог · N вариантов», без него в свёрнутом виде подписи
  // не понять, что вообще внутри.
  'compare.count.one': '{count} вариант',
  'compare.count.few': '{count} варианта',
  'compare.count.many': '{count} вариантов',
  'compare.count.other': '{count} варианта',
  'compare.metric': 'Показатель',
  'compare.total': 'Итого',
  'compare.totalWithBaggage': 'Итого с багажом',
  'compare.flightTime': 'Чистый налёт',
  'compare.doorToDoor': 'Время дверь-в-дверь',
  'compare.nights': 'Ночей',
  'compare.warnings': 'Предупреждений',
  'compare.empty': 'Добавьте хотя бы один вариант, чтобы увидеть сравнение.',

  // ── Формы ───────────────────────────────────────────────────────────────
  'form.close': 'Закрыть',
  'form.cancel': 'Отмена',
  'form.save': 'Сохранить',
  'form.confirm': 'Подтвердить',
  'form.optional': 'необязательно',
  'form.new': 'Новый блок',
  'form.edit': 'Изменить',
  'form.notFound': 'Блок не найден',
  'form.gone': 'Этот блок больше не существует в библиотеке.',
  'form.decrease': 'Уменьшить',
  'form.increase': 'Увеличить',

  'kind.flight': 'Перелёт',
  'kind.transfer': 'Переезд',
  'kind.lodging': 'Жильё',
  'kind.taxi': 'Такси',
  'kind.food': 'Еда',
  'kind.baggage': 'Багаж',
  'kind.misc': 'Прочее',

  'kind.flight.desc': 'Рейс между аэропортами',
  'kind.transfer.desc': 'Трансфер, поезд, автобус между городами',
  'kind.lodging.desc': 'Отель, апартаменты — по ночам',
  'kind.taxi.desc': 'Поездка по городу',
  'kind.food.desc': 'Питание, по дням',
  'kind.baggage.desc': 'Платный багаж к конкретному рейсу',
  'kind.misc.desc': 'Любые другие расходы',

  'scope.one_way': 'Цена в одну сторону',
  'scope.round_trip_half': 'Половина цены туда-обратно',
  'scope.round_trip_full': 'Полная цена туда-обратно',
  'scope.pairNote': 'Цена будет учтена один раз — у пары total станет 0.',

  'field.from': 'Откуда',
  'field.to': 'Куда',
  'field.city': 'Город',
  'field.label': 'Название',
  'field.carrier': 'Перевозчик',
  'field.url': 'Ссылка на рейс или билет',
  'field.url.hint': 'Куда вернуться за подробностями: страница рейса, подборка или купленный билет.',
  'field.url.short': 'ссылка',
  'field.flightNumber': 'Номер рейса',
  'field.depart': 'Вылет',
  'field.arrive': 'Прилёт',
  'field.duration': 'Длительность, мин',
  'field.nights': 'Ночей',
  'field.days': 'Дней',
  'field.quantity': 'Количество',
  'field.price': 'Цена',
  'field.pricePerNight': 'Цена за ночь',
  'field.perPerson': 'Цена за человека',
  'field.wholeStay': 'На весь промежуток',
  'field.wholeStay.hint': 'Ночи считаются сами: от прилёта предыдущего рейса варианта до вылета следующего (не больше 365).',
  'field.wholeStay.need': 'Доступно, когда за блоком в варианте стоит следующий блок.',
  'labels.wholeStay': 'на весь промежуток',
  'field.flight': 'Рейс',
  'field.note': 'Заметка',
  'field.tzFrom': 'Таймзона вылета',
  'field.tzTo': 'Таймзона прилёта',

  'error.iata': 'Код аэропорта — ровно 3 латинские буквы',
  'error.departRequired': 'Укажите время вылета',
  'error.arriveRequired': 'Укажите время прилёта',
  'error.departDateRequired': 'Укажите дату и время вылета',
  'error.arriveDateRequired': 'Укажите дату и время прилёта',
  'error.arriveBeforeDepart': 'Прилёт должен быть позже вылета',
  'error.priceNegative': 'Цена должна быть числом ≥ 0',
  'error.fromRequired': 'Укажите пункт отправления',
  'error.toRequired': 'Укажите пункт назначения',
  'error.durationNegative': 'Длительность должна быть числом ≥ 0',
  'error.cityRequired': 'Укажите город',
  'error.nightsMin': 'Минимум 1 ночь',
  'error.labelRequired': 'Укажите название',
  'error.quantityMin': 'Минимум 1',
  'error.flightRequired': 'Выберите рейс',
  'warn.tzMissing': 'Таймзона не указана — длительность не будет точно посчитана',
  'warn.tzMissingParse': 'Таймзона не определена — время в пути не будет точно посчитано.',

  // ── Разбор ссылки/текста ────────────────────────────────────────────────
  'parse.missing.from': 'аэропорт вылета',
  'parse.missing.to': 'аэропорт прилёта',
  'parse.missing.fromTz': 'таймзона вылета',
  'parse.missing.toTz': 'таймзона прилёта',
  'parse.missing.departLocal': 'дата вылета',
  'parse.missing.departTime': 'время вылета',
  'parse.missing.arriveLocal': 'дата прилёта',
  'parse.missing.arriveTime': 'время прилёта',
  'parse.missing.carrier': 'перевозчик',
  'parse.missing.price': 'цена',

  'library.empty.text': 'Есть два способа добавить блок: собрать его руками или вставить то, что скопировали с сайта поиска авиабилетов или отелей.',
  'library.noResults': 'Ничего не найдено по запросу «{query}»',
  'library.resetSearch': 'Сбросить поиск',
  'library.group.flight': 'Перелёты',
  'library.group.transfer': 'Переезды',
  'library.group.lodging': 'Жильё',
  'library.group.taxi': 'Такси',
  'library.group.food': 'Еда',
  'library.group.baggage': 'Багаж',
  'library.group.misc': 'Прочее',
  'compare.hint': 'Сравнение работает от двух вариантов — добавьте ещё один, чтобы увидеть подсветку лучших значений.',
  'compare.caption': 'Сравнение вариантов поездки: строки — показатели, столбцы — варианты',

  // ── Синхронизация ───────────────────────────────────────────────────────
  'parse.flightN': 'Рейс {n}',
  'parse.notRecognised': 'Не распознано: {fields}. Проверьте и заполните вручную.',
  'parse.back': 'Назад',
  'parse.addSelected': 'Добавить выбранные ({count})',

  'sync.boot': 'загрузка…',
  'sync.local': 'локально',
  'sync.saving': 'сохранение…',
  'sync.synced': 'сохранено',
  'sync.conflict': 'версия сервера',
  'sync.rejected': 'не сохранено',
  'sync.boot.tip': 'Читаем состояние с сервера',
  'sync.local.tip': 'Работаем локально',
  'sync.saving.tip': 'Отправляем изменения на сервер',
  'sync.synced.tip': 'Состояние сохранено в базе на сервере',
  'sync.conflict.tip': 'Состояние изменилось на сервере, применена его версия',
  'sync.rejected.tip': 'Сервер отклонил состояние',
  'toolbar.snapshots': 'Снимки и история',
  'sync.snapshot': 'Снимок',
  'sync.snapshot.tip': 'Сохранить снимок: текущее состояние ляжет в базу на сервере',
  'sync.history.tip': 'История снимков: вернуться к ранее сохранённому состоянию',
  'sync.history': 'История',
  'sync.snapshots': 'Снимки на сервере',
  'sync.snapshot.saved': 'Снимок сохранён на сервере',
  'sync.snapshot.failed': 'Не удалось сохранить снимок',
  'sync.snapshot.notFound': 'Снимок не найден',
  'sync.snapshot.restored': 'Состояние восстановлено из снимка',
  'sync.snapshot.empty': 'Снимков нет. Кнопка «Снимок» сохраняет текущее состояние в базу — к нему можно вернуться позже.',
  'sync.snapshot.unnamed': 'без подписи',
  'sync.snapshot.label': 'Снимок {stamp}',

  // ── Предупреждения расчёта ──────────────────────────────────────────────
  'calcwarn.need_transfer': 'нужен переезд {from} → {to}',
  'calcwarn.risky_connection': 'рискованная стыковка {duration}',
  'calcwarn.negative_connection': 'не успеваете: вылет раньше прилёта',
  'calcwarn.long_layover': 'долгое ожидание, добавить ночёвку?',
  'calcwarn.orphan_baggage': 'рейс этого багажа не входит в вариант',
  'calcwarn.rt_full_unpaired': 'цена указана за туда-обратно',
  'calcwarn.missing_tz': 'неизвестна таймзона, длительность рейса не учтена',

  // ── Аккаунт и авторизация ───────────────────────────────────────────────
  'auth.signin': 'Войти',
  'auth.signup': 'Регистрация',
  'auth.signout': 'Выйти',
  'auth.email': 'Почта',
  'auth.password': 'Пароль',
  'auth.password.new': 'Новый пароль',
  'auth.password.current': 'Текущий пароль',
  'auth.password.change': 'Сменить пароль',
  'auth.password.changed': 'Пароль изменён.',
  'auth.name': 'Имя',
  'auth.submit.signup': 'Создать аккаунт',
  'auth.forgot.link': 'Забыли пароль?',
  'auth.forgot.title': 'Восстановление пароля',
  'auth.forgot.hint': 'Пришлём на этот адрес ссылку для смены пароля.',
  'auth.forgot.submit': 'Отправить ссылку',
  'auth.forgot.sent': 'Если аккаунт с такой почтой есть, письмо со ссылкой уже отправлено. Ссылка действует час.',
  'auth.forgot.back': 'Назад ко входу',
  'auth.reset.title': 'Новый пароль',
  'auth.reset.submit': 'Сохранить пароль',
  'auth.reset.done': 'Пароль изменён. Прежние входы закрыты — войдите заново.',
  'auth.verify.title': 'Подтверждение почты',
  'auth.verify.banner': 'Подтвердите почту — без этого не восстановить доступ к аккаунту.',
  'auth.verify.resend': 'Отправить письмо ещё раз',
  'auth.verify.sent': 'Письмо отправлено — проверьте почту.',
  'auth.verify.ok': 'Почта подтверждена.',
  'auth.verify.fail': 'Ссылка недействительна или устарела.',
  'auth.guest.hint': 'Без входа поездки хранятся только в этом браузере.',
  'auth.subtitle': 'Аккаунт нужен, чтобы поездки жили не только в этом браузере — и открывались с любого устройства.',
  'auth.tabs.label': 'Вход или регистрация',
  'auth.password.show': 'Показать пароль',
  'auth.password.hide': 'Скрыть пароль',
  'auth.capslock': 'Включён Caps Lock',
  'auth.busy': 'Подождите…',
  'auth.toApp': 'К планировщику',
  'auth.pw.rule': 'Не короче 10 символов.',
  'auth.pw.short': 'Слишком короткий: нужно хотя бы 10 символов',
  'auth.pw.long': 'Слишком длинный',
  'auth.pw.common': 'Слишком простой — такой подбирают первым',
  'auth.pw.same_as_email': 'Пароль не должен повторять адрес почты',
  'auth.err.invalid_credentials': 'Неверная почта или пароль.',
  'auth.err.invalid_email': 'Проверьте адрес почты.',
  'auth.err.weak_password': 'Такой пароль не подходит.',
  'auth.err.email_taken': 'Аккаунт с такой почтой уже есть.',
  'auth.err.blocked': 'Аккаунт заблокирован.',
  'auth.err.rate_limited': 'Слишком много попыток. Попробуйте позже.',
  'auth.err.bad_token': 'Ссылка недействительна или устарела.',
  'auth.err.unauthorized': 'Нужно войти.',
  'auth.err.forbidden': 'Недостаточно прав.',
  'auth.err.self_change': 'Себе роль и статус менять нельзя.',
  'auth.err.origin': 'Запрос отклонён из соображений безопасности.',
  'auth.err.network': 'Сервер недоступен.',
  'auth.err.unknown': 'Что-то пошло не так.',
  'auth.err.restorable': 'На эту почту уже был аккаунт — его можно восстановить.',
  'auth.restore.title': 'Восстановление аккаунта',
  'auth.restore.hint': 'На эту почту уже был аккаунт — введите прежний пароль, чтобы вернуть его вместе с поездками.',
  'auth.restore.submit': 'Восстановить',
  'auth.restore.back': 'Назад к регистрации',

  'account.title': 'Аккаунт',
  'account.section.account': 'Аккаунт',
  'account.appearance': 'Оформление',
  'account.guest.title': 'Вы не вошли',
  'account.emailState': 'Состояние почты',
  'account.verified': 'подтверждена',
  'account.unverified': 'не подтверждена',
  'account.save': 'Сохранить',
  'account.saved': 'Сохранено',
  'account.admin': 'Админка',
  'account.menu.admin.hint': 'Пользователи и настройки распознавания',
  'account.password.warning': 'После смены пароля остальные входы закроются.',
  'account.menu.language': 'Язык',
  'account.menu.language.hint': 'Язык интерфейса',
  'account.menu.trips.hint': 'Все ваши поездки',
  'account.menu.trips.hint.guest': 'Поездки доступны после входа',
  'account.menu.pricing': 'Прайсинг',
  'account.menu.pricing.hint': 'Планы, цены и дополнительные опции',
  'account.menu.settings': 'Настройки',
  'account.menu.settings.hint': 'Пароль, почта, удаление аккаунта',
  'account.menu.person.hint': 'Палитра, кегль и отступы — настроить интерфейс под себя',
  'account.menu.signout.hint': 'Завершить сеанс на этом устройстве',
  'account.delete.title': 'Удалить аккаунт',
  'account.delete.hint': 'Аккаунт и все его поездки станут недоступны сразу после удаления.',
  'account.delete.action': 'Удалить аккаунт',
  'account.delete.confirm': 'Аккаунт и все его поездки перестанут быть доступны. Восстановить доступ можно будет только повторной регистрацией на эту же почту.',

  // ── Админка ─────────────────────────────────────────────────────────────
  'admin.title': 'Пользователи',
  'admin.back': 'К планировщику',
  'admin.search': 'Поиск по почте или имени',
  'admin.col.user': 'Пользователь',
  'admin.col.role': 'Роль',
  'admin.col.status': 'Статус',
  'admin.col.created': 'Регистрация',
  'admin.col.lastLogin': 'Последний вход',
  'admin.col.data': 'Данные',
  'admin.col.actions': 'Действия',
  'admin.role.user': 'Пользователь',
  'admin.role.admin': 'Администратор',
  'admin.status.active': 'Активен',
  'admin.status.blocked': 'Заблокирован',
  'admin.status.deleted': 'Удалён',
  'admin.action.block': 'Заблокировать',
  'admin.action.unblock': 'Разблокировать',
  'admin.action.promote': 'Дать права админа',
  'admin.action.demote': 'Снять права админа',
  'admin.action.delete': 'Удалить',
  'admin.action.view': 'Данные',
  'admin.delete.confirm': 'Удалить {email} вместе со всеми поездками и снимками? Отменить это будет нельзя.',
  'admin.empty': 'Никого не нашлось',
  'admin.prev': 'Назад',
  'admin.next': 'Дальше',
  'admin.page': '{from}–{to} из {total}',
  'admin.forbidden': 'Нужны права администратора.',
  'admin.stats.users': 'Пользователей',
  'admin.stats.newWeek': 'Новых за неделю',
  'admin.stats.activeWeek': 'Заходили за неделю',
  'admin.stats.verified': 'Подтвердили почту',
  'admin.stats.blocked': 'Заблокировано',
  'admin.stats.snapshots': 'Снимков',
  'admin.stats.size': 'Объём данных',
  'admin.view.title': 'Данные: {email}',
  'admin.view.readonly': 'Чужие данные, только просмотр. Открытие записано в журнал.',
  'admin.view.empty': 'Пользователь ещё ничего не сохранял.',
  'admin.view.variants': 'Вариантов',
  'admin.view.segments': 'Блоков',
  'admin.view.showRaw': 'Показать JSON',
  'admin.view.hideRaw': 'Скрыть JSON',

  'sync.snapshot.beforeLogin': 'До входа в аккаунт',
  'auth.guest.locked': 'Доступно после входа в аккаунт',
  'variant.add.guest': 'Без входа доступно вариантов: {count}. Войдите, чтобы добавить ещё.',

  'share.title': 'Поделиться поездкой',
  // ── Поездки ─────────────────────────────────────────────────────────────
  'trips.title': 'Мои поездки',
  'trips.all': 'Мои поездки ({count})',
  'trips.none': 'ещё не заведена',
  'trips.untitled': 'Без названия',
  'trips.newName': 'Новая поездка',
  'trips.create': '+ Новая поездка',
  'trips.open': 'открыта',
  'trips.openAction': 'Открыть',
  'trips.rename': 'Переименовать',
  'trips.rename.ownerOnly': 'Название поездки меняет её владелец',
  'trips.name': 'Название поездки',
  'trips.copyLink': 'Копировать ссылку',
  'trips.shared': 'есть публичная ссылка',
  'trips.linkClosed': 'нет публичной ссылки',
  'trips.info.button': 'Подробности о поездке «{name}»',
  'trips.info.title': 'О поездке «{name}»',
  'trips.updated': 'изменена {date}',
  'trips.created': 'создана {date}',
  'trips.delete': 'Удалить',
  'trips.delete.title': 'Удалить поездку?',
  'trips.delete.text': 'Поездка «{name}» и её история будут удалены без возможности восстановления. Публичная ссылка на неё перестанет работать.',
  'trips.empty': 'Поездок пока нет. Заведите первую — и работа в ней начнёт сохраняться на сервере.',
  'trips.guest.title': 'Нужен аккаунт',
  'trips.guest': 'Несколько поездок доступны после входа: гостевая работа живёт только в этом браузере и одной поездкой.',
  'trips.back': 'К планировщику',
  'trips.section.mine': 'Мои поездки',
  'trips.section.shared': 'Общие поездки',
  'trips.section.shared.hint': 'Сюда вас позвали соавтором: содержимое править можно, распоряжаться самой поездкой — нет.',
  'trips.byOwner': 'владелец: {name}',
  'trips.members.one': '{count} соавтор',
  'trips.members.few': '{count} соавтора',
  'trips.members.many': '{count} соавторов',
  'trips.members.other': '{count} соавтора',
  'trips.leave': 'Выйти',
  'trips.leave.title': 'Выйти из поездки?',
  'trips.leave.text': 'Поездка «{name}» пропадёт из вашего списка. У владельца она останется, и он сможет позвать вас снова.',

  // ── Общий доступ к поездке ──────────────────────────────────────────────
  'trips.access': 'Доступ',
  'trips.access.title': 'Доступ к поездке «{name}»',
  'trips.access.lead': 'Соавторы правят ту же поездку, что и вы: библиотеку блоков, варианты, цены. Переименовывать её, публиковать ссылку и звать других может только владелец.',
  'trips.access.invite': 'Пригласить по почте',
  'trips.access.add': 'Пригласить',
  'trips.access.hint': 'Есть аккаунт — поездка появится в его списке сразу. Нет — на почту уйдёт приглашение, а доступ выдастся после регистрации.',
  'trips.access.pending': 'Аккаунта с почтой {email} ещё нет: мы отправили приглашение — доступ появится сразу после регистрации.',
  'trips.access.empty': 'Пока никого, кроме владельца.',
  'trips.access.you': 'это вы',
  'trips.access.remove': 'Убрать',
  'trips.access.role.owner': 'владелец',
  'trips.access.role.editor': 'соавтор',
  'trips.access.err.no_user': 'Человека с такой почтой в сервисе ещё нет. Попросите его зарегистрироваться и пригласите снова.',
  'trips.access.err.already_member': 'Этот человек уже в списке.',
  'trips.access.err.is_owner': 'Это владелец поездки.',
  'trips.access.err.too_many': 'Соавторов в одной поездке уже слишком много.',
  'trips.access.err.invalid_email': 'Проверьте адрес почты.',
  'trips.access.err.blocked': 'Этот аккаунт заблокирован.',
  'trips.access.err.unknown': 'Не получилось. Попробуйте ещё раз.',

  // ── Заготовки: шаблоны поездок и частые блоки ───────────────────────────
  'tpl.start': 'Начать с шаблона',
  'tpl.start.hint': 'Типовая поездка в один клик: блоки уже расставлены, цены пустые.',
  'tpl.create': 'Создать поездку',
  'tpl.fill': 'Собрать вариант',
  'tpl.page.note': 'Шаблон заведёт отдельную новую поездку. Уже существующие поездки не изменятся.',
  'tpl.board.note': 'Шаблон добавит вариант с этими блоками. Всё, что уже собрано, останется на месте.',
  'tpl.blocks': 'Частые блоки',
  'tpl.blocks.hint': 'Нажатие кладёт блок в библиотеку с нулевой ценой — останется вписать сумму.',
  'tpl.added': 'Блок «{name}» добавлен в библиотеку',
  'tpl.weekend.name': 'Уикенд на два дня',
  'tpl.weekend.desc': 'Две ночи в городе: дорога из аэропорта, жильё, еда и такси.',
  'tpl.beach.name': 'Неделя у моря',
  'tpl.beach.desc': 'Семь ночей на одном месте: трансфер до отеля, еда, экскурсии.',
  'tpl.business.name': 'Командировка',
  'tpl.business.desc': 'Три ночи по делам: жильё, такси, еда и связь.',
  'tpl.twoCities.name': 'Два города за поездку',
  'tpl.twoCities.desc': 'По три ночи в каждом и переезд между ними.',
  'tpl.mountains.name': 'Горы и лыжи',
  'tpl.mountains.desc': 'Пять ночей у склона: подъёмник, прокат снаряжения, еда.',
  'tpl.place.airport': 'Аэропорт',
  'tpl.place.city': 'Город',
  'tpl.place.hotel': 'Отель',
  'tpl.place.city1': 'Первый город',
  'tpl.place.city2': 'Второй город',
  'tpl.block.taxi': 'Такси по городу',
  'tpl.block.food': 'Еда',
  'tpl.block.insurance': 'Страховка',
  'tpl.block.esim': 'Связь и интернет',
  'tpl.block.tickets': 'Экскурсии и билеты',
  'tpl.block.visa': 'Виза и сборы',
  'tpl.block.skipass': 'Подъёмник',
  'tpl.block.gear': 'Прокат снаряжения',
  'share.tip': 'Ссылка на поездку для тех, у кого нет аккаунта',
  'share.hint': 'По ссылке видно копию поездки на момент публикации — только просмотр, без правок. Пока не нажать «Обновить», дальнейшие изменения читателю не видны.',
  'share.create': 'Создать ссылку',
  'share.update': 'Обновить',
  'share.revoke': 'Отключить ссылку',
  'share.copy': 'Копировать',
  'share.copied': 'Скопировано ✓',
  'share.link': 'Ссылка на поездку',
  'share.look': 'Ссылка откроется в вашей теме, размере и языке — читатель увидит поездку такой же, какой её собрали вы.',
  'share.updated': 'Обновлено: {date}',
  'share.views': 'просмотров: {count}',
  'share.failed': 'Не удалось создать ссылку.',
  'share.readonly': 'Только просмотр',
  'share.travelers': 'Путешественников: {count}',
  'share.cheapest': 'Дешевле всего',
  'share.segments': 'Из чего сложилась цена',
  'share.noPrice': 'Цены ещё не проставлены',
  'share.notFound': 'Ссылка не найдена или отключена.',
  'share.empty': 'В этой поездке пока нет вариантов.',
  'share.makeYours': 'Собрать свою поездку',
  // Превью ссылки — то, что мессенджер или соцсеть показывают вместо голого
  // адреса. Собирается на языке автора поездки: делится он своим кругом, и
  // испанская поездка должна прийти к его друзьям по-испански.
  'share.preview.variants.one': '{count} вариант',
  'share.preview.variants.few': '{count} варианта',
  'share.preview.variants.many': '{count} вариантов',
  'share.preview.variants.other': '{count} варианта',
  'share.preview.from': 'от {price}',
  'share.preview.tail': 'Сравнение цены и времени в Plan Your Trip.',
  'share.preview.empty': 'Поездка ещё собирается: вариантов пока нет.',

  // ── Библиотека: новые блоки ─────────────────────────────────────────────
  'library.new': 'new',
  'library.new.title': 'Блок добавлен недавно',
  'library.new.count': 'Новых: {count}',
  'library.new.dismiss': 'Отметить просмотренными',
  'library.fromSource': 'Из источника',
  'library.fromSource.desc': 'Можно распознать рейс из текста, ссылки или скриншота.',

  // ── Распознавание источников ────────────────────────────────────────────
  'recog.title': 'Добавить из источника',
  'recog.tab.text': 'Описание',
  'recog.tab.url': 'Ссылка',
  'recog.tab.screenshot': 'Скриншот',
  'recog.text.label': 'Выдача поиска или короткое описание рейса',
  'recog.text.placeholder': '27 декабря Wizz Air Ларнака 11:55 → Ереван 16:00, 149 $',
  'recog.url.label': 'Ссылка на рейс или бронирование',
  'recog.url.placeholder': 'https://…',
  'recog.url.invalid': 'Нужна ссылка http:// или https://',
  'recog.shot.label': 'Скриншоты выдачи, письма или билета',
  'recog.shot.drop': 'Перетащите картинки, вставьте из буфера (Ctrl+V) или выберите файлы',
  'recog.shot.pick': 'Выбрать файлы',
  'recog.shot.add': 'Добавить ещё',
  'recog.shot.remove': 'Убрать все',
  'recog.shot.removeOne': 'Убрать скриншот {num}',
  'recog.shot.tooBig': 'Картинка больше {limit} МБ',
  'recog.shot.tooMany': 'За раз распознаём не больше {limit} картинок — лишние не добавлены: {count}',
  'recog.shot.tooBigTotal': 'Вместе картинки не должны быть тяжелее {limit} МБ — не добавлено: {count}',
  'recog.shot.badType': 'Не похоже на картинку, которую можно прочитать: подойдут PNG, JPEG, WebP или HEIC',
  'recog.shot.ready': 'Картинок: {count} из {limit} · {size}',
  'recog.shot.multi': 'Несколько картинок уходят одним запросом — рейсы со всех попадут в общий список.',
  'recog.run': 'Распознать',
  'recog.running': 'Распознаём…',
  'recog.review': 'Проверьте и поправьте перед добавлением',
  'recog.model': 'Модель: {model}',
  'recog.local': 'Разобрано локально, без ИИ',
  'recog.fallback': 'ИИ не ответил — показан результат локального разбора.',
  'recog.privacy.ai': 'Текст, ссылка или картинка уходят выбранной модели ИИ. Ничего не сохраняется само: перед добавлением всё можно проверить и поправить.',
  'recog.privacy.local': 'Никаких сетевых запросов: разбирается только вставленный текст. Перед добавлением всё можно проверить и поправить.',
  'recog.off': 'ИИ-распознавание выключено администратором — остаётся локальный разбор текста.',
  'recog.off.guest': 'ИИ-распознавание доступно после входа в аккаунт.',
  'recog.nothing': 'Рейсы не найдены — попробуйте другой текст, ссылку или скриншот.',
  'recog.notes': 'Как разобрано',
  'recog.err.unauthorized': 'Нужен вход в аккаунт.',
  'recog.err.disabled': 'Распознавание выключено администратором.',
  'recog.err.no_model': 'Для этого источника не выбрана модель — задайте её в админке.',
  'recog.err.no_key': 'Не задан ключ ИИ-провайдера — задайте его в админке.',
  'recog.err.provider': 'Провайдер вернул ошибку: {message}',
  'recog.err.timeout': 'Модель не ответила вовремя. Попробуйте ещё раз.',
  'recog.err.rate_limited': 'Слишком много запросов. Попробуйте позже.',
  'recog.err.too_large': 'Запрос слишком большой.',
  'recog.err.bad_response': 'Модель ответила не по формату — разобрать нечего. Попробуйте ещё раз или пришлите меньше картинок за раз.',
  'recog.err.fetch_url': 'Не удалось открыть ссылку — вставьте текст или скриншот.',
  'recog.err.bad_request': 'Нечего распознавать.',
  'recog.err.unknown': 'Не удалось распознать. Попробуйте ещё раз.',

  // ── Магия вариантов ─────────────────────────────────────────────────────
  'magic.title': 'Магия вариантов',
  'magic.action': 'Магия',
  'magic.tip': 'Собрать лучшие варианты из библиотеки',
  'magic.from': 'Откуда',
  'magic.from.placeholder': 'LCA или Ларнака',
  'magic.to': 'Куда',
  'magic.to.placeholder': 'EVN или Ереван',
  'magic.roundTrip': 'Туда-обратно',
  'magic.stay': 'Пребывание, дней',
  'magic.stay.from': 'от',
  'magic.stay.to': 'до',
  'magic.stay.hint': 'Столько дней между прилётом в точку назначения и обратным вылетом.',
  'magic.wishes': 'Пожелания',
  'magic.wishes.placeholder': 'Без ночных стыковок, не дороже 500 $ на человека, максимум одна пересадка',
  'magic.count': 'Сколько вариантов',
  'magic.count.hint': 'Свободных мест на доске: {count}',
  'magic.run': 'Сделать магию',
  'magic.running': 'Колдуем…',
  'magic.library': 'В запрос уйдут все блоки библиотеки: {count}',
  'magic.library.empty': 'В библиотеке нет ни одного рейса — магии не из чего собирать варианты.',
  'magic.library.trimmed': 'В библиотеке {count} блоков, в выбранную модель поместятся {limit} — остальные в запрос не уйдут.',
  'magic.privacy': 'Список блоков библиотеки и ваши пожелания уходят выбранной модели ИИ. Варианты появятся на доске только после вашего подтверждения.',
  'magic.review': 'Проверьте варианты перед добавлением',
  'magic.model': 'Модель: {model}',
  'magic.notes': 'Замечания',
  'magic.nothing': 'Собрать вариант не удалось — уточните пожелания или добавьте в библиотеку больше рейсов.',
  'magic.add': 'Добавить варианты ({count})',
  'magic.back': 'Назад',
  'magic.stayDays': 'Пребывание: {days} дн.',
  'magic.segments': 'Блоков: {count}',
  'magic.noRoom': 'На доске нет свободного места для новых вариантов — удалите лишние.',
  'magic.tooMany': 'Выбрано больше вариантов, чем помещается на доске: снимите лишние галочки.',
  'magic.off': 'Магия выключена администратором.',
  'magic.off.guest': 'Магия доступна после входа в аккаунт.',
  'magic.err.unauthorized': 'Нужен вход в аккаунт.',
  'magic.err.disabled': 'ИИ-функции выключены администратором.',
  'magic.err.no_model': 'Для магии не выбрана модель — задайте её в админке.',
  'magic.err.no_key': 'Не задан ключ ИИ-провайдера — задайте его в админке.',
  'magic.err.bad_request': 'Запрос не принят: {message}',
  'magic.err.too_large': 'Библиотека слишком большая для одного запроса.',
  'magic.err.rate_limited': 'Слишком много запросов. Попробуйте позже.',
  'magic.err.timeout': 'Модель не ответила вовремя. Попробуйте ещё раз.',
  'magic.err.provider': 'Провайдер вернул ошибку: {message}',
  'magic.err.bad_response': 'Ответ модели не удалось разобрать.',
  'magic.err.unknown': 'Магия не сработала. Попробуйте ещё раз.',

  // ── Админка: ИИ-функции ─────────────────────────────────────────────────
  'admin.tab.users': 'Пользователи',
  'admin.tab.ai': 'ИИ',
  'admin.tab.logs': 'Журнал',
  'admin.tab.chats': 'Чаты',
  'admin.tab.pricing': 'Тарифы',
  'admin.tab.trips': 'Поездки',
  'admin.action.enter': 'Войти как',
  'admin.action.limits': 'Лимиты',
  'admin.limits.title': 'Лимиты пользователя',
  'admin.limits.hint': 'Пустое поле — значение по умолчанию.',
  'admin.limits.default': 'по умолчанию',
  'admin.limits.maxVariants': 'Вариантов на доске',
  'admin.limits.aiDaily': 'Запросов к ИИ в сутки',
  'admin.limits.magicDaily': 'Запусков магии в сутки',
  'admin.limits.maxTrips': 'Поездок',
  'admin.logs.filter.problems': 'Ошибки и предупреждения',
  'admin.logs.filter.ai': 'Запросы к ИИ',
  'admin.logs.filter.all': 'Все',
  'admin.logs.clear': 'Очистить журнал',
  'admin.logs.clear.confirm': 'Удалить все записи журнала?',
  'admin.logs.empty': 'Записей нет',
  'admin.logs.retention': 'Журнал хранится 7 дней, ротация ежедневная',
  'admin.logs.noDetails': 'У записи нет подробностей',
  'admin.logs.details.meta': 'Сводка',
  'admin.logs.details.request': 'Запрос к модели',
  'admin.logs.details.response': 'Ответ модели',
  'admin.chats.new': 'Новый диалог',
  'admin.chats.search': 'Почта пользователя…',
  'admin.chats.empty': 'Диалогов пока нет',
  'admin.chats.pick': 'Выберите диалог слева',
  'admin.chats.start': 'Сообщений ещё нет — напишите первым',
  'admin.chats.placeholder': 'Ответ пользователю…',
  'admin.chats.send': 'Отправить',
  'admin.chats.clear': 'Удалить диалог',
  'admin.chats.clear.confirm': 'Удалить всю переписку с {email}?',
  'admin.chats.close': 'Завершить разговор',
  'admin.chats.close.confirm': 'Завершить разговор с {email}? История останется видна только администраторам.',
  'admin.chats.lang': 'Язык пользователя',
  'admin.chats.client': 'Клиент пользователя',
  'admin.chats.files': 'Файлы в чате',
  'hero.title': 'Планируйте поездку вариантами',
  'hero.lead': 'Это демо-поездка: три маршрута рядом — цены, стыковки и предупреждения на одной доске. Потрогайте её или соберите свою; аккаунт сохранит всё и откроет магию ИИ.',
  'hero.signup': 'Создать аккаунт',
  'hero.try': 'Просто попробовать',
  'support.title': 'Поддержка',
  'support.close': 'Закрыть',
  'support.minimize': 'Свернуть',
  'support.closed': 'Разговор был завершён',
  'support.attach': 'Прикрепить файл',
  'support.attach.bad': 'Файл не подходит: изображение или PDF до 3 МБ',
  'support.empty': 'Напишите нам — ответим здесь',
  'support.placeholder': 'Сообщение…',
  'support.send': 'Отправить',
  'support.team': 'Поддержка',
  'support.err': 'Не отправилось — попробуйте ещё раз',
  'admin.stats.trips': 'Поездок',
  'admin.stats.tripsShared': 'С соавторами',
  'admin.stats.tripsPublished': 'Опубликовано',
  'admin.trips.search': 'Поиск по названию или владельцу',
  'admin.trips.col.trip': 'Поездка',
  'admin.trips.col.owner': 'Владелец',
  'admin.trips.col.members': 'Соавторы',
  'admin.trips.col.share': 'Ссылка',
  'admin.trips.col.updated': 'Изменена',
  'admin.trips.col.created': 'Создана',
  'admin.trips.published': 'Открыть',
  'admin.trips.enter': 'Войти как владелец',
  'admin.trips.empty': 'Поездок не нашлось',
  'admin.trips.view.title': 'Поездка: {name}',
  'admin.trips.view.empty': 'В поездке пока ничего нет.',
  'impersonate.confirm': 'Войти в аккаунт {email}? Дальше вы работаете от его имени: всё, что вы измените, изменится у него. Запись об этом останется в журнале.',
  'impersonate.bar': 'Вы работаете от имени {email}. Ваш аккаунт: {admin}.',
  'impersonate.stop': 'Вернуться в свой аккаунт',
  'auth.err.impersonating': 'В режиме «от имени» это действие недоступно.',
  'admin.pricing.source': 'Здесь правится русский текст и устройство страницы. Остальные языки — на вкладке «Переводы».',
  'admin.tr.note': 'Слева оригинал, справа перевод. Пустое поле означает, что посетителю покажут оригинал.',
  'admin.tr.target': 'Язык перевода',
  'admin.tr.progress': 'переведено {done} из {total}',
  'admin.tr.done': 'Переведено полностью',
  'admin.tr.onlyEmpty': 'Только непереведённое',
  'admin.tr.source': 'Оригинал',
  'admin.tr.translation': 'Перевод',
  'admin.pricing.save': 'Сохранить',
  'admin.pricing.saved': 'Сохранено',
  'admin.pricing.lang': 'Язык',
  'admin.pricing.reset': 'Вернуть встроенное',
  'admin.pricing.reset.title': 'Вернуть встроенный контент?',
  'admin.pricing.reset.text': 'Все правки страницы тарифов будут удалены, страница снова соберётся из того, что заложено в коде. Отменить это нельзя.',
  'admin.pricing.updated': 'изменено {date}, {name}',
  'admin.pricing.never': 'правок ещё не было',
  'admin.pricing.section.price': 'Цены',
  'admin.pricing.section.texts': 'Тексты страницы',
  'admin.pricing.section.sections': 'Разделы таблицы',
  'admin.pricing.section.features': 'Строки тарифов',
  'admin.pricing.section.highlights': 'В карточках тарифов',
  'admin.pricing.section.extras': 'Разовые покупки',
  'admin.pricing.section.soon': 'В работе',
  'admin.pricing.section.faq': 'Вопросы',
  'admin.pricing.currency': 'Валюта',
  'admin.pricing.monthly': 'В месяц',
  'admin.pricing.yearly': 'За год',
  'admin.pricing.discount': 'скидка {percent}%',
  'admin.pricing.discount.none': 'год не выгоднее месяца',
  'admin.pricing.id': 'Идентификатор',
  'admin.pricing.name': 'Название',
  'admin.pricing.hint': 'Подсказка',
  'admin.pricing.question': 'Вопрос',
  'admin.pricing.answer': 'Ответ',
  'admin.pricing.price': 'Цена',
  'admin.pricing.parent': 'Раздел',
  'admin.pricing.free': 'Free',
  'admin.pricing.pro': 'Pro',
  'admin.pricing.shown': 'Показывать',
  'admin.pricing.add': 'Добавить',
  'admin.pricing.remove': 'Удалить',
  'admin.pricing.up': 'Выше',
  'admin.pricing.down': 'Ниже',
  'admin.pricing.kind.count': 'Число',
  'admin.pricing.kind.unlimited': 'Без лимита',
  'admin.pricing.kind.yes': 'Есть',
  'admin.pricing.kind.none': 'Нет',
  'admin.pricing.kind.text': 'Свой текст',
  'admin.pricing.per.total': 'всего',
  'admin.pricing.per.month': 'в месяц',
  'admin.pricing.key': 'Ключ словаря',
  'admin.pricing.highlights.hint': 'Отмеченные строки попадают в карточку тарифа наверху страницы. Пять — предел читаемости.',
  'admin.pricing.empty': 'Пока пусто',
  'admin.ai.title': 'ИИ-функции',
  'admin.ai.hint': 'Распознавание источников, магия вариантов и проверка цен работают через ИИ-провайдера. Модель у каждой задачи своя: скриншоту нужна модель с картинками, магии — с рассуждением, ценам — с веб-поиском.',
  'admin.ai.enabled': 'Включить ИИ-функции',
  'admin.ai.enabled.hint': 'Выключено — остаётся локальный разбор текста, а магия вариантов недоступна.',
  'admin.ai.key': 'Ключ ИИ-провайдера',
  'admin.ai.key.present': 'Ключ задан (…{tail})',
  'admin.ai.key.env': 'Ключ берётся из переменной окружения сервера',
  'admin.ai.key.missing': 'Ключ не задан',
  'admin.ai.key.placeholder': 'sk-…',
  'admin.ai.key.hint': 'Ключ хранится на сервере и в браузер не отдаётся.',
  'admin.ai.key.clear': 'Удалить ключ',
  'admin.ai.source.screenshot': 'Скриншот',
  'admin.ai.source.url': 'Ссылка',
  'admin.ai.source.text': 'Текстовое описание',
  'admin.ai.source.screenshot.hint': 'Модель обязана принимать изображения.',
  'admin.ai.source.url.hint': 'Сервер сам открывает страницу и отдаёт модели её текст.',
  'admin.ai.source.text.hint': 'Вставленная выдача поиска или пара строк от руки.',
  'admin.ai.source.magic': 'Магия: подбор вариантов',
  'admin.ai.source.magic.hint': 'Собирает готовые варианты поездки из блоков библиотеки: выбирает рейсы, выстраивает их по времени и следит, чтобы перелёты не налезали друг на друга.',
  'admin.ai.source.prices': 'Цены: актуальная цена рейса',
  'admin.ai.source.prices.hint': 'Ищет по сайтам бронирования цену одного конкретного рейса и возвращает найденные предложения со ссылкой на источник.',
  'admin.ai.source.settings': 'Настройки этой задачи',
  'admin.ai.source.settings.hint': 'Пустое поле — действует общее значение снизу. Суточный лимит здесь считается отдельно от общего: сработает тот, который строже.',
  'admin.ai.override.inherit': 'как общая: {value}',
  'admin.ai.advanced.hint': 'Общие значения для всех задач. Любую из них можно настроить отдельно — в её карточке.',
  'admin.ai.req.title': 'Требования к модели для магии',
  'admin.ai.req.context': 'Контекст от 128K токенов: в один запрос уходит вся библиотека блоков.',
  'admin.ai.req.json': 'Поддержка структурированного ответа (json_schema) и точное следование схеме.',
  'admin.ai.req.reasoning': 'Сильные рассуждения: модель сравнивает время в UTC, считает стыковки и планирует маршрут.',
  'admin.ai.req.tokens': 'Ответ длинный, поэтому для магии сервер поднимает лимит токенов минимум до 2000, а таймаут — до 60 секунд. У reasoning-моделей в этот же лимит попадают токены рассуждения — ставьте 8000, иначе ответ приедет обрезанным.',
  'admin.ai.req.vision': 'Картинки не нужны: сюда уходит только текст, модель со зрением преимуществ не даёт.',
  'admin.ai.req.temp': 'Температура 0: подбор должен быть воспроизводимым. Reasoning-модели (GPT-5) параметр игнорируют — это нормально.',
  'admin.ai.req.recommend': 'Рекомендуем модели уровня pro/thinking (Gemini 2.5 Pro, Claude Sonnet 4.5, GPT-5). Дешёвые быстрые модели путают хронологию и выдумывают несуществующие id — сервер такие варианты отбрасывает, и человек видит пустой результат.',
  'admin.ai.recommend': 'Дёшево и достаточно',
  'admin.ai.recommend.hint': 'Цены $ за 1M токенов на {date}, порядок — от самой дешёвой к самой надёжной. Это подсказка, а не ограничение: выбрать можно любую модель. Нажмите, чтобы подставить.',
  'admin.ai.model': 'Модель',
  'admin.ai.model.search': 'Поиск по моделям…',
  'admin.ai.model.none': 'Не выбрана',
  'admin.ai.model.vision': 'картинки',
  'admin.ai.model.free': 'бесплатная',
  'admin.ai.model.context': 'контекст {tokens}',
  'admin.ai.model.price': '{in} / {out} $ за 1M токенов',
  'admin.ai.model.custom': 'Задать id вручную',
  'admin.ai.model.tools': 'инструменты',
  'admin.ai.model.reasoning': 'ризонинг',
  'admin.ai.model.nojson': 'без строгого JSON',
  'admin.ai.model.warn.json': 'Не подойдёт: сервер шлёт строгий json_schema, а эта модель его не принимает — вернётся ошибка провайдера, а не разбор.',
  'admin.ai.model.warn.vision': 'Не подойдёт: скриншоты уходят картинками, а модель принимает только текст.',
  'admin.ai.model.warn.tools': 'Не подойдёт: веб-поиск цен подключается через инструменты, а модель их не поддерживает.',
  'admin.ai.model.warn.context': 'Мало контекста: в один запрос уходит вся библиотека блоков, нужно от {min}.',
  'admin.ai.model.warn.reasoning': 'Рассуждение у этой модели не отключается, и его токены идут в тот же лимит ответа. Поставьте максимум токенов не меньше {tokens}, иначе ответ приедет обрезанным.',
  'admin.ai.tune.recommend': 'Рекомендуем: {values}',
  'admin.ai.tune.apply': 'Проставить',
  'admin.ai.model.unknown': 'Модели {model} нет в списке провайдера',
  'admin.ai.models.reload': 'Обновить список',
  'admin.ai.models.loading': 'Загружаем список моделей…',
  'admin.ai.models.failed': 'Не удалось получить список моделей: {message}',
  'admin.ai.models.count': 'Моделей: {count}',
  'admin.ai.advanced': 'Дополнительно',
  'admin.ai.maxTokens': 'Максимум токенов ответа',
  'admin.ai.temperature': 'Температура',
  'admin.ai.timeout': 'Таймаут, секунды',
  'admin.ai.limit': 'Запросов в сутки на пользователя',
  'admin.ai.maxImages': 'Скриншотов за раз',
  'admin.ai.maxImages.hint': 'Сколько картинок пользователь отправляет одним запросом при распознавании скриншотов. Больше картинок — длиннее и дороже ответ модели.',
  'admin.ai.save': 'Сохранить',
  'admin.ai.saved': 'Сохранено',
  'admin.ai.save.failed': 'Не удалось сохранить: {message}',
  'admin.ai.test': 'Проверить',
  'admin.ai.test.running': 'Проверяем…',
  'admin.ai.test.ok': 'Модель ответила за {ms} мс',
  'admin.ai.test.failed': 'Проверка не прошла: {message}',
  'admin.ai.updated': 'Изменено {date}',

  // ── Онбординг ───────────────────────────────────────────────────────────
  // Тур из четырёх шагов: три направления → открытая поездка → два варианта
  // рядом → итог. Подписи макета (mock.*) — часть картинки, а не интерфейса:
  // маршруты в ней остаются кодами IATA и не переводятся.
  'onboarding.title': 'Как это работает',
  'onboarding.tip': 'Короткий тур: как собрать и сравнить варианты поездки',
  'onboarding.step': 'Шаг {current} из {total}',
  'onboarding.goto': 'Шаг {n}',
  'onboarding.skip': 'Пропустить',
  'onboarding.back': 'Назад',
  'onboarding.next': 'Дальше',
  'onboarding.done': 'Понятно',
  'onboarding.s1.title': 'Сначала — куда',
  'onboarding.s1.text': 'Азия, Европа, Америка: заведите поездку на каждое направление, которое рассматриваете. Они лежат рядом и не мешают друг другу, а начать можно с готового шаблона.',
  'onboarding.s2.title': 'Внутри поездки',
  'onboarding.s2.text': 'Слева библиотека блоков: перелёты, ночёвки, трансферы, любые траты. Справа — варианты маршрута, которые вы из них собираете.',
  'onboarding.s3.title': 'Ссылка, текст или скриншот',
  'onboarding.s3.text': 'Рейс не нужно вбивать руками. Вставьте ссылку на бронирование, скопированную выдачу поиска или скриншот письма — приложение разберёт маршрут, время и цену, а вам останется проверить.',
  'onboarding.s4.title': 'Два варианта рядом',
  'onboarding.s4.text': 'Дешёвые билеты идут с пересадками, а ночевать в пути приходится за деньги. Прямые дороже, зато ночёвок нет. Соберите оба варианта и поставьте рядом.',
  'onboarding.s5.title': 'Кто дешевле, покажет итог',
  'onboarding.s5.text': 'Внизу каждого варианта считается полная сумма: билеты, жильё и всё остальное. Тут и выясняется, что дешёвые билеты вышли дороже.',
  'onboarding.s6.title': 'Планировать можно вдвоём',
  'onboarding.s6.text': 'Пригласите соавтора по почте — он правит ту же поездку: библиотеку, варианты, цены. Переименовать поездку и звать других может только владелец.',
  'onboarding.s7.title': 'Показать результат',
  'onboarding.s7.text': 'Публичная ссылка открывает поездку тем, у кого нет аккаунта: только просмотр, копия на момент публикации. Дальнейшие правки читателю не видны, пока вы не нажмёте «Обновить».',
  'onboarding.mock.asia': 'Азия',
  'onboarding.mock.europe': 'Европа',
  'onboarding.mock.america': 'Америка',
  'onboarding.mock.cheap': 'Эконом',
  'onboarding.mock.cheap.note': 'дешёвые билеты, 2 ночёвки в пути',
  'onboarding.mock.direct': 'Прямой',
  'onboarding.mock.direct.note': 'билеты дороже, ночёвок нет',
  'onboarding.mock.night.ath': 'Ночь в Афинах',
  'onboarding.mock.night.mxp': 'Ночь в Милане',
  'onboarding.mock.total': 'Итого',
  'onboarding.mock.win': 'дешевле на {sum}',

  // ── Тарифы ──────────────────────────────────────────────────────────────
  'pricing.title': 'Тарифы',
  'pricing.subtitle': 'Планировать поездку можно бесплатно. Pro снимает лимиты и включает то, что считает и следит за вас.',
  'pricing.seo.title': 'Тарифы — Free и Pro',
  'pricing.seo.description': 'Что входит в бесплатный план и что даёт Pro: лимиты поездок, распознавание броней, подсказки по стране и слежение за ценой.',
  'pricing.back': 'К планировщику',
  'pricing.billing.monthly': 'Помесячно',
  'pricing.billing.yearly': 'На год',
  'pricing.billing.save': 'дешевле на {percent}%',
  'pricing.price.free': 'Бесплатно',
  'pricing.price.perMonth': '{amount} / мес',
  'pricing.price.yearlyHint': '{amount} в год, списывается разом',
  'pricing.price.monthlyHint': 'Отменить можно в любой момент',
  'pricing.plan.free': 'Free',
  'pricing.plan.free.tagline': 'Собрать одну поездку и сравнить два маршрута.',
  'pricing.plan.pro': 'Pro',
  'pricing.plan.pro.tagline': 'Для тех, кто летает часто и планирует всерьёз.',
  'pricing.cta.free': 'Начать бесплатно',
  'pricing.cta.pro': 'Перейти на Pro',
  'pricing.cta.soon': 'Оплата скоро',
  'pricing.cta.note': 'Приём оплаты ещё настраивается — напишите, если хотите попасть в первую сотню.',
  'pricing.compare': 'Что входит',
  'pricing.compare.more': 'Все возможности · ещё {count}',
  'pricing.compare.less': 'Свернуть',
  'pricing.col.feature': 'Возможность',
  'pricing.value.unlimited': 'без лимита',
  'pricing.value.none': '—',
  'pricing.value.yes': 'есть',
  'pricing.value.perMonth': '{count} / мес',
  'pricing.value.total': '{count}',
  'pricing.value.month1': '1 месяц',
  'pricing.value.forever': 'бессрочно',
  'pricing.section.planner': 'Планировщик',
  'pricing.section.collab': 'Совместная работа',
  'pricing.section.ai': 'Распознавание и ИИ',
  'pricing.section.ai.hint': 'Каждый вызов стоит денег на нашей стороне, поэтому здесь счётчики, а не «сколько угодно».',
  'pricing.section.advisor': 'Подсказчик',
  'pricing.section.export': 'Экспорт',
  'pricing.section.watch': 'Слежение и напоминания',
  'pricing.section.watch.hint': 'Работает, пока подписка активна: это не разовый ответ, а фоновая проверка.',
  'pricing.f.trips': 'Поездок',
  'pricing.f.variants': 'Вариантов маршрута в поездке',
  'pricing.f.library': 'Блоков в библиотеке',
  'pricing.f.snapshots': 'Снимков истории',
  'pricing.f.currencies': 'Валют в расчёте',
  'pricing.f.coauthors': 'Соавторов поездки',
  'pricing.f.shareLife': 'Срок жизни публичной ссылки',
  'pricing.f.voting': 'Голосование за вариант',
  'pricing.f.recognize': 'Распознавание брони, ссылки, скриншота',
  'pricing.f.agent': 'Диалог с ИИ-планировщиком',
  'pricing.f.alternatives': 'Поиск альтернатив блоку',
  'pricing.f.connections': 'Умная проверка стыковок',
  'pricing.f.mailIn': 'Письмо с бронью на свой адрес поездки',
  'pricing.f.summary': 'Краткое описание поездки текстом',
  'pricing.f.dossier': 'Досье по стране: связь, авто, деньги',
  'pricing.f.visa': 'Виза по вашему паспорту',
  'pricing.f.weather': 'Погода и сезонность на даты',
  'pricing.f.local': 'Праздники, забастовки, что закрыто',
  'pricing.f.tips': 'Нюансы аэропортов и авиакомпаний',
  'pricing.f.living': 'Дневной бюджет по городу',
  'pricing.f.pdf': 'Экспорт в PDF без водяного знака',
  'pricing.f.ics': 'Календарь: файл .ics и подписка',
  'pricing.f.csv': 'Экспорт в Excel и CSV',
  'pricing.f.sheets': 'Выгрузка в Google Sheets и Notion',
  'pricing.f.offline': 'Работа без интернета',
  'pricing.f.priceWatch': 'Слежение за ценой перелёта',
  'pricing.f.flightAlerts': 'Оповещения о переносе рейса',
  'pricing.f.reminders': 'Напоминания: регистрация, виза, паспорт',
  'pricing.f.deadlines': 'Дедлайны бесплатной отмены',
  'pricing.soon.title': 'В работе',
  'pricing.soon.hint': 'Это ещё не сделано и не входит ни в один тариф. Появится — скажем отдельно.',
  'pricing.soon.optimizer': 'Оптимизатор маршрута: сдвиг дат и порядка городов',
  'pricing.soon.split': 'Раздел расходов между участниками',
  'pricing.soon.routes': 'Разные маршруты у разных участников',
  'pricing.soon.roles': 'Права соавторов: смотреть, но не править',
  'pricing.soon.leaderboard': 'Лидерборд маршрутов от авторов',
  'pricing.soon.affiliate': 'Покупка билета прямо из блока',
  'pricing.soon.api': 'API к своим поездкам',
  'pricing.soon.whitelabel': 'Свой домен и лого для агентств',
  'pricing.extras.title': 'Без подписки',
  'pricing.extras.hint': 'Летаете раз в год — платить каждый месяц незачем. Это докупается разово и не сгорает.',
  'pricing.extras.dossier': 'Досье по одной стране',
  'pricing.extras.dossier.hint': 'Виза, связь, аренда авто, деньги, нюансы — по одному направлению.',
  'pricing.extras.pack': 'Пакет 50 распознаваний',
  'pricing.extras.pack.hint': 'Брони, ссылки и скриншоты в блоки поездки. Срока годности нет.',
  'pricing.extras.price': '{amount} разово',
  'pricing.faq.title': 'Вопросы',
  'pricing.faq.q1': 'Что будет с поездками, если отменить Pro?',
  'pricing.faq.a1': 'Ничего не удаляется. Поездки сверх бесплатного лимита переходят в режим чтения: открыть, посмотреть и выгрузить можно всегда, править — после того как вернётесь на Pro или освободите место.',
  'pricing.faq.q2': 'Почему подписка, а не разовая оплата?',
  'pricing.faq.a2': 'Часть возможностей работает непрерывно: следит за ценой, ловит перенос рейса, напоминает о регистрации. Это расходы каждый месяц, а не один раз. То, что считается разово, мы и продаём разово — см. «Без подписки».',
  'pricing.faq.q3': 'Распознавание ошиблось — списался лимит?',
  'pricing.faq.a3': 'Если разбор не дал ни одного блока, попытка не засчитывается. Списывается только результат, который действительно попал в поездку.',
  'pricing.faq.q4': 'Что считается месяцем?',
  'pricing.faq.a4': 'Тридцать дней от даты оплаты, а не календарный месяц. Счётчики обнуляются в тот же день.',

  // ── Лайфхаки ────────────────────────────────────────────────────────────
  'lh.title': 'Лайфхаки',
  'lh.subtitle': 'Короткие советы от тех, кто уже летал. Пишет любой, кто вошёл; на страницу попадает то, что прошло проверку.',
  'lh.seo.title': 'Лайфхаки путешественников',
  'lh.seo.description': 'Советы про аэропорты и стыковки, жильё, аренду авто, еду и багаж — от тех, кто там был.',
  'lh.back': 'К планировщику',
  'lh.loading': 'Загружаем…',
  'lh.empty': 'Пока ничего не опубликовано. Ваш совет может стать первым.',
  'lh.empty.mine': 'Вы ещё ничего не предлагали.',
  'lh.author.anon': 'Без имени',
  'lh.delete': 'Удалить',
  'lh.editorial': 'От редакции',
  'lh.lang.hint': 'Перевода на ваш язык пока нет — совет показан на языке оригинала.',
  'lh.confirm': 'Подтвердить: у меня сработало',
  'lh.confirmed': 'Вы подтвердили этот совет',
  'lh.filter.category': 'Категория',
  'lh.filter.country': 'Страна',
  'lh.filter.mine': 'Мои',
  'lh.any.category': 'Все категории',
  'lh.any.country': 'Все страны',
  'lh.form.none': 'Не выбрано',
  'lh.cat.flight': 'Перелёт',
  'lh.cat.lodging': 'Жильё',
  'lh.cat.car': 'Авто',
  'lh.cat.food': 'Еда',
  'lh.cat.luggage': 'Чемодан',
  'lh.status.pending': 'На проверке',
  'lh.status.published': 'Опубликовано',
  'lh.status.rejected': 'Отклонено',
  'lh.form.text': 'Ваш совет',
  'lh.form.placeholder': 'Например: в Кутаиси весь аэропорт — один зал, часа на стыковку хватает с запасом.',
  'lh.form.category': 'Категория',
  'lh.form.country': 'Страна',
  'lh.form.submit': 'Предложить',
  'lh.form.sent': 'Отправлено на проверку — появится на странице, когда его посмотрит редактор.',
  'lh.form.hint': 'Один факт вместо рассказа: короткие советы читают, длинные пролистывают.',
  'lh.form.guest': 'Совет может добавить любой, кто вошёл в аккаунт.',
  'lh.form.left': 'осталось {count}',
  'lh.err.text_short': 'Слишком коротко — так совет не поймут.',
  'lh.err.text_long': 'Слишком длинно. Оставьте один факт, остальное лишнее.',
  'lh.err.bad_country': 'Не удалось разобрать страну.',
  'lh.err.bad_category': 'Такой категории нет.',
  'lh.err.too_many_pending': 'На проверке уже несколько ваших советов. Дождитесь решения по ним.',
  'lh.err.rate_limited': 'Слишком часто. Попробуйте позже.',
  'admin.tab.lifehacks': 'Лайфхаки',
  'admin.lh.queue': 'Очередь',
  'admin.lh.empty': 'В этом статусе пусто.',
  'admin.lh.publish': 'Опубликовать',
  'admin.lh.reject': 'Отклонить',
  'admin.lh.requeue': 'Вернуть в очередь',
  'admin.lh.delete': 'Удалить',

  // ── Актуализация цен на перелёты ────────────────────────────────────────
  // Бейдж говорит только то, что агрегатор показывал в момент проверки, и
  // сравнивает найденное с ценой в поездке. «Подешевело» он сказать не может:
  // билет у человека уже свой, цена в блоке — его, и менять её он решает сам.
  'prices.badge.cheaper': 'На {site} сейчас {price} — дешевле вашей',
  'prices.badge.dearer': 'На {site} сейчас {price} — дороже вашей',
  'prices.badge.same': 'На {site} сейчас {price} — как у вас',
  // Молчание читалось бы как «всё в порядке»; честнее сказать, что не нашли.
  'prices.badge.none': 'Актуальной цены не нашли',
  'prices.badge.checked': 'Проверено {when}',
  'prices.action.refresh': 'Проверить цену',
  'prices.action.refreshMany': 'Проверить цены ({count})',
  'prices.action.history': 'История цен',
  'prices.action.apply': 'Поставить эту цену',
  'prices.busy': 'Проверяем цену…',
  // Гостю кнопку показываем, но она зовёт вход, а не проверку: пустое место
  // на месте функции не объясняет, почему её нет. Это условие, а не ошибка —
  // ни укора, ни зазывания.
  'prices.guest': 'Войдите, чтобы проверять цены',
  'prices.history.title': 'История цен',
  'prices.history.empty': 'Цену ещё не проверяли.',
  'prices.history.quotes': 'Что нашли ({count})',
  'prices.history.exact': 'Тот же рейс',
  'prices.history.applied': 'Поставлена в поездку',
  'prices.history.check': 'Проверка {when}',
  'prices.notfound': 'В эту проверку актуальной цены не нашли.',
  // Сервер обрежет список молча — предупреждаем до отправки, иначе человек
  // решит, что непроверенные рейсы проверены и цена у них та же.
  'prices.trimmed': 'За раз проверяем не больше {count} рейсов — остальные останутся с прежней ценой.',
  'prices.err.disabled': 'Проверка цен выключена администратором.',
  'prices.err.no_key': 'Не задан ключ агрегатора — задайте его в админке.',
  'prices.err.rate_limited': 'Слишком много запросов. Попробуйте позже.',
  'prices.err.quota': 'Проверки цен на сегодня закончились.',
  'prices.err.provider': 'Агрегатор не ответил. Попробуйте позже.',
  'prices.err.bad_request': 'Данных о рейсе не хватает — искать нечего.',
  // Запасной текст для кодов вне списка (сеть, отказ доступа, новый код
  // сервера): свалить их на провайдера — назвать причину, которой не знаем.
  'prices.err.unknown': 'Не получилось проверить цены. Попробуйте ещё раз.',

  // ── Админка: актуализация цен ───────────────────────────────────────────
  // Источники перечислены по тому, чем они друг от друга отличаются: кэш или
  // живой обход, бесплатно или за деньги, точное совпадение или широкий охват.
  // Админ выбирает не «сколько галочек поставить», а чем платить за находку.
  'admin.tab.prices': 'Цены',
  'admin.prices.title': 'Актуализация цен',
  'admin.prices.enabled': 'Включить проверку цен',
  'admin.prices.enabled.hint': 'Выключено — кнопки проверки не видит никто, включая гостей.',
  'admin.prices.providers': 'Источники цен',
  'admin.prices.provider.travelpayouts': 'Travelpayouts (Aviasales)',
  'admin.prices.provider.travelpayouts.hint': 'Бесплатный партнёрский токен. Отдаёт кэш уже сделанных поисков Aviasales, а не живой поиск, и только точное совпадение по номеру рейса — пустой ответ здесь обычное дело.',
  'admin.prices.provider.websearch': 'Веб-поиск моделью',
  'admin.prices.provider.websearch.hint': 'Модель сама обходит сайты бронирования: самый широкий охват — и единственный источник, который стоит денег за каждый запрос.',
  'admin.prices.provider.page': 'Страница рейса',
  'admin.prices.provider.page.hint': 'Перечитывает ссылку, сохранённую у самого рейса. Поле «ссылка» необязательно, а сайты, рисующие цену скриптом, не дадут ничего.',
  'admin.prices.provider.ready': 'готов',
  'admin.prices.provider.notReady': 'не настроен',
  'admin.prices.token': 'Токен Travelpayouts',
  'admin.prices.token.hint': 'Токен хранится на сервере и в браузер не отдаётся.',
  'admin.prices.token.present': 'Токен задан (…{tail})',
  'admin.prices.token.clear': 'Удалить токен',
  'admin.prices.token.cleared': 'Токен удалён',
  'admin.prices.marker': 'Партнёрский маркер',
  'admin.prices.marker.hint': 'Без маркера партнёрские ссылки ничего не приносят.',
  // Маркер лежит в одной строке с токеном, и записать её сервер соглашается
  // только когда токен есть в базе (server/prices/settings.mjs,
  // setTravelpayouts → 422 «сначала сохраните токен»). Токен из окружения
  // здесь не в счёт: setTravelpayouts читает базу с пустым env, поэтому
  // дописать к нему маркер нельзя. Отсюда и условие показа: токена в базе нет
  // вовсе — ни `present`, ни `source: 'db'`. Обещать «введите ещё раз» тут
  // нечего: вводить в первый раз.
  'admin.prices.marker.needsToken': 'Сначала сохраните токен — маркер хранится только вместе с ним.',
  'admin.prices.advanced': 'Дополнительно',
  'admin.prices.maxFlights': 'Рейсов за один запрос',
  'admin.prices.maxFlights.hint': 'Лишние рейсы отрезаются с заметкой, а не отклоняют весь запрос.',
  'admin.prices.sanity': 'Коридор вменяемости',
  'admin.prices.sanity.hint': 'Цены вне диапазона «база × мин … база × макс» отбрасываются: так отсеиваются цена одного плеча вместо пары, чужая валюта и выдумка модели.',
  'admin.prices.sanityMin': 'Нижний множитель',
  'admin.prices.sanityMax': 'Верхний множитель',
  'admin.prices.save': 'Сохранить',
  'admin.prices.saved': 'Сохранено',
  'admin.prices.updated': 'Изменено {when}, {who}',
  'admin.prices.err.load': 'Не удалось загрузить настройки цен.',
  'admin.prices.err.save': 'Не удалось сохранить настройки цен.',
  'admin.prices.err.range': 'Значение вне допустимого диапазона.',
} as const;

export type TranslationKey = keyof typeof RU;

const EN: Record<TranslationKey, string> = {
  'app.brand': 'Plan your trip',
  'app.language': 'Interface language',
  'app.menu': 'Menu',
  'menu.section.trip': 'Trip',
  'menu.section.trips': 'Trips',
  'menu.section.view': 'View',
  'menu.section.account': 'Account',
  'app.library': 'Block library',
  'app.board': 'Trip options',
  'app.compare': 'Option comparison',
  'app.panel.hide': 'Hide: {name}',
  'app.panel.show': 'Show: {name}',
  'app.panel.width': '“{name}” panel width',

  'toolbar.travelers': 'Number of travellers',
  'toolbar.travelers.dec': 'Fewer travellers',
  'toolbar.travelers.inc': 'More travellers',
  'toolbar.currency': 'Currency',
  'toolbar.currency.tip': 'Currency for all amounts and the comparison',
  'toolbar.rates': 'Currencies and rates',
  'toolbar.rates.tip': 'Currencies and rates — quoted against {base}',

  'person.title': 'Personalisation',
  'person.tip': 'Personalisation: colour theme and text size',
  'person.theme': 'Colour theme',
  'person.mode.light': 'light',
  'person.mode.dark': 'dark',
  'person.size': 'Text size',
  'person.size.compact': 'Compact',
  'person.size.dense': 'Dense',
  'person.size.default': 'Default',
  'person.size.large': 'Large',
  'person.size.larger': 'Larger',
  'person.size.xlarge': 'Extra large',
  'person.size.huge': 'Largest',
  'person.space': 'Spacing',
  'person.space.tight': 'Tightest',
  'person.space.dense': 'Tight',
  'person.space.default': 'Default',
  'person.space.roomy': 'Roomy',
  'person.space.wide': 'Airy',
  'person.space.max': 'Airiest',
  'person.note': 'Appearance settings live in this browser only — they never reach the trip state or the server.',

  'currencies.title': 'Currencies',
  'currencies.hint': 'The rate is how much {base} one unit is worth. The base currency {base} is always 1 and cannot be removed.',
  'currencies.code': 'Code',
  'currencies.symbol': 'Symbol',
  'currencies.rate': 'Rate to {base}',
  'currencies.actions': 'Actions',
  'currencies.symbolOf': 'Symbol for {code}',
  'currencies.rateOf': 'Rate for {code}',
  'currencies.add': 'Add',
  'currencies.remove.base': 'The base currency cannot be removed',
  'currencies.remove.display': 'This currency is selected for display',
  'currencies.remove.used': 'Used in prices: {count}',
  'currencies.error.rate': 'The rate must be a number greater than 0',
  'currencies.add.currency': 'Currency',
  'currencies.add.rateLabel': '1 {code} = ? {base}',
  'currencies.add.empty': 'All currencies from the list are already added.',
  'currency.name.USD': 'US Dollar',
  'currency.name.EUR': 'Euro',
  'currency.name.GBP': 'British Pound',
  'currency.name.CHF': 'Swiss Franc',
  'currency.name.JPY': 'Japanese Yen',
  'currency.name.CNY': 'Chinese Yuan',
  'currency.name.TRY': 'Turkish Lira',
  'currency.name.AED': 'UAE Dirham',
  'currency.name.GEL': 'Georgian Lari',
  'currency.name.AMD': 'Armenian Dram',
  'currency.name.RUB': 'Russian Ruble',
  'currency.name.KZT': 'Kazakhstani Tenge',
  'currency.name.PLN': 'Polish Zloty',
  'currency.name.CZK': 'Czech Koruna',
  'currency.name.HUF': 'Hungarian Forint',
  'currency.name.RON': 'Romanian Leu',
  'currency.name.BGN': 'Bulgarian Lev',
  'currency.name.RSD': 'Serbian Dinar',
  'currency.name.SEK': 'Swedish Krona',
  'currency.name.NOK': 'Norwegian Krone',
  'currency.name.DKK': 'Danish Krone',
  'currency.name.ILS': 'Israeli New Shekel',
  'currency.name.THB': 'Thai Baht',
  'currency.name.VND': 'Vietnamese Dong',
  'currency.name.INR': 'Indian Rupee',
  'currency.name.IDR': 'Indonesian Rupiah',
  'currency.name.MYR': 'Malaysian Ringgit',
  'currency.name.SGD': 'Singapore Dollar',
  'currency.name.EGP': 'Egyptian Pound',
  'currency.name.MAD': 'Moroccan Dirham',

  'library.title': 'Library',
  'library.count.one': '{count} block',
  'library.count.few': '{count} blocks',
  'library.count.many': '{count} blocks',
  'library.count.other': '{count} blocks',
  'library.add': '+ Block',
  'library.search': 'Search the library',
  'library.search.placeholder': 'Search the library…',
  'library.empty': 'The library is empty',
  'library.empty.hint': 'Add your first block with “+ Block”, or parse a link.',
  'library.nothingFound': 'Nothing found',
  'library.converted': 'converted',
  'library.edit': 'Edit',
  'library.delete': 'Delete',
  'library.clone': 'Clone',
  'library.delete.title': 'Delete this block?',
  'library.delete.used.one': 'It will be removed from {count} option. This cannot be undone.',
  'library.delete.used.few': 'It will be removed from {count} options. This cannot be undone.',
  'library.delete.used.many': 'It will be removed from {count} options. This cannot be undone.',
  'library.delete.used.other': 'It will be removed from {count} options. This cannot be undone.',
  'library.delete.unused': 'This block is not used in any option. This cannot be undone.',
  'library.locked': 'The block belongs to a locked option',
  'library.addTo': 'Add to…',
  'library.addTo.pick': 'Pick an option',
  'library.addTo.none': 'Create an option first',
  'library.addTo.locked': 'Option is locked',

  'board.title': 'Options',
  'variant.add': '+ Option',
  'variant.add.short': '+',
  'variant.add.max': 'At most {count} options',
  'variant.empty.title': 'No options yet',
  'variant.empty.hint': 'Create an option to start putting a route together.',
  'variant.name': 'Option name',
  'variant.rename': 'Rename',
  'variant.collapse': 'Collapse option',
  'variant.expand': 'Expand option',
  'variant.order': 'Option order',
  'variant.moveUp': 'Move option up',
  'variant.moveDown': 'Move option down',
  'variant.lock': 'Lock this option against changes',
  'variant.unlock': 'Option is locked — click to allow changes',
  'variant.locked': 'Option is locked',
  'variant.menu': 'Option menu',
  'variant.color': 'Option colour',
  'variant.colorOf': 'Colour {color}',
  'variant.clone': 'Duplicate option',
  'variant.cloneName': '{name} — copy',
  'variant.delete': 'Delete option',
  'variant.delete.title': 'Delete this option?',
  'variant.delete.body': '“{name}” will be deleted. Its blocks stay in the library. This cannot be undone.',
  'variant.defaultName': 'Option {n}',

  'timeline.empty.title': 'Drop the first segment here',
  'timeline.empty.hint': 'Or use “Add to…” on a block card in the library on the left.',
  'timeline.copyHere': 'copy into option',
  'timeline.remove': 'Remove',
  'timeline.moveUp': 'Move up',
  'timeline.moveDown': 'Move down',
  'timeline.lockedNote': 'locked',
  'timeline.lockedNote.tip': 'Option is locked — open the padlock in its header',
  'timeline.pairPrice': 'in the pair price',
  'timeline.dropToRemove': 'Drop here to take the block out of the option',
  'timeline.layover': 'layover {duration}',
  'timeline.layover.unknown': 'layover unknown: no timezone',
  'timeline.layover.transferCounted': 'transfer counted',
  'timeline.layover.lodgingCounted': 'overnight stay counted',
  'timeline.layover.bothCounted': 'transfer and overnight stay counted',
  'timeline.stay': '{duration} at destination — rest, travel, or business',

  'summary.rides.one': '{count} ride',
  'summary.rides.few': '{count} rides',
  'summary.rides.many': '{count} rides',
  'summary.rides.other': '{count} rides',
  'summary.nights.one': '{count} night',
  'summary.nights.few': '{count} nights',
  'summary.nights.many': '{count} nights',
  'summary.nights.other': '{count} nights',

  'totals.withBaggage': 'with baggage',
  'totals.perTraveler': 'per person',
  'totals.flightTime': 'In the air',
  'totals.doorToDoor': 'Door to door',
  'totals.nights': 'Nights',
  'totals.breakdown': 'Breakdown by type',
  'totals.converted': 'converted',
  'totals.warnings.one': '{count} warning',
  'totals.warnings.few': '{count} warnings',
  'totals.warnings.many': '{count} warnings',
  'totals.warnings.other': '{count} warnings',

  'labels.hour': 'h',
  'labels.minute': 'm',
  'labels.perNight': '/night',
  'field.fromIata': 'From (IATA)',
  'field.toIata': 'To (IATA)',
  'field.departLocal': 'Departure (local time)',
  'field.arriveLocal': 'Arrival (local time)',
  'field.priceScope': 'Price type',
  'field.linked': 'Paired round-trip leg',
  'form.saveChanges': 'Save',
  'form.create': 'Create',
  'form.none': '— none —',
  'form.pickFlight': '— pick a flight —',
  'baggage.noFlights': 'There are no flights in the library yet — add one first.',
  'compare.best': '{value}, best value',
  'compare.title': 'Option comparison',
  'compare.count.one': '{count} option',
  'compare.count.few': '{count} options',
  'compare.count.many': '{count} options',
  'compare.count.other': '{count} options',
  'compare.metric': 'Metric',
  'compare.total': 'Total',
  'compare.totalWithBaggage': 'Total with baggage',
  'compare.flightTime': 'Time in the air',
  'compare.doorToDoor': 'Door-to-door time',
  'compare.nights': 'Nights',
  'compare.warnings': 'Warnings',
  'compare.empty': 'Add at least one option to see the comparison.',

  'form.close': 'Close',
  'form.cancel': 'Cancel',
  'form.save': 'Save',
  'form.confirm': 'Confirm',
  'form.optional': 'optional',
  'form.new': 'New block',
  'form.edit': 'Edit',
  'form.notFound': 'Block not found',
  'form.gone': 'This block no longer exists in the library.',
  'form.decrease': 'Decrease',
  'form.increase': 'Increase',

  'kind.flight': 'Flight',
  'kind.transfer': 'Transfer',
  'kind.lodging': 'Stay',
  'kind.taxi': 'Taxi',
  'kind.food': 'Food',
  'kind.baggage': 'Baggage',
  'kind.misc': 'Other',

  'kind.flight.desc': 'A leg between airports',
  'kind.transfer.desc': 'Shuttle, train or bus between cities',
  'kind.lodging.desc': 'Hotel or apartment, priced per night',
  'kind.taxi.desc': 'A ride around town',
  'kind.food.desc': 'Meals, priced per day',
  'kind.baggage.desc': 'Paid baggage tied to a specific flight',
  'kind.misc.desc': 'Any other spending',

  'scope.one_way': 'One-way price',
  'scope.round_trip_half': 'Half of the round-trip price',
  'scope.round_trip_full': 'Full round-trip price',
  'scope.pairNote': 'The price is counted once — the paired leg totals 0.',

  'field.from': 'From',
  'field.to': 'To',
  'field.city': 'City',
  'field.label': 'Name',
  'field.carrier': 'Carrier',
  'field.url': 'Link to the flight or ticket',
  'field.url.hint': 'Where to go back for details: the flight page, a search result or the ticket you bought.',
  'field.url.short': 'link',
  'field.flightNumber': 'Flight number',
  'field.depart': 'Departure',
  'field.arrive': 'Arrival',
  'field.duration': 'Duration, min',
  'field.nights': 'Nights',
  'field.days': 'Days',
  'field.quantity': 'Quantity',
  'field.price': 'Price',
  'field.pricePerNight': 'Price per night',
  'field.perPerson': 'Price per person',
  'field.wholeStay': 'For the whole gap',
  'field.wholeStay.hint': 'Nights are counted automatically: from the previous flight\u2019s arrival to the next flight\u2019s departure (up to 365).',
  'field.wholeStay.need': 'Available when another block follows this one in an option.',
  'labels.wholeStay': 'whole gap',
  'field.flight': 'Flight',
  'field.note': 'Note',
  'field.tzFrom': 'Departure timezone',
  'field.tzTo': 'Arrival timezone',

  'error.iata': 'An airport code is exactly 3 latin letters',
  'error.departRequired': 'Enter the departure time',
  'error.arriveRequired': 'Enter the arrival time',
  'error.departDateRequired': 'Enter the departure date and time',
  'error.arriveDateRequired': 'Enter the arrival date and time',
  'error.arriveBeforeDepart': 'Arrival must be later than departure',
  'error.priceNegative': 'The price must be a number ≥ 0',
  'error.fromRequired': 'Enter the origin',
  'error.toRequired': 'Enter the destination',
  'error.durationNegative': 'The duration must be a number ≥ 0',
  'error.cityRequired': 'Enter the city',
  'error.nightsMin': 'At least 1 night',
  'error.labelRequired': 'Enter a name',
  'error.quantityMin': 'At least 1',
  'error.flightRequired': 'Pick a flight',
  'warn.tzMissing': 'No timezone — the duration will not be exact',
  'warn.tzMissingParse': 'Timezone not detected — travel time will not be exact.',

  'parse.missing.from': 'departure airport',
  'parse.missing.to': 'arrival airport',
  'parse.missing.fromTz': 'departure timezone',
  'parse.missing.toTz': 'arrival timezone',
  'parse.missing.departLocal': 'departure date',
  'parse.missing.departTime': 'departure time',
  'parse.missing.arriveLocal': 'arrival date',
  'parse.missing.arriveTime': 'arrival time',
  'parse.missing.carrier': 'carrier',
  'parse.missing.price': 'price',

  'library.empty.text': 'There are two ways to add a block: build it by hand, or paste what you copied from a flight or hotel search site.',
  'library.noResults': 'Nothing matches “{query}”',
  'library.resetSearch': 'Clear the search',
  'library.group.flight': 'Flights',
  'library.group.transfer': 'Transfers',
  'library.group.lodging': 'Stays',
  'library.group.taxi': 'Taxi',
  'library.group.food': 'Food',
  'library.group.baggage': 'Baggage',
  'library.group.misc': 'Other',
  'compare.hint': 'Comparison starts at two options — add another one to see the best values highlighted.',
  'compare.caption': 'Trip option comparison: rows are metrics, columns are options',

  'parse.flightN': 'Flight {n}',
  'parse.notRecognised': 'Not recognised: {fields}. Check and fill them in by hand.',
  'parse.back': 'Back',
  'parse.addSelected': 'Add selected ({count})',

  'sync.boot': 'loading…',
  'sync.local': 'local',
  'sync.saving': 'saving…',
  'sync.synced': 'saved',
  'sync.conflict': 'server version',
  'sync.rejected': 'not saved',
  'sync.boot.tip': 'Reading state from the server',
  'sync.local.tip': 'Working locally',
  'sync.saving.tip': 'Sending changes to the server',
  'sync.synced.tip': 'State is stored in the database on the server',
  'sync.conflict.tip': 'State changed on the server; its version was applied',
  'sync.rejected.tip': 'The server rejected the state',
  'toolbar.snapshots': 'Snapshots and history',
  'sync.snapshot': 'Snapshot',
  'sync.snapshot.tip': 'Save a snapshot: the current state is stored in the server database',
  'sync.history.tip': 'Snapshot history: go back to a state you saved earlier',
  'sync.history': 'History',
  'sync.snapshots': 'Snapshots on the server',
  'sync.snapshot.saved': 'Snapshot saved on the server',
  'sync.snapshot.failed': 'Could not save the snapshot',
  'sync.snapshot.notFound': 'Snapshot not found',
  'sync.snapshot.restored': 'State restored from the snapshot',
  'sync.snapshot.empty': 'No snapshots yet. “Snapshot” stores the current state so you can come back to it.',
  'sync.snapshot.unnamed': 'untitled',
  'sync.snapshot.label': 'Snapshot {stamp}',

  'calcwarn.need_transfer': 'transfer needed {from} → {to}',
  'calcwarn.risky_connection': 'tight connection {duration}',
  'calcwarn.negative_connection': 'impossible: departure before arrival',
  'calcwarn.long_layover': 'long wait — add an overnight stay?',
  'calcwarn.orphan_baggage': 'baggage without its flight in this option',
  'calcwarn.rt_full_unpaired': 'price covers the round trip',
  'calcwarn.missing_tz': 'unknown timezone — duration not calculated',

  'auth.signin': 'Sign in',
  'auth.signup': 'Sign up',
  'auth.signout': 'Sign out',
  'auth.email': 'Email',
  'auth.password': 'Password',
  'auth.password.new': 'New password',
  'auth.password.current': 'Current password',
  'auth.password.change': 'Change password',
  'auth.password.changed': 'Password changed.',
  'auth.name': 'Name',
  'auth.submit.signup': 'Create account',
  'auth.forgot.link': 'Forgot your password?',
  'auth.forgot.title': 'Password recovery',
  'auth.forgot.hint': 'We will email a link for setting a new password to this address.',
  'auth.forgot.submit': 'Send the link',
  'auth.forgot.sent': 'If an account with that email exists, the link is already on its way. It is valid for one hour.',
  'auth.forgot.back': 'Back to sign in',
  'auth.reset.title': 'New password',
  'auth.reset.submit': 'Save password',
  'auth.reset.done': 'Password changed. All previous sessions were closed — sign in again.',
  'auth.verify.title': 'Email confirmation',
  'auth.verify.banner': 'Confirm your email — without it you cannot recover access to your account.',
  'auth.verify.resend': 'Send the email again',
  'auth.verify.sent': 'Email sent — check your inbox.',
  'auth.verify.ok': 'Email confirmed.',
  'auth.verify.fail': 'The link is invalid or has expired.',
  'auth.guest.hint': 'Without signing in, trips are stored in this browser only.',
  'auth.subtitle': 'An account keeps your trips out of this one browser — open them from any device.',
  'auth.tabs.label': 'Sign in or sign up',
  'auth.password.show': 'Show password',
  'auth.password.hide': 'Hide password',
  'auth.capslock': 'Caps Lock is on',
  'auth.busy': 'One moment…',
  'auth.toApp': 'To the planner',
  'auth.pw.rule': 'At least 10 characters.',
  'auth.pw.short': 'Too short: at least 10 characters',
  'auth.pw.long': 'Too long',
  'auth.pw.common': 'Too common — this is the first one attackers try',
  'auth.pw.same_as_email': 'The password must not repeat your email',
  'auth.err.invalid_credentials': 'Wrong email or password.',
  'auth.err.invalid_email': 'Check the email address.',
  'auth.err.weak_password': 'That password will not do.',
  'auth.err.email_taken': 'An account with that email already exists.',
  'auth.err.blocked': 'The account is blocked.',
  'auth.err.rate_limited': 'Too many attempts. Try again later.',
  'auth.err.bad_token': 'The link is invalid or has expired.',
  'auth.err.unauthorized': 'Please sign in.',
  'auth.err.forbidden': 'Not enough permissions.',
  'auth.err.self_change': 'You cannot change your own role or status.',
  'auth.err.origin': 'Request rejected for security reasons.',
  'auth.err.network': 'The server is unreachable.',
  'auth.err.unknown': 'Something went wrong.',
  'auth.err.restorable': 'This email already had an account — you can restore it.',
  'auth.restore.title': 'Restore account',
  'auth.restore.hint': 'This email already had an account — enter its old password to bring it back with its trips.',
  'auth.restore.submit': 'Restore',
  'auth.restore.back': 'Back to sign up',

  'account.title': 'Account',
  'account.section.account': 'Account',
  'account.appearance': 'Appearance',
  'account.guest.title': 'Not signed in',
  'account.emailState': 'Email status',
  'account.verified': 'confirmed',
  'account.unverified': 'not confirmed',
  'account.save': 'Save',
  'account.saved': 'Saved',
  'account.admin': 'Admin',
  'account.menu.admin.hint': 'Users and recognition settings',
  'account.password.warning': 'Changing the password closes every other session.',
  'account.menu.language': 'Language',
  'account.menu.language.hint': 'Interface language',
  'account.menu.trips.hint': 'All your trips',
  'account.menu.trips.hint.guest': 'Trips become available after signing in',
  'account.menu.pricing': 'Pricing',
  'account.menu.pricing.hint': 'Plans, prices and extra options',
  'account.menu.settings': 'Settings',
  'account.menu.settings.hint': 'Password, email, account deletion',
  'account.menu.person.hint': 'Colour palette, text size and spacing — tailor the interface to yourself',
  'account.menu.signout.hint': 'End the session on this device',
  'account.delete.title': 'Delete account',
  'account.delete.hint': 'The account and all its trips become unavailable right after deletion.',
  'account.delete.action': 'Delete account',
  'account.delete.confirm': 'The account and all its trips will stop being available. You can only get them back by registering again with the same email.',

  'admin.title': 'Users',
  'admin.back': 'To the planner',
  'admin.search': 'Search by email or name',
  'admin.col.user': 'User',
  'admin.col.role': 'Role',
  'admin.col.status': 'Status',
  'admin.col.created': 'Signed up',
  'admin.col.lastLogin': 'Last sign-in',
  'admin.col.data': 'Data',
  'admin.col.actions': 'Actions',
  'admin.role.user': 'User',
  'admin.role.admin': 'Administrator',
  'admin.status.active': 'Active',
  'admin.status.blocked': 'Blocked',
  'admin.status.deleted': 'Deleted',
  'admin.action.block': 'Block',
  'admin.action.unblock': 'Unblock',
  'admin.action.promote': 'Grant admin',
  'admin.action.demote': 'Revoke admin',
  'admin.action.delete': 'Delete',
  'admin.action.view': 'Data',
  'admin.delete.confirm': 'Delete {email} together with every trip and snapshot? This cannot be undone.',
  'admin.empty': 'Nobody found',
  'admin.prev': 'Previous',
  'admin.next': 'Next',
  'admin.page': '{from}–{to} of {total}',
  'admin.forbidden': 'Administrator rights required.',
  'admin.stats.users': 'Users',
  'admin.stats.newWeek': 'New this week',
  'admin.stats.activeWeek': 'Active this week',
  'admin.stats.verified': 'Email confirmed',
  'admin.stats.blocked': 'Blocked',
  'admin.stats.snapshots': 'Snapshots',
  'admin.stats.size': 'Data size',
  'admin.view.title': 'Data: {email}',
  'admin.view.readonly': "Someone else's data, read-only. This view is written to the audit log.",
  'admin.view.empty': 'This user has not saved anything yet.',
  'admin.view.variants': 'Options',
  'admin.view.segments': 'Blocks',
  'admin.view.showRaw': 'Show JSON',
  'admin.view.hideRaw': 'Hide JSON',

  'sync.snapshot.beforeLogin': 'Before signing in',
  'auth.guest.locked': 'Available after signing in',
  'variant.add.guest': 'Without signing in you get {count} options. Sign in to add more.',

  'share.title': 'Share the trip',
  // ── Trips ───────────────────────────────────────────────────────────────
  'trips.title': 'My trips',
  'trips.all': 'My trips ({count})',
  'trips.none': 'not created yet',
  'trips.untitled': 'Untitled',
  'trips.newName': 'New trip',
  'trips.create': '+ New trip',
  'trips.open': 'open',
  'trips.openAction': 'Open',
  'trips.rename': 'Rename',
  'trips.rename.ownerOnly': 'Only the owner can rename the trip',
  'trips.name': 'Trip name',
  'trips.copyLink': 'Copy link',
  'trips.shared': 'has a public link',
  'trips.linkClosed': 'no public link',
  'trips.info.button': 'Trip details for “{name}”',
  'trips.info.title': 'About “{name}”',
  'trips.updated': 'edited {date}',
  'trips.created': 'created {date}',
  'trips.delete': 'Delete',
  'trips.delete.title': 'Delete the trip?',
  'trips.delete.text': 'The trip “{name}” and its history will be deleted for good. Its public link will stop working.',
  'trips.empty': 'No trips yet. Create the first one and your work will start saving to the server.',
  'trips.guest.title': 'Account needed',
  'trips.guest': 'Multiple trips need an account: guest work stays in this browser as a single trip.',
  'trips.back': 'Back to the planner',
  'trips.section.mine': 'My trips',
  'trips.section.shared': 'Shared with me',
  'trips.section.shared.hint': 'You were invited here as a co-editor: the contents are yours to change, the trip itself is not.',
  'trips.byOwner': 'owner: {name}',
  'trips.members.one': '{count} co-editor',
  'trips.members.few': '{count} co-editors',
  'trips.members.many': '{count} co-editors',
  'trips.members.other': '{count} co-editors',
  'trips.leave': 'Leave',
  'trips.leave.title': 'Leave the trip?',
  'trips.leave.text': 'The trip “{name}” will disappear from your list. The owner keeps it and can invite you again.',

  // ── Shared access ───────────────────────────────────────────────────────
  'trips.access': 'Access',
  'trips.access.title': 'Access to “{name}”',
  'trips.access.lead': 'Co-editors work on the very same trip: the block library, the variants, the prices. Only the owner can rename it, publish a link and invite others.',
  'trips.access.invite': 'Invite by email',
  'trips.access.add': 'Invite',
  'trips.access.hint': 'With an account, the trip shows up in their list right away. Without one, they get an invitation email and access after signing up.',
  'trips.access.pending': 'No account with {email} yet: we sent an invitation — access appears right after they sign up.',
  'trips.access.empty': 'Nobody but the owner so far.',
  'trips.access.you': 'that’s you',
  'trips.access.remove': 'Remove',
  'trips.access.role.owner': 'owner',
  'trips.access.role.editor': 'co-editor',
  'trips.access.err.no_user': 'Nobody with that email is here yet. Ask them to sign up, then invite again.',
  'trips.access.err.already_member': 'They are on the list already.',
  'trips.access.err.is_owner': 'That is the owner of the trip.',
  'trips.access.err.too_many': 'This trip already has too many co-editors.',
  'trips.access.err.invalid_email': 'Check the email address.',
  'trips.access.err.blocked': 'That account is blocked.',
  'trips.access.err.unknown': 'That did not work. Please try again.',

  // ── Templates and frequent blocks ───────────────────────────────────────
  'tpl.start': 'Start from a template',
  'tpl.start.hint': 'A typical trip in one click: the blocks are laid out, the prices are empty.',
  'tpl.create': 'Create trip',
  'tpl.fill': 'Build the option',
  'tpl.page.note': 'A template creates a separate new trip. Your existing trips stay untouched.',
  'tpl.board.note': 'The template adds an option with these blocks. Everything you already built stays where it is.',
  'tpl.blocks': 'Frequent blocks',
  'tpl.blocks.hint': 'One tap puts the block into the library with a zero price — just fill in the amount.',
  'tpl.added': 'Block “{name}” added to the library',
  'tpl.weekend.name': 'Two-day weekend',
  'tpl.weekend.desc': 'Two nights in a city: the ride from the airport, a stay, food and taxis.',
  'tpl.beach.name': 'A week by the sea',
  'tpl.beach.desc': 'Seven nights in one place: transfer to the hotel, food, day trips.',
  'tpl.business.name': 'Business trip',
  'tpl.business.desc': 'Three nights on business: a stay, taxis, food and connectivity.',
  'tpl.twoCities.name': 'Two cities in one trip',
  'tpl.twoCities.desc': 'Three nights in each and the leg between them.',
  'tpl.mountains.name': 'Mountains and skiing',
  'tpl.mountains.desc': 'Five nights by the slope: lift pass, gear rental, food.',
  'tpl.place.airport': 'Airport',
  'tpl.place.city': 'City',
  'tpl.place.hotel': 'Hotel',
  'tpl.place.city1': 'First city',
  'tpl.place.city2': 'Second city',
  'tpl.block.taxi': 'Taxis around town',
  'tpl.block.food': 'Food',
  'tpl.block.insurance': 'Insurance',
  'tpl.block.esim': 'Mobile data',
  'tpl.block.tickets': 'Tours and tickets',
  'tpl.block.visa': 'Visa and fees',
  'tpl.block.skipass': 'Lift pass',
  'tpl.block.gear': 'Gear rental',
  'share.tip': 'A link to the trip for people without an account',
  'share.hint': 'The link shows a copy of the trip as it was when published — view only, no editing. Later changes stay private until you press “Update”.',
  'share.create': 'Create a link',
  'share.update': 'Update',
  'share.revoke': 'Disable the link',
  'share.copy': 'Copy',
  'share.copied': 'Copied ✓',
  'share.link': 'Trip link',
  'share.look': 'The link opens in your theme, size and language — the reader sees the trip exactly as you built it.',
  'share.updated': 'Updated: {date}',
  'share.views': 'views: {count}',
  'share.failed': 'The link could not be created.',
  'share.readonly': 'View only',
  'share.travelers': 'Travellers: {count}',
  'share.cheapest': 'Cheapest',
  'share.segments': 'What makes up the price',
  'share.noPrice': 'No prices filled in yet',
  'share.notFound': 'The link was not found or has been disabled.',
  'share.empty': 'This trip has no options yet.',
  'share.makeYours': 'Plan your own trip',
  'share.preview.variants.one': '{count} option',
  'share.preview.variants.few': '{count} options',
  'share.preview.variants.many': '{count} options',
  'share.preview.variants.other': '{count} options',
  'share.preview.from': 'from {price}',
  'share.preview.tail': 'Compare price and time on Plan Your Trip.',
  'share.preview.empty': 'The trip is still being put together: no options yet.',

  'library.new': 'new',
  'library.new.title': 'Added recently',
  'library.new.count': 'New: {count}',
  'library.new.dismiss': 'Mark as seen',
  'library.fromSource': 'From a source',
  'library.fromSource.desc': 'Recognise a flight from text, a link, or a screenshot.',

  'recog.title': 'Add from a source',
  'recog.tab.text': 'Description',
  'recog.tab.url': 'Link',
  'recog.tab.screenshot': 'Screenshot',
  'recog.text.label': 'Search results or a short flight description',
  'recog.text.placeholder': 'Dec 27, Wizz Air Larnaca 11:55 → Yerevan 16:00, $149',
  'recog.url.label': 'Link to a flight or a booking',
  'recog.url.placeholder': 'https://…',
  'recog.url.invalid': 'An http:// or https:// link is required',
  'recog.shot.label': 'Screenshots of search results, an email or a ticket',
  'recog.shot.drop': 'Drop images here, paste them (Ctrl+V) or pick files',
  'recog.shot.pick': 'Pick files',
  'recog.shot.add': 'Add more',
  'recog.shot.remove': 'Remove all',
  'recog.shot.removeOne': 'Remove screenshot {num}',
  'recog.shot.tooBig': 'The image is larger than {limit} MB',
  'recog.shot.tooMany': 'No more than {limit} images at a time — skipped: {count}',
  'recog.shot.tooBigTotal': 'Together the images must stay under {limit} MB — skipped: {count}',
  'recog.shot.badType': 'This does not look like a readable image: PNG, JPEG, WebP or HEIC will do',
  'recog.shot.ready': 'Images: {count} of {limit} · {size}',
  'recog.shot.multi': 'Several images go in a single request — flights from all of them land in one list.',
  'recog.run': 'Recognise',
  'recog.running': 'Recognising…',
  'recog.review': 'Check and fix before adding',
  'recog.model': 'Model: {model}',
  'recog.local': 'Parsed locally, without AI',
  'recog.fallback': 'The AI did not answer — showing the local parser result.',
  'recog.privacy.ai': 'The text, link or image goes to the selected AI model. Nothing is saved on its own: everything can be checked and fixed before adding.',
  'recog.privacy.local': 'No network requests: only the pasted text is parsed. Everything can be checked and fixed before adding.',
  'recog.off': 'AI recognition is switched off by the administrator — local text parsing is still available.',
  'recog.off.guest': 'AI recognition becomes available once you sign in.',
  'recog.nothing': 'No flights found — try another text, link or screenshot.',
  'recog.notes': 'How it was parsed',
  'recog.err.unauthorized': 'Please sign in.',
  'recog.err.disabled': 'Recognition is switched off by the administrator.',
  'recog.err.no_model': 'No model is selected for this source — set one in the admin panel.',
  'recog.err.no_key': 'The AI provider key is missing — set it in the admin panel.',
  'recog.err.provider': 'The provider returned an error: {message}',
  'recog.err.timeout': 'The model did not answer in time. Please try again.',
  'recog.err.rate_limited': 'Too many requests. Please try again later.',
  'recog.err.too_large': 'The request is too large.',
  'recog.err.bad_response': 'The model answered off-format, so there is nothing to parse. Try again or send fewer images at a time.',
  'recog.err.fetch_url': 'The link could not be opened — paste text or a screenshot instead.',
  'recog.err.bad_request': 'There is nothing to recognise.',
  'recog.err.unknown': 'Recognition failed. Please try again.',

  // ── Magic variants ──────────────────────────────────────────────────────
  'magic.title': 'Magic variants',
  'magic.action': 'Magic',
  'magic.tip': 'Build the best variants from the library',
  'magic.from': 'From',
  'magic.from.placeholder': 'LCA or Larnaca',
  'magic.to': 'To',
  'magic.to.placeholder': 'EVN or Yerevan',
  'magic.roundTrip': 'Round trip',
  'magic.stay': 'Stay, days',
  'magic.stay.from': 'from',
  'magic.stay.to': 'to',
  'magic.stay.hint': 'Days between arriving at the destination and the flight back.',
  'magic.wishes': 'Wishes',
  'magic.wishes.placeholder': 'No overnight layovers, under $500 per person, at most one connection',
  'magic.count': 'How many variants',
  'magic.count.hint': 'Free slots on the board: {count}',
  'magic.run': 'Do the magic',
  'magic.running': 'Casting…',
  'magic.library': 'All library blocks go into the request: {count}',
  'magic.library.empty': 'The library has no flights — magic has nothing to build variants from.',
  'magic.library.trimmed': 'The library has {count} blocks, {limit} fit into the selected model — the rest will not be sent.',
  'magic.privacy': 'The list of library blocks and your wishes go to the selected AI model. Variants land on the board only after you confirm them.',
  'magic.review': 'Check the variants before adding',
  'magic.model': 'Model: {model}',
  'magic.notes': 'Notes',
  'magic.nothing': 'No variant could be built — refine the wishes or add more flights to the library.',
  'magic.add': 'Add variants ({count})',
  'magic.back': 'Back',
  'magic.stayDays': 'Stay: {days} d.',
  'magic.segments': 'Blocks: {count}',
  'magic.noRoom': 'No free slots on the board — delete a variant first.',
  'magic.tooMany': 'More variants selected than fit on the board: uncheck the extras.',
  'magic.off': 'Magic is switched off by the administrator.',
  'magic.off.guest': 'Magic is available after signing in.',
  'magic.err.unauthorized': 'Signing in is required.',
  'magic.err.disabled': 'AI features are switched off by the administrator.',
  'magic.err.no_model': 'No model is selected for magic — set it in the admin panel.',
  'magic.err.no_key': 'No AI provider key is set — set it in the admin panel.',
  'magic.err.bad_request': 'Request rejected: {message}',
  'magic.err.too_large': 'The library is too large for a single request.',
  'magic.err.rate_limited': 'Too many requests. Try again later.',
  'magic.err.timeout': 'The model did not answer in time. Try again.',
  'magic.err.provider': 'The provider returned an error: {message}',
  'magic.err.bad_response': 'The model reply could not be parsed.',
  'magic.err.unknown': 'The magic failed. Try again.',

  'admin.tab.users': 'Users',
  'admin.tab.ai': 'AI',
  'admin.tab.logs': 'Log',
  'admin.tab.chats': 'Chats',
  'admin.tab.pricing': 'Pricing',
  'admin.tab.trips': 'Trips',
  'admin.action.enter': 'Sign in as',
  'admin.action.limits': 'Limits',
  'admin.limits.title': 'User limits',
  'admin.limits.hint': 'Empty field — default value.',
  'admin.limits.default': 'default',
  'admin.limits.maxVariants': 'Variants on the board',
  'admin.limits.aiDaily': 'AI requests per day',
  'admin.limits.magicDaily': 'Magic runs per day',
  'admin.limits.maxTrips': 'Trips',
  'admin.logs.filter.problems': 'Errors and warnings',
  'admin.logs.filter.ai': 'AI requests',
  'admin.logs.filter.all': 'All',
  'admin.logs.clear': 'Clear log',
  'admin.logs.clear.confirm': 'Delete all log entries?',
  'admin.logs.empty': 'No entries',
  'admin.logs.retention': 'Kept for 7 days, rotated daily',
  'admin.logs.noDetails': 'This entry has no details',
  'admin.logs.details.meta': 'Summary',
  'admin.logs.details.request': 'Request to the model',
  'admin.logs.details.response': 'Model response',
  'admin.chats.new': 'New conversation',
  'admin.chats.search': 'User email…',
  'admin.chats.empty': 'No conversations yet',
  'admin.chats.pick': 'Pick a conversation on the left',
  'admin.chats.start': 'No messages yet — write first',
  'admin.chats.placeholder': 'Reply to the user…',
  'admin.chats.send': 'Send',
  'admin.chats.clear': 'Delete conversation',
  'admin.chats.clear.confirm': 'Delete the whole conversation with {email}?',
  'admin.chats.close': 'End conversation',
  'admin.chats.close.confirm': 'End the conversation with {email}? The history will stay visible to admins only.',
  'admin.chats.lang': 'User language',
  'admin.chats.client': 'User client',
  'admin.chats.files': 'Files in chat',
  'hero.title': 'Plan your trip in variants',
  'hero.lead': 'This is a demo trip: three routes side by side — prices, connections and warnings on one board. Play with it or build your own; an account saves everything and unlocks the AI magic.',
  'hero.signup': 'Create an account',
  'hero.try': 'Just try it',
  'support.title': 'Support',
  'support.close': 'Close',
  'support.minimize': 'Minimize',
  'support.closed': 'The conversation was closed',
  'support.attach': 'Attach a file',
  'support.attach.bad': 'File not accepted: image or PDF up to 3 MB',
  'support.empty': 'Write to us — we will reply here',
  'support.placeholder': 'Message…',
  'support.send': 'Send',
  'support.team': 'Support',
  'support.err': 'Failed to send — try again',
  'admin.stats.trips': 'Trips',
  'admin.stats.tripsShared': 'With collaborators',
  'admin.stats.tripsPublished': 'Published',
  'admin.trips.search': 'Search by name or owner',
  'admin.trips.col.trip': 'Trip',
  'admin.trips.col.owner': 'Owner',
  'admin.trips.col.members': 'Collaborators',
  'admin.trips.col.share': 'Link',
  'admin.trips.col.updated': 'Updated',
  'admin.trips.col.created': 'Created',
  'admin.trips.published': 'Open',
  'admin.trips.enter': 'Sign in as owner',
  'admin.trips.empty': 'No trips found',
  'admin.trips.view.title': 'Trip: {name}',
  'admin.trips.view.empty': 'This trip is still empty.',
  'impersonate.confirm': 'Sign in to the account of {email}? From there you act as them: whatever you change, changes for them. The action is recorded in the audit log.',
  'impersonate.bar': 'You are acting as {email}. Your account: {admin}.',
  'impersonate.stop': 'Back to my account',
  'auth.err.impersonating': 'This action is not available while acting as another user.',
  'admin.pricing.source': 'This is where the Russian text and the page structure are edited. Other languages live on the “Translations” tab.',
  'admin.tr.note': 'Original on the left, translation on the right. An empty field means visitors see the original.',
  'admin.tr.target': 'Target language',
  'admin.tr.progress': '{done} of {total} translated',
  'admin.tr.done': 'Fully translated',
  'admin.tr.onlyEmpty': 'Untranslated only',
  'admin.tr.source': 'Original',
  'admin.tr.translation': 'Translation',
  'admin.pricing.save': 'Save',
  'admin.pricing.saved': 'Saved',
  'admin.pricing.lang': 'Language',
  'admin.pricing.reset': 'Restore built-in',
  'admin.pricing.reset.title': 'Restore the built-in content?',
  'admin.pricing.reset.text': 'Every edit to the pricing page will be deleted and the page will be built from what is in the code again. This cannot be undone.',
  'admin.pricing.updated': 'edited {date}, {name}',
  'admin.pricing.never': 'no edits yet',
  'admin.pricing.section.price': 'Prices',
  'admin.pricing.section.texts': 'Page texts',
  'admin.pricing.section.sections': 'Table sections',
  'admin.pricing.section.features': 'Plan rows',
  'admin.pricing.section.highlights': 'On the plan cards',
  'admin.pricing.section.extras': 'One-off purchases',
  'admin.pricing.section.soon': 'In progress',
  'admin.pricing.section.faq': 'Questions',
  'admin.pricing.currency': 'Currency',
  'admin.pricing.monthly': 'Per month',
  'admin.pricing.yearly': 'Per year',
  'admin.pricing.discount': '{percent}% off',
  'admin.pricing.discount.none': 'a year is no cheaper than a month',
  'admin.pricing.id': 'Identifier',
  'admin.pricing.name': 'Name',
  'admin.pricing.hint': 'Hint',
  'admin.pricing.question': 'Question',
  'admin.pricing.answer': 'Answer',
  'admin.pricing.price': 'Price',
  'admin.pricing.parent': 'Section',
  'admin.pricing.free': 'Free',
  'admin.pricing.pro': 'Pro',
  'admin.pricing.shown': 'Show',
  'admin.pricing.add': 'Add',
  'admin.pricing.remove': 'Remove',
  'admin.pricing.up': 'Up',
  'admin.pricing.down': 'Down',
  'admin.pricing.kind.count': 'Number',
  'admin.pricing.kind.unlimited': 'Unlimited',
  'admin.pricing.kind.yes': 'Yes',
  'admin.pricing.kind.none': 'No',
  'admin.pricing.kind.text': 'Custom text',
  'admin.pricing.per.total': 'in total',
  'admin.pricing.per.month': 'per month',
  'admin.pricing.key': 'Dictionary key',
  'admin.pricing.highlights.hint': 'Ticked rows appear on the plan card at the top of the page. Five is the readability limit.',
  'admin.pricing.empty': 'Nothing here yet',
  'admin.ai.title': 'AI features',
  'admin.ai.hint': 'Source recognition, variant magic and price checks all run through an AI provider. Each task gets its own model: a screenshot needs one that accepts images, magic needs one that reasons, prices need one with web search.',
  'admin.ai.enabled': 'Enable AI features',
  'admin.ai.enabled.hint': 'When off, only local text parsing is left and variant magic is unavailable.',
  'admin.ai.key': 'AI provider key',
  'admin.ai.key.present': 'Key is set (…{tail})',
  'admin.ai.key.env': 'The key comes from a server environment variable',
  'admin.ai.key.missing': 'No key set',
  'admin.ai.key.placeholder': 'sk-…',
  'admin.ai.key.hint': 'The key stays on the server and is never sent to the browser.',
  'admin.ai.key.clear': 'Remove the key',
  'admin.ai.source.screenshot': 'Screenshot',
  'admin.ai.source.url': 'Link',
  'admin.ai.source.text': 'Text description',
  'admin.ai.source.screenshot.hint': 'The model must accept images.',
  'admin.ai.source.url.hint': 'The server opens the page itself and hands its text to the model.',
  'admin.ai.source.text.hint': 'Pasted search results or a couple of handwritten lines.',
  'admin.ai.source.magic': 'Magic: variant planning',
  'admin.ai.source.magic.hint': 'Builds ready trip variants out of library blocks: picks flights, orders them in time and keeps them from overlapping.',
  'admin.ai.source.prices': 'Prices: current flight price',
  'admin.ai.source.prices.hint': 'Searches booking sites for the price of one specific flight and returns the quotes it found with a link to the source.',
  'admin.ai.source.settings': 'Settings for this task',
  'admin.ai.source.settings.hint': 'An empty field means the shared value below applies. The daily limit here is counted separately from the shared one: the stricter one wins.',
  'admin.ai.override.inherit': 'shared: {value}',
  'admin.ai.advanced.hint': 'Shared values for every task. Any task can override them in its own card.',
  'admin.ai.req.title': 'Requirements for the magic model',
  'admin.ai.req.context': 'Context from 128K tokens: the whole library goes into a single request.',
  'admin.ai.req.json': 'Structured output (json_schema) support and exact schema compliance.',
  'admin.ai.req.reasoning': 'Strong reasoning: the model compares UTC times, counts layovers and plans the route.',
  'admin.ai.req.tokens': 'The reply is long, so for magic the server raises the token limit to at least 2000 and the timeout to 60 seconds. Reasoning models spend the same budget on thinking — set 8000, or the reply arrives truncated.',
  'admin.ai.req.vision': 'Images are not needed: only text is sent here, a vision model gains nothing.',
  'admin.ai.req.temp': 'Temperature 0: the planning must be reproducible. Reasoning models (GPT-5) ignore the parameter — that is fine.',
  'admin.ai.req.recommend': 'We recommend pro/thinking tier models (Gemini 2.5 Pro, Claude Sonnet 4.5, GPT-5). Cheap fast models confuse chronology and invent ids that do not exist — the server drops such variants and the user gets an empty result.',
  'admin.ai.recommend': 'Cheap and good enough',
  'admin.ai.recommend.hint': 'Prices in $ per 1M tokens as of {date}, ordered from cheapest to most dependable. A hint, not a limit: any model can be chosen. Click one to fill it in.',
  'admin.ai.model': 'Model',
  'admin.ai.model.search': 'Search models…',
  'admin.ai.model.none': 'Not selected',
  'admin.ai.model.vision': 'images',
  'admin.ai.model.free': 'free',
  'admin.ai.model.context': 'context {tokens}',
  'admin.ai.model.price': '$ {in} / {out} per 1M tokens',
  'admin.ai.model.custom': 'Enter an id manually',
  'admin.ai.model.tools': 'tools',
  'admin.ai.model.reasoning': 'reasoning',
  'admin.ai.model.nojson': 'no strict JSON',
  'admin.ai.model.warn.json': 'Will not work: the server sends a strict json_schema and this model does not accept one, so it returns a provider error instead of a parse.',
  'admin.ai.model.warn.vision': 'Will not work: screenshots are sent as images and this model only accepts text.',
  'admin.ai.model.warn.tools': 'Will not work: the price web search is wired through tools, which this model does not support.',
  'admin.ai.model.warn.context': 'Context too small: the whole block library goes in one request, {min} or more is needed.',
  'admin.ai.model.warn.reasoning': 'This model cannot turn reasoning off, and those tokens count against the same answer limit. Set max tokens to at least {tokens} or the answer arrives truncated.',
  'admin.ai.tune.recommend': 'Recommended: {values}',
  'admin.ai.tune.apply': 'Apply',
  'admin.ai.model.unknown': 'Model {model} is not in the provider list',
  'admin.ai.models.reload': 'Refresh the list',
  'admin.ai.models.loading': 'Loading the model list…',
  'admin.ai.models.failed': 'The model list could not be loaded: {message}',
  'admin.ai.models.count': 'Models: {count}',
  'admin.ai.advanced': 'Advanced',
  'admin.ai.maxTokens': 'Max answer tokens',
  'admin.ai.temperature': 'Temperature',
  'admin.ai.timeout': 'Timeout, seconds',
  'admin.ai.limit': 'Requests per user per day',
  'admin.ai.maxImages': 'Screenshots per request',
  'admin.ai.maxImages.hint': 'How many images a user sends in one screenshot-recognition request. More images mean a longer and pricier model answer.',
  'admin.ai.save': 'Save',
  'admin.ai.saved': 'Saved',
  'admin.ai.save.failed': 'Could not save: {message}',
  'admin.ai.test': 'Test',
  'admin.ai.test.running': 'Testing…',
  'admin.ai.test.ok': 'The model answered in {ms} ms',
  'admin.ai.test.failed': 'The test failed: {message}',
  'admin.ai.updated': 'Changed {date}',

  // ── Onboarding ──────────────────────────────────────────────────────────
  'onboarding.title': 'How it works',
  'onboarding.tip': 'Quick tour: how to build and compare trip options',
  'onboarding.step': 'Step {current} of {total}',
  'onboarding.goto': 'Step {n}',
  'onboarding.skip': 'Skip',
  'onboarding.back': 'Back',
  'onboarding.next': 'Next',
  'onboarding.done': 'Got it',
  'onboarding.s1.title': 'First, where to',
  'onboarding.s1.text': 'Asia, Europe, America: start a trip for every destination you are weighing. They sit side by side and never mix, and a ready-made template gets you going.',
  'onboarding.s2.title': 'Inside a trip',
  'onboarding.s2.text': 'The block library is on the left: flights, nights, transfers, any expense at all. On the right are the route options you assemble from them.',
  'onboarding.s3.title': 'A link, some text or a screenshot',
  'onboarding.s3.text': 'No need to type a flight in by hand. Paste a booking link, copied search results or a screenshot of an email — the app pulls out the route, the times and the price, and leaves you to check it.',
  'onboarding.s4.title': 'Two options side by side',
  'onboarding.s4.text': 'Cheap tickets come with layovers, and sleeping on the way costs money. Direct flights cost more, but need no nights. Build both options and put them next to each other.',
  'onboarding.s5.title': 'The total decides',
  'onboarding.s5.text': 'Every option adds itself up at the bottom: tickets, lodging and everything else. That is where the cheap tickets turn out to be the expensive ones.',
  'onboarding.s6.title': 'Plan it together',
  'onboarding.s6.text': 'Invite a co-editor by email and they work on the very same trip: the library, the options, the prices. Only the owner can rename it and invite others.',
  'onboarding.s7.title': 'Show the result',
  'onboarding.s7.text': 'A public link opens the trip for people without an account: view only, a copy as it was when published. Later changes stay private until you press Update.',
  'onboarding.mock.asia': 'Asia',
  'onboarding.mock.europe': 'Europe',
  'onboarding.mock.america': 'America',
  'onboarding.mock.cheap': 'Budget',
  'onboarding.mock.cheap.note': 'cheap tickets, 2 nights on the way',
  'onboarding.mock.direct': 'Direct',
  'onboarding.mock.direct.note': 'pricier tickets, no nights',
  'onboarding.mock.night.ath': 'Night in Athens',
  'onboarding.mock.night.mxp': 'Night in Milan',
  'onboarding.mock.total': 'Total',
  'onboarding.mock.win': '{sum} cheaper',

  'pricing.title': 'Pricing',
  'pricing.subtitle': 'Planning a trip is free. Pro lifts the limits and adds the parts that do the counting and the watching for you.',
  'pricing.seo.title': 'Pricing — Free and Pro',
  'pricing.seo.description': 'What the free plan covers and what Pro adds: trip limits, booking recognition, country advice and price watching.',
  'pricing.back': 'Back to the planner',
  'pricing.billing.monthly': 'Monthly',
  'pricing.billing.yearly': 'Yearly',
  'pricing.billing.save': 'save {percent}%',
  'pricing.price.free': 'Free',
  'pricing.price.perMonth': '{amount} / mo',
  'pricing.price.yearlyHint': '{amount} a year, billed at once',
  'pricing.price.monthlyHint': 'Cancel any time',
  'pricing.plan.free': 'Free',
  'pricing.plan.free.tagline': 'Build one trip and compare two routes.',
  'pricing.plan.pro': 'Pro',
  'pricing.plan.pro.tagline': 'For people who fly often and plan properly.',
  'pricing.cta.free': 'Start for free',
  'pricing.cta.pro': 'Upgrade to Pro',
  'pricing.cta.soon': 'Payments coming soon',
  'pricing.cta.note': 'Payments are still being set up — write to us if you want to be among the first hundred.',
  'pricing.compare': 'What you get',
  'pricing.compare.more': 'All features · {count} more',
  'pricing.compare.less': 'Collapse',
  'pricing.col.feature': 'Feature',
  'pricing.value.unlimited': 'unlimited',
  'pricing.value.none': '—',
  'pricing.value.yes': 'yes',
  'pricing.value.perMonth': '{count} / mo',
  'pricing.value.total': '{count}',
  'pricing.value.month1': '1 month',
  'pricing.value.forever': 'no expiry',
  'pricing.section.planner': 'Planner',
  'pricing.section.collab': 'Working together',
  'pricing.section.ai': 'Recognition and AI',
  'pricing.section.ai.hint': 'Every call costs us real money, which is why there are counters here rather than “as much as you like”.',
  'pricing.section.advisor': 'Advisor',
  'pricing.section.export': 'Export',
  'pricing.section.watch': 'Watching and reminders',
  'pricing.section.watch.hint': 'Runs for as long as the subscription is active: this is a background check, not a one-off answer.',
  'pricing.f.trips': 'Trips',
  'pricing.f.variants': 'Route options per trip',
  'pricing.f.library': 'Blocks in the library',
  'pricing.f.snapshots': 'History snapshots',
  'pricing.f.currencies': 'Currencies in the calculation',
  'pricing.f.coauthors': 'Trip co-authors',
  'pricing.f.shareLife': 'Public link lifetime',
  'pricing.f.voting': 'Voting on an option',
  'pricing.f.recognize': 'Recognising a booking, link or screenshot',
  'pricing.f.agent': 'Chat with the AI planner',
  'pricing.f.alternatives': 'Alternatives for a block',
  'pricing.f.connections': 'Smart connection check',
  'pricing.f.mailIn': 'Forward a booking to your trip address',
  'pricing.f.summary': 'Short written summary of the trip',
  'pricing.f.dossier': 'Country dossier: SIM, car hire, money',
  'pricing.f.visa': 'Visa check for your passport',
  'pricing.f.weather': 'Weather and season for the dates',
  'pricing.f.local': 'Holidays, strikes, what is closed',
  'pricing.f.tips': 'Airport and airline gotchas',
  'pricing.f.living': 'Daily budget for the city',
  'pricing.f.pdf': 'PDF export without a watermark',
  'pricing.f.ics': 'Calendar: .ics file and subscription',
  'pricing.f.csv': 'Export to Excel and CSV',
  'pricing.f.sheets': 'Send to Google Sheets and Notion',
  'pricing.f.offline': 'Works without internet',
  'pricing.f.priceWatch': 'Flight price watch',
  'pricing.f.flightAlerts': 'Alerts when a flight is moved',
  'pricing.f.reminders': 'Reminders: check-in, visa, passport',
  'pricing.f.deadlines': 'Free-cancellation deadlines',
  'pricing.soon.title': 'In progress',
  'pricing.soon.hint': 'Not built yet and not part of any plan. We will say so separately when it lands.',
  'pricing.soon.optimizer': 'Route optimiser: shift dates and city order',
  'pricing.soon.split': 'Splitting costs between travellers',
  'pricing.soon.routes': 'Different routes for different travellers',
  'pricing.soon.roles': 'Co-author rights: view but not edit',
  'pricing.soon.leaderboard': 'Leaderboard of routes from authors',
  'pricing.soon.affiliate': 'Book a ticket straight from a block',
  'pricing.soon.api': 'API for your own trips',
  'pricing.soon.whitelabel': 'Own domain and logo for agencies',
  'pricing.extras.title': 'Without a subscription',
  'pricing.extras.hint': 'If you fly once a year there is no point paying every month. These are bought once and never expire.',
  'pricing.extras.dossier': 'Dossier for one country',
  'pricing.extras.dossier.hint': 'Visa, connectivity, car hire, money, gotchas — for a single destination.',
  'pricing.extras.pack': 'Pack of 50 recognitions',
  'pricing.extras.pack.hint': 'Bookings, links and screenshots into trip blocks. No expiry date.',
  'pricing.extras.price': '{amount} once',
  'pricing.faq.title': 'Questions',
  'pricing.faq.q1': 'What happens to my trips if I cancel Pro?',
  'pricing.faq.a1': 'Nothing is deleted. Trips beyond the free limit become read-only: you can always open, read and export them, and edit again once you return to Pro or free up a slot.',
  'pricing.faq.q2': 'Why a subscription rather than a one-off payment?',
  'pricing.faq.a2': 'Some of this runs continuously: watching a price, catching a moved flight, reminding you about check-in. That is a monthly cost, not a one-time one. What is genuinely one-off we do sell one-off — see “Without a subscription”.',
  'pricing.faq.q3': 'Recognition got it wrong — did that use up my quota?',
  'pricing.faq.a3': 'If the parse produced no blocks at all, the attempt does not count. Only a result that actually landed in your trip is charged.',
  'pricing.faq.q4': 'What counts as a month?',
  'pricing.faq.a4': 'Thirty days from the payment date, not a calendar month. Counters reset on that same day.',

  // ── Лайфхаки ────────────────────────────────────────────────────────────
  'lh.title': 'Tips',
  'lh.subtitle': 'Short tips from people who have already flown it. Anyone signed in can write one; what reaches this page has been checked.',
  'lh.seo.title': 'Traveller tips',
  'lh.seo.description': 'Tips on airports and layovers, places to stay, car rental, food and luggage — from people who have been there.',
  'lh.back': 'To the planner',
  'lh.loading': 'Loading…',
  'lh.empty': 'Nothing published yet. Yours could be the first.',
  'lh.empty.mine': 'You have not suggested anything yet.',
  'lh.author.anon': 'No name',
  'lh.delete': 'Delete',
  'lh.editorial': 'From the editors',
  'lh.lang.hint': 'No translation into your language yet — the tip is shown as written.',
  'lh.confirm': 'Confirm: worked for me',
  'lh.confirmed': 'You confirmed this tip',
  'lh.filter.category': 'Category',
  'lh.filter.country': 'Country',
  'lh.filter.mine': 'Mine',
  'lh.any.category': 'All categories',
  'lh.any.country': 'All countries',
  'lh.form.none': 'Not set',
  'lh.cat.flight': 'Flight',
  'lh.cat.lodging': 'Stay',
  'lh.cat.car': 'Car',
  'lh.cat.food': 'Food',
  'lh.cat.luggage': 'Luggage',
  'lh.status.pending': 'In review',
  'lh.status.published': 'Published',
  'lh.status.rejected': 'Rejected',
  'lh.form.text': 'Your tip',
  'lh.form.placeholder': 'For example: Kutaisi airport is one single hall, an hour between flights is plenty.',
  'lh.form.category': 'Category',
  'lh.form.country': 'Country',
  'lh.form.submit': 'Suggest',
  'lh.form.sent': 'Sent for review — it appears on the page once an editor has looked at it.',
  'lh.form.hint': 'One fact beats a story: short tips get read, long ones get scrolled past.',
  'lh.form.guest': 'Anyone signed in can add a tip.',
  'lh.form.left': '{count} left',
  'lh.err.text_short': 'Too short — nobody will get the point.',
  'lh.err.text_long': 'Too long. Keep one fact and drop the rest.',
  'lh.err.bad_country': 'Could not read that country.',
  'lh.err.bad_category': 'No such category.',
  'lh.err.too_many_pending': 'Several of your tips are already in review. Wait for a decision on those.',
  'lh.err.rate_limited': 'Too often. Try again later.',
  'admin.tab.lifehacks': 'Tips',
  'admin.lh.queue': 'Queue',
  'admin.lh.empty': 'Nothing in this status.',
  'admin.lh.publish': 'Publish',
  'admin.lh.reject': 'Reject',
  'admin.lh.requeue': 'Back to queue',
  'admin.lh.delete': 'Delete',

  // ── Flight price refresh ────────────────────────────────────────────────
  'prices.badge.cheaper': 'On {site} it is {price} now — less than yours',
  'prices.badge.dearer': 'On {site} it is {price} now — more than yours',
  'prices.badge.same': 'On {site} it is {price} now — the same as yours',
  'prices.badge.none': 'No current price found',
  'prices.badge.checked': 'Checked {when}',
  'prices.action.refresh': 'Check the price',
  'prices.action.refreshMany': 'Check prices ({count})',
  'prices.action.history': 'Price history',
  'prices.action.apply': 'Use this price',
  'prices.busy': 'Checking the price…',
  'prices.guest': 'Sign in to check prices',
  'prices.history.title': 'Price history',
  'prices.history.empty': 'The price has not been checked yet.',
  'prices.history.quotes': 'What we found ({count})',
  'prices.history.exact': 'The same flight',
  'prices.history.applied': 'Written into the trip',
  'prices.history.check': 'Check {when}',
  'prices.notfound': 'No current price was found in this check.',
  'prices.trimmed': 'We check up to {count} flights at a time — the rest keep their current price.',
  'prices.err.disabled': 'Price checks are switched off by the administrator.',
  'prices.err.no_key': 'The aggregator key is missing — set it in the admin panel.',
  'prices.err.rate_limited': 'Too many requests. Please try again later.',
  'prices.err.quota': 'Today’s price checks are used up.',
  'prices.err.provider': 'The aggregator did not answer. Please try again later.',
  'prices.err.bad_request': 'The flight details are incomplete — there is nothing to search for.',
  'prices.err.unknown': 'The prices could not be checked. Please try again.',
  'admin.tab.prices': 'Prices',
  'admin.prices.title': 'Price checks',
  'admin.prices.enabled': 'Enable price checks',
  'admin.prices.enabled.hint': 'Switched off, nobody sees the check button, guests included.',
  'admin.prices.providers': 'Price sources',
  'admin.prices.provider.travelpayouts': 'Travelpayouts (Aviasales)',
  'admin.prices.provider.travelpayouts.hint': 'A free partner token. It returns a cache of Aviasales searches already made, not a live search, and only an exact flight-number match — an empty answer here is normal.',
  'admin.prices.provider.websearch': 'Model web search',
  'admin.prices.provider.websearch.hint': 'The model walks the booking sites itself: the broadest coverage — and the only source that costs money per request.',
  'admin.prices.provider.page': 'The flight’s own page',
  'admin.prices.provider.page.hint': 'Re-reads the link saved on the flight itself. The link field is optional, and sites that draw the price with a script will yield nothing.',
  'admin.prices.provider.ready': 'ready',
  'admin.prices.provider.notReady': 'not configured',
  'admin.prices.token': 'Travelpayouts token',
  'admin.prices.token.hint': 'The token is kept on the server and never handed to the browser.',
  'admin.prices.token.present': 'Token is set (…{tail})',
  'admin.prices.token.clear': 'Delete the token',
  'admin.prices.token.cleared': 'The token is deleted',
  'admin.prices.marker': 'Partner marker',
  'admin.prices.marker.hint': 'Without a marker the affiliate links earn nothing.',
  'admin.prices.marker.needsToken': 'Save a token first — the marker is stored only together with it.',
  'admin.prices.advanced': 'Advanced',
  'admin.prices.maxFlights': 'Flights per request',
  'admin.prices.maxFlights.hint': 'Extra flights are cut off with a note instead of rejecting the whole request.',
  'admin.prices.sanity': 'Sanity corridor',
  'admin.prices.sanity.hint': 'Prices outside the “base × min … base × max” range are discarded: this catches a one-leg price instead of a pair, a foreign currency and the model’s invention.',
  'admin.prices.sanityMin': 'Lower multiplier',
  'admin.prices.sanityMax': 'Upper multiplier',
  'admin.prices.save': 'Save',
  'admin.prices.saved': 'Saved',
  'admin.prices.updated': 'Changed {when}, {who}',
  'admin.prices.err.load': 'The price settings could not be loaded.',
  'admin.prices.err.save': 'The price settings could not be saved.',
  'admin.prices.err.range': 'The value is outside the allowed range.',
};

/** Испанский. Порядок ключей общий со всеми словарями — см. EN. */
const ES: Record<TranslationKey, string> = {
  'app.brand': 'Planifica tu viaje',
  'app.language': 'Idioma de la interfaz',
  'app.menu': 'Menú',
  'menu.section.trip': 'Viaje',
  'menu.section.trips': 'Viajes',
  'menu.section.view': 'Vista',
  'menu.section.account': 'Cuenta',
  'app.library': 'Biblioteca de bloques',
  'app.board': 'Opciones de viaje',
  'app.compare': 'Comparación de opciones',
  'app.panel.hide': 'Ocultar: {name}',
  'app.panel.show': 'Mostrar: {name}',
  'app.panel.width': 'Ancho del panel «{name}»',
  'toolbar.travelers': 'Número de viajeros',
  'toolbar.travelers.dec': 'Menos viajeros',
  'toolbar.travelers.inc': 'Más viajeros',
  'toolbar.currency': 'Moneda',
  'toolbar.currency.tip': 'Moneda de todos los importes y de la comparación',
  'toolbar.rates': 'Monedas y tipos de cambio',
  'toolbar.rates.tip': 'Monedas y tipos de cambio — expresados en {base}',

  'person.title': 'Personalización',
  'person.tip': 'Personalización: tema de color y tamaño del texto',
  'person.theme': 'Tema de color',
  'person.mode.light': 'claro',
  'person.mode.dark': 'oscuro',
  'person.size': 'Tamaño del texto',
  'person.size.compact': 'Compacto',
  'person.size.dense': 'Denso',
  'person.size.default': 'Normal',
  'person.size.large': 'Grande',
  'person.size.larger': 'Más grande',
  'person.size.xlarge': 'Muy grande',
  'person.size.huge': 'Máximo',
  'person.space': 'Espaciado',
  'person.space.tight': 'Mínimo',
  'person.space.dense': 'Ajustado',
  'person.space.default': 'Normal',
  'person.space.roomy': 'Amplio',
  'person.space.wide': 'Muy amplio',
  'person.space.max': 'Máximo',
  'person.note': 'Los ajustes de apariencia se guardan solo en este navegador: no llegan al estado del viaje ni al servidor.',
  'currencies.title': 'Monedas',
  'currencies.hint': 'El tipo de cambio indica cuántos {base} vale una unidad. La moneda base {base} siempre es 1 y no se puede eliminar.',
  'currencies.code': 'Código',
  'currencies.symbol': 'Símbolo',
  'currencies.rate': 'Cambio a {base}',
  'currencies.actions': 'Acciones',
  'currencies.symbolOf': 'Símbolo de {code}',
  'currencies.rateOf': 'Cambio de {code}',
  'currencies.add': 'Añadir',
  'currencies.remove.base': 'La moneda base no se puede eliminar',
  'currencies.remove.display': 'Esta moneda está seleccionada para mostrar',
  'currencies.remove.used': 'Usada en precios: {count}',
  'currencies.error.rate': 'El tipo de cambio debe ser un número mayor que 0',
  'currencies.add.currency': 'Moneda',
  'currencies.add.rateLabel': '1 {code} = ? {base}',
  'currencies.add.empty': 'Ya se añadieron todas las monedas de la lista.',
  'currency.name.USD': 'Dólar estadounidense',
  'currency.name.EUR': 'Euro',
  'currency.name.GBP': 'Libra esterlina',
  'currency.name.CHF': 'Franco suizo',
  'currency.name.JPY': 'Yen japonés',
  'currency.name.CNY': 'Yuan chino',
  'currency.name.TRY': 'Lira turca',
  'currency.name.AED': 'Dírham de EAU',
  'currency.name.GEL': 'Lari georgiano',
  'currency.name.AMD': 'Dram armenio',
  'currency.name.RUB': 'Rublo ruso',
  'currency.name.KZT': 'Tenge kazajo',
  'currency.name.PLN': 'Zloty polaco',
  'currency.name.CZK': 'Corona checa',
  'currency.name.HUF': 'Forinto húngaro',
  'currency.name.RON': 'Leu rumano',
  'currency.name.BGN': 'Lev búlgaro',
  'currency.name.RSD': 'Dinar serbio',
  'currency.name.SEK': 'Corona sueca',
  'currency.name.NOK': 'Corona noruega',
  'currency.name.DKK': 'Corona danesa',
  'currency.name.ILS': 'Nuevo séquel israelí',
  'currency.name.THB': 'Baht tailandés',
  'currency.name.VND': 'Dong vietnamita',
  'currency.name.INR': 'Rupia india',
  'currency.name.IDR': 'Rupia indonesia',
  'currency.name.MYR': 'Ringgit malayo',
  'currency.name.SGD': 'Dólar de Singapur',
  'currency.name.EGP': 'Libra egipcia',
  'currency.name.MAD': 'Dírham marroquí',
  'library.title': 'Biblioteca',
  'library.count.one': '{count} bloque',
  'library.count.few': '{count} bloques',
  'library.count.many': '{count} bloques',
  'library.count.other': '{count} bloques',
  'library.add': '+ Bloque',
  'library.search': 'Buscar en la biblioteca',
  'library.search.placeholder': 'Buscar en la biblioteca…',
  'library.empty': 'La biblioteca está vacía',
  'library.empty.hint': 'Añade tu primer bloque con «+ Bloque» o analiza un enlace.',
  'library.nothingFound': 'No se encontró nada',
  'library.converted': 'convertido',
  'library.edit': 'Editar',
  'library.delete': 'Eliminar',
  'library.clone': 'Clonar',
  'library.delete.title': '¿Eliminar este bloque?',
  'library.delete.used.one': 'Se quitará de {count} opción. Esta acción no se puede deshacer.',
  'library.delete.used.few': 'Se quitará de {count} opciones. Esta acción no se puede deshacer.',
  'library.delete.used.many': 'Se quitará de {count} opciones. Esta acción no se puede deshacer.',
  'library.delete.used.other': 'Se quitará de {count} opciones. Esta acción no se puede deshacer.',
  'library.delete.unused': 'Este bloque no se usa en ninguna opción. Esta acción no se puede deshacer.',
  'library.locked': 'El bloque pertenece a una opción bloqueada',
  'library.addTo': 'Añadir a…',
  'library.addTo.pick': 'Elige una opción',
  'library.addTo.none': 'Crea primero una opción',
  'library.addTo.locked': 'La opción está bloqueada',
  'board.title': 'Opciones',
  'variant.add': '+ Opción',
  'variant.add.short': '+',
  'variant.add.max': 'Como máximo {count} opciones',
  'variant.empty.title': 'Todavía no hay opciones',
  'variant.empty.hint': 'Crea una opción para empezar a montar la ruta.',
  'variant.name': 'Nombre de la opción',
  'variant.rename': 'Renombrar',
  'variant.collapse': 'Contraer opción',
  'variant.expand': 'Expandir opción',
  'variant.order': 'Orden de la opción',
  'variant.moveUp': 'Subir la opción',
  'variant.moveDown': 'Bajar la opción',
  'variant.lock': 'Bloquear la opción frente a cambios',
  'variant.unlock': 'La opción está bloqueada — pulsa para permitir cambios',
  'variant.locked': 'La opción está bloqueada',
  'variant.menu': 'Menú de la opción',
  'variant.color': 'Color de la opción',
  'variant.colorOf': 'Color {color}',
  'variant.clone': 'Duplicar opción',
  'variant.cloneName': '{name} — copia',
  'variant.delete': 'Eliminar opción',
  'variant.delete.title': '¿Eliminar esta opción?',
  'variant.delete.body': '«{name}» se eliminará. Sus bloques permanecen en la biblioteca. Esta acción no se puede deshacer.',
  'variant.defaultName': 'Opción {n}',
  'timeline.empty.title': 'Suelta aquí el primer tramo',
  'timeline.empty.hint': 'O usa «Añadir a…» en una tarjeta de bloque de la biblioteca de la izquierda.',
  'timeline.copyHere': 'copiar en la opción',
  'timeline.remove': 'Quitar',
  'timeline.moveUp': 'Subir',
  'timeline.moveDown': 'Bajar',
  'timeline.lockedNote': 'bloqueado',
  'timeline.lockedNote.tip': 'La opción está bloqueada — abre el candado en su cabecera',
  'timeline.pairPrice': 'en el precio del par',
  'timeline.dropToRemove': 'Suelta aquí para sacar el bloque de la opción',
  'timeline.layover': 'escala {duration}',
  'timeline.layover.unknown': 'escala desconocida: falta la zona horaria',
  'timeline.layover.transferCounted': 'traslado contabilizado',
  'timeline.layover.lodgingCounted': 'noche contabilizada',
  'timeline.layover.bothCounted': 'traslado y noche contabilizados',
  'timeline.stay': '{duration} en destino — descanso, viaje o negocios',
  'summary.rides.one': '{count} traslado',
  'summary.rides.few': '{count} traslados',
  'summary.rides.many': '{count} traslados',
  'summary.rides.other': '{count} traslados',
  'summary.nights.one': '{count} noche',
  'summary.nights.few': '{count} noches',
  'summary.nights.many': '{count} noches',
  'summary.nights.other': '{count} noches',
  'totals.withBaggage': 'con equipaje',
  'totals.perTraveler': 'por persona',
  'totals.flightTime': 'En el aire',
  'totals.doorToDoor': 'Puerta a puerta',
  'totals.nights': 'Noches',
  'totals.breakdown': 'Desglose por tipo',
  'totals.converted': 'convertido',
  'totals.warnings.one': '{count} aviso',
  'totals.warnings.few': '{count} avisos',
  'totals.warnings.many': '{count} avisos',
  'totals.warnings.other': '{count} avisos',
  'labels.hour': 'h',
  'labels.minute': 'min',
  'labels.perNight': '/noche',
  'field.fromIata': 'Desde (IATA)',
  'field.toIata': 'Hasta (IATA)',
  'field.departLocal': 'Salida (hora local)',
  'field.arriveLocal': 'Llegada (hora local)',
  'field.priceScope': 'Tipo de precio',
  'field.linked': 'Tramo emparejado de ida y vuelta',
  'form.saveChanges': 'Guardar',
  'form.create': 'Crear',
  'form.none': '— ninguno —',
  'form.pickFlight': '— elige un vuelo —',
  'baggage.noFlights': 'Todavía no hay vuelos en la biblioteca — añade uno primero.',
  'compare.best': '{value}, mejor valor',
  'compare.title': 'Comparación de opciones',
  'compare.count.one': '{count} opción',
  'compare.count.few': '{count} opciones',
  'compare.count.many': '{count} opciones',
  'compare.count.other': '{count} opciones',
  'compare.metric': 'Indicador',
  'compare.total': 'Total',
  'compare.totalWithBaggage': 'Total con equipaje',
  'compare.flightTime': 'Tiempo neto de vuelo',
  'compare.doorToDoor': 'Tiempo puerta a puerta',
  'compare.nights': 'Noches',
  'compare.warnings': 'Avisos',
  'compare.empty': 'Añade al menos una opción para ver la comparación.',
  'form.close': 'Cerrar',
  'form.cancel': 'Cancelar',
  'form.save': 'Guardar',
  'form.confirm': 'Confirmar',
  'form.optional': 'opcional',
  'form.new': 'Bloque nuevo',
  'form.edit': 'Editar',
  'form.notFound': 'Bloque no encontrado',
  'form.gone': 'Este bloque ya no existe en la biblioteca.',
  'form.decrease': 'Disminuir',
  'form.increase': 'Aumentar',
  'kind.flight': 'Vuelo',
  'kind.transfer': 'Traslado',
  'kind.lodging': 'Alojamiento',
  'kind.taxi': 'Taxi',
  'kind.food': 'Comida',
  'kind.baggage': 'Equipaje',
  'kind.misc': 'Otros',
  'kind.flight.desc': 'Tramo entre aeropuertos',
  'kind.transfer.desc': 'Lanzadera, tren o autobús entre ciudades',
  'kind.lodging.desc': 'Hotel o apartamento, con precio por noche',
  'kind.taxi.desc': 'Trayecto por la ciudad',
  'kind.food.desc': 'Comidas, con precio por día',
  'kind.baggage.desc': 'Equipaje de pago ligado a un vuelo concreto',
  'kind.misc.desc': 'Cualquier otro gasto',
  'scope.one_way': 'Precio de solo ida',
  'scope.round_trip_half': 'Mitad del precio de ida y vuelta',
  'scope.round_trip_full': 'Precio completo de ida y vuelta',
  'scope.pairNote': 'El precio se cuenta una vez — el tramo emparejado suma 0.',
  'field.from': 'Desde',
  'field.to': 'Hasta',
  'field.city': 'Ciudad',
  'field.label': 'Nombre',
  'field.carrier': 'Aerolínea',
  'field.url': 'Enlace al vuelo o al billete',
  'field.url.hint': 'Adónde volver para ver los detalles: la página del vuelo, una búsqueda o el billete comprado.',
  'field.url.short': 'enlace',
  'field.flightNumber': 'Número de vuelo',
  'field.depart': 'Salida',
  'field.arrive': 'Llegada',
  'field.duration': 'Duración, min',
  'field.nights': 'Noches',
  'field.days': 'Días',
  'field.quantity': 'Cantidad',
  'field.price': 'Precio',
  'field.pricePerNight': 'Precio por noche',
  'field.perPerson': 'Precio por persona',
  'field.wholeStay': 'Todo el intervalo',
  'field.wholeStay.hint': 'Las noches se calculan solas: de la llegada del vuelo anterior a la salida del siguiente (máx. 365).',
  'field.wholeStay.need': 'Disponible cuando otro bloque sigue a este en una variante.',
  'labels.wholeStay': 'todo el intervalo',
  'field.flight': 'Vuelo',
  'field.note': 'Nota',
  'field.tzFrom': 'Zona horaria de salida',
  'field.tzTo': 'Zona horaria de llegada',
  'error.iata': 'El código de aeropuerto son exactamente 3 letras latinas',
  'error.departRequired': 'Indica la hora de salida',
  'error.arriveRequired': 'Indica la hora de llegada',
  'error.departDateRequired': 'Indica la fecha y hora de salida',
  'error.arriveDateRequired': 'Indica la fecha y hora de llegada',
  'error.arriveBeforeDepart': 'La llegada debe ser posterior a la salida',
  'error.priceNegative': 'El precio debe ser un número ≥ 0',
  'error.fromRequired': 'Indica el origen',
  'error.toRequired': 'Indica el destino',
  'error.durationNegative': 'La duración debe ser un número ≥ 0',
  'error.cityRequired': 'Indica la ciudad',
  'error.nightsMin': 'Mínimo 1 noche',
  'error.labelRequired': 'Indica un nombre',
  'error.quantityMin': 'Mínimo 1',
  'error.flightRequired': 'Elige un vuelo',
  'warn.tzMissing': 'Sin zona horaria — la duración no será exacta',
  'warn.tzMissingParse': 'Zona horaria no detectada — el tiempo de viaje no será exacto.',
  'parse.missing.from': 'aeropuerto de salida',
  'parse.missing.to': 'aeropuerto de llegada',
  'parse.missing.fromTz': 'zona horaria de salida',
  'parse.missing.toTz': 'zona horaria de llegada',
  'parse.missing.departLocal': 'fecha de salida',
  'parse.missing.departTime': 'hora de salida',
  'parse.missing.arriveLocal': 'fecha de llegada',
  'parse.missing.arriveTime': 'hora de llegada',
  'parse.missing.carrier': 'aerolínea',
  'parse.missing.price': 'precio',
  'library.empty.text': 'Hay dos maneras de añadir un bloque: montarlo a mano o pegar lo que copiaste de una web de búsqueda de vuelos u hoteles.',
  'library.noResults': 'Nada coincide con «{query}»',
  'library.resetSearch': 'Borrar la búsqueda',
  'library.group.flight': 'Vuelos',
  'library.group.transfer': 'Traslados',
  'library.group.lodging': 'Alojamiento',
  'library.group.taxi': 'Taxi',
  'library.group.food': 'Comida',
  'library.group.baggage': 'Equipaje',
  'library.group.misc': 'Otros',
  'compare.hint': 'La comparación empieza con dos opciones — añade otra para ver resaltados los mejores valores.',
  'compare.caption': 'Comparación de opciones de viaje: las filas son indicadores, las columnas opciones',
  'parse.flightN': 'Vuelo {n}',
  'parse.notRecognised': 'No reconocido: {fields}. Revísalo y complétalo a mano.',
  'parse.back': 'Atrás',
  'parse.addSelected': 'Añadir seleccionados ({count})',
  'sync.boot': 'cargando…',
  'sync.local': 'local',
  'sync.saving': 'guardando…',
  'sync.synced': 'guardado',
  'sync.conflict': 'versión del servidor',
  'sync.rejected': 'sin guardar',
  'sync.boot.tip': 'Leyendo el estado del servidor',
  'sync.local.tip': 'Trabajando en local',
  'sync.saving.tip': 'Enviando los cambios al servidor',
  'sync.synced.tip': 'El estado está guardado en la base de datos del servidor',
  'sync.conflict.tip': 'El estado cambió en el servidor; se aplicó su versión',
  'sync.rejected.tip': 'El servidor rechazó el estado',
  'toolbar.snapshots': 'Instantáneas e historial',
  'sync.snapshot': 'Instantánea',
  'sync.snapshot.tip': 'Guardar una instantánea: el estado actual se guarda en la base de datos del servidor',
  'sync.history.tip': 'Historial de instantáneas: vuelve a un estado que guardaste antes',
  'sync.history': 'Historial',
  'sync.snapshots': 'Instantáneas en el servidor',
  'sync.snapshot.saved': 'Instantánea guardada en el servidor',
  'sync.snapshot.failed': 'No se pudo guardar la instantánea',
  'sync.snapshot.notFound': 'Instantánea no encontrada',
  'sync.snapshot.restored': 'Estado restaurado desde la instantánea',
  'sync.snapshot.empty': 'Todavía no hay instantáneas. «Instantánea» guarda el estado actual para que puedas volver a él.',
  'sync.snapshot.unnamed': 'sin título',
  'sync.snapshot.label': 'Instantánea {stamp}',
  'calcwarn.need_transfer': 'hace falta un traslado {from} → {to}',
  'calcwarn.risky_connection': 'conexión ajustada {duration}',
  'calcwarn.negative_connection': 'imposible: la salida es anterior a la llegada',
  'calcwarn.long_layover': 'espera larga — ¿añadir una noche?',
  'calcwarn.orphan_baggage': 'equipaje sin su vuelo en esta opción',
  'calcwarn.rt_full_unpaired': 'el precio cubre la ida y vuelta',
  'calcwarn.missing_tz': 'zona horaria desconocida — duración no calculada',

  'auth.signin': 'Entrar',
  'auth.signup': 'Crear cuenta',
  'auth.signout': 'Salir',
  'auth.email': 'Correo',
  'auth.password': 'Contraseña',
  'auth.password.new': 'Nueva contraseña',
  'auth.password.current': 'Contraseña actual',
  'auth.password.change': 'Cambiar contraseña',
  'auth.password.changed': 'Contraseña cambiada.',
  'auth.name': 'Nombre',
  'auth.submit.signup': 'Crear cuenta',
  'auth.forgot.link': '¿Olvidaste la contraseña?',
  'auth.forgot.title': 'Recuperar contraseña',
  'auth.forgot.hint': 'Enviaremos a esta dirección un enlace para poner una contraseña nueva.',
  'auth.forgot.submit': 'Enviar el enlace',
  'auth.forgot.sent': 'Si existe una cuenta con ese correo, el enlace ya va en camino. Es válido durante una hora.',
  'auth.forgot.back': 'Volver a entrar',
  'auth.reset.title': 'Nueva contraseña',
  'auth.reset.submit': 'Guardar contraseña',
  'auth.reset.done': 'Contraseña cambiada. Todas las sesiones anteriores se cerraron: vuelve a entrar.',
  'auth.verify.title': 'Confirmación del correo',
  'auth.verify.banner': 'Confirma tu correo: sin él no podrás recuperar el acceso a la cuenta.',
  'auth.verify.resend': 'Enviar el correo otra vez',
  'auth.verify.sent': 'Correo enviado: revisa tu bandeja.',
  'auth.verify.ok': 'Correo confirmado.',
  'auth.verify.fail': 'El enlace no es válido o ha caducado.',
  'auth.guest.hint': 'Sin entrar, los viajes se guardan solo en este navegador.',
  'auth.subtitle': 'Con una cuenta tus viajes dejan de vivir en un solo navegador: ábrelos desde cualquier dispositivo.',
  'auth.tabs.label': 'Entrar o crear cuenta',
  'auth.password.show': 'Mostrar la contraseña',
  'auth.password.hide': 'Ocultar la contraseña',
  'auth.capslock': 'Bloq Mayús está activado',
  'auth.busy': 'Un momento…',
  'auth.toApp': 'Al planificador',
  'auth.pw.rule': 'Al menos 10 caracteres.',
  'auth.pw.short': 'Demasiado corta: al menos 10 caracteres',
  'auth.pw.long': 'Demasiado larga',
  'auth.pw.common': 'Demasiado común: es la primera que se prueba',
  'auth.pw.same_as_email': 'La contraseña no debe repetir tu correo',
  'auth.err.invalid_credentials': 'Correo o contraseña incorrectos.',
  'auth.err.invalid_email': 'Revisa la dirección de correo.',
  'auth.err.weak_password': 'Esa contraseña no sirve.',
  'auth.err.email_taken': 'Ya existe una cuenta con ese correo.',
  'auth.err.blocked': 'La cuenta está bloqueada.',
  'auth.err.rate_limited': 'Demasiados intentos. Inténtalo más tarde.',
  'auth.err.bad_token': 'El enlace no es válido o ha caducado.',
  'auth.err.unauthorized': 'Necesitas entrar.',
  'auth.err.forbidden': 'Permisos insuficientes.',
  'auth.err.self_change': 'No puedes cambiar tu propio rol ni estado.',
  'auth.err.origin': 'Solicitud rechazada por seguridad.',
  'auth.err.network': 'El servidor no responde.',
  'auth.err.unknown': 'Algo salió mal.',
  'auth.err.restorable': 'Ya existía una cuenta con este correo — puedes restaurarla.',
  'auth.restore.title': 'Restaurar cuenta',
  'auth.restore.hint': 'Ya existía una cuenta con este correo — introduce la contraseña anterior para recuperarla junto con sus viajes.',
  'auth.restore.submit': 'Restaurar',
  'auth.restore.back': 'Volver al registro',

  'account.title': 'Cuenta',
  'account.section.account': 'Cuenta',
  'account.appearance': 'Apariencia',
  'account.guest.title': 'Sin iniciar sesión',
  'account.emailState': 'Estado del correo',
  'account.verified': 'confirmado',
  'account.unverified': 'sin confirmar',
  'account.save': 'Guardar',
  'account.saved': 'Guardado',
  'account.admin': 'Administración',
  'account.menu.admin.hint': 'Usuarios y ajustes de reconocimiento',
  'account.password.warning': 'Cambiar la contraseña cierra las demás sesiones.',
  'account.menu.language': 'Idioma',
  'account.menu.language.hint': 'Idioma de la interfaz',
  'account.menu.trips.hint': 'Todos tus viajes',
  'account.menu.trips.hint.guest': 'Los viajes están disponibles tras iniciar sesión',
  'account.menu.pricing': 'Precios',
  'account.menu.pricing.hint': 'Planes, precios y opciones adicionales',
  'account.menu.settings': 'Ajustes',
  'account.menu.settings.hint': 'Contraseña, correo, eliminar cuenta',
  'account.menu.person.hint': 'Paleta, tamaño de texto y espaciado: adapta la interfaz a tu gusto',
  'account.menu.signout.hint': 'Cerrar la sesión en este dispositivo',
  'account.delete.title': 'Eliminar cuenta',
  'account.delete.hint': 'La cuenta y todos sus viajes dejarán de estar disponibles justo después de eliminarla.',
  'account.delete.action': 'Eliminar cuenta',
  'account.delete.confirm': 'La cuenta y todos sus viajes dejarán de estar disponibles. Solo podrás recuperarlos registrándote de nuevo con el mismo correo.',


  'admin.title': 'Usuarios',
  'admin.back': 'Al planificador',
  'admin.search': 'Buscar por correo o nombre',
  'admin.col.user': 'Usuario',
  'admin.col.role': 'Rol',
  'admin.col.status': 'Estado',
  'admin.col.created': 'Registro',
  'admin.col.lastLogin': 'Última entrada',
  'admin.col.data': 'Datos',
  'admin.col.actions': 'Acciones',
  'admin.role.user': 'Usuario',
  'admin.role.admin': 'Administrador',
  'admin.status.active': 'Activo',
  'admin.status.blocked': 'Bloqueado',
  'admin.status.deleted': 'Eliminado',
  'admin.action.block': 'Bloquear',
  'admin.action.unblock': 'Desbloquear',
  'admin.action.promote': 'Dar permisos de admin',
  'admin.action.demote': 'Quitar permisos de admin',
  'admin.action.delete': 'Eliminar',
  'admin.action.view': 'Datos',
  'admin.delete.confirm': '¿Eliminar {email} junto con todos sus viajes e instantáneas? No se puede deshacer.',
  'admin.empty': 'No se encontró a nadie',
  'admin.prev': 'Anterior',
  'admin.next': 'Siguiente',
  'admin.page': '{from}–{to} de {total}',
  'admin.forbidden': 'Se requieren permisos de administrador.',
  'admin.stats.users': 'Usuarios',
  'admin.stats.newWeek': 'Nuevos esta semana',
  'admin.stats.activeWeek': 'Activos esta semana',
  'admin.stats.verified': 'Correo confirmado',
  'admin.stats.blocked': 'Bloqueados',
  'admin.stats.snapshots': 'Instantáneas',
  'admin.stats.size': 'Volumen de datos',
  'admin.view.title': 'Datos: {email}',
  'admin.view.readonly': 'Datos de otra persona, solo lectura. Esta consulta queda registrada.',
  'admin.view.empty': 'Este usuario aún no ha guardado nada.',
  'admin.view.variants': 'Opciones',
  'admin.view.segments': 'Bloques',
  'admin.view.showRaw': 'Mostrar JSON',
  'admin.view.hideRaw': 'Ocultar JSON',

  'sync.snapshot.beforeLogin': 'Antes de entrar',
  'auth.guest.locked': 'Disponible tras iniciar sesión',
  'variant.add.guest': 'Sin iniciar sesión dispones de {count} opciones. Entra para añadir más.',

  'share.title': 'Compartir el viaje',
  // ── Viajes ──────────────────────────────────────────────────────────────
  'trips.title': 'Mis viajes',
  'trips.all': 'Mis viajes ({count})',
  'trips.none': 'aún sin crear',
  'trips.untitled': 'Sin nombre',
  'trips.newName': 'Viaje nuevo',
  'trips.create': '+ Viaje nuevo',
  'trips.open': 'abierto',
  'trips.openAction': 'Abrir',
  'trips.rename': 'Renombrar',
  'trips.rename.ownerOnly': 'Solo el propietario puede renombrar el viaje',
  'trips.name': 'Nombre del viaje',
  'trips.copyLink': 'Copiar enlace',
  'trips.shared': 'tiene enlace público',
  'trips.linkClosed': 'sin enlace público',
  'trips.info.button': 'Detalles del viaje «{name}»',
  'trips.info.title': 'Sobre «{name}»',
  'trips.updated': 'editado el {date}',
  'trips.created': 'creado el {date}',
  'trips.delete': 'Eliminar',
  'trips.delete.title': '¿Eliminar el viaje?',
  'trips.delete.text': 'El viaje «{name}» y su historial se eliminarán de forma definitiva. Su enlace público dejará de funcionar.',
  'trips.empty': 'Todavía no hay viajes. Crea el primero y tu trabajo empezará a guardarse en el servidor.',
  'trips.guest.title': 'Se necesita una cuenta',
  'trips.guest': 'Varios viajes requieren una cuenta: el trabajo de invitado vive en este navegador como un solo viaje.',
  'trips.back': 'Volver al planificador',
  'trips.section.mine': 'Mis viajes',
  'trips.section.shared': 'Compartidos conmigo',
  'trips.section.shared.hint': 'Aquí te han invitado como coeditor: puedes cambiar el contenido, pero no el viaje en sí.',
  'trips.byOwner': 'propietario: {name}',
  'trips.members.one': '{count} coeditor',
  'trips.members.few': '{count} coeditores',
  'trips.members.many': '{count} coeditores',
  'trips.members.other': '{count} coeditores',
  'trips.leave': 'Salir',
  'trips.leave.title': '¿Salir del viaje?',
  'trips.leave.text': 'El viaje «{name}» desaparecerá de tu lista. El propietario lo conserva y puede volver a invitarte.',

  // ── Acceso compartido ───────────────────────────────────────────────────
  'trips.access': 'Acceso',
  'trips.access.title': 'Acceso al viaje «{name}»',
  'trips.access.lead': 'Los coeditores trabajan sobre el mismo viaje: la biblioteca de bloques, las variantes y los precios. Solo el propietario puede renombrarlo, publicar el enlace e invitar a otros.',
  'trips.access.invite': 'Invitar por correo',
  'trips.access.add': 'Invitar',
  'trips.access.hint': 'Con cuenta, el viaje aparece en su lista al instante. Sin cuenta, recibe una invitación por correo y el acceso tras registrarse.',
  'trips.access.pending': 'Aún no hay cuenta con {email}: enviamos una invitación — el acceso aparecerá justo después del registro.',
  'trips.access.empty': 'De momento nadie más que el propietario.',
  'trips.access.you': 'eres tú',
  'trips.access.remove': 'Quitar',
  'trips.access.role.owner': 'propietario',
  'trips.access.role.editor': 'coeditor',
  'trips.access.err.no_user': 'Todavía no hay nadie con ese correo. Pídele que se registre e invítalo de nuevo.',
  'trips.access.err.already_member': 'Ya está en la lista.',
  'trips.access.err.is_owner': 'Es el propietario del viaje.',
  'trips.access.err.too_many': 'Este viaje ya tiene demasiados coeditores.',
  'trips.access.err.invalid_email': 'Revisa la dirección de correo.',
  'trips.access.err.blocked': 'Esa cuenta está bloqueada.',
  'trips.access.err.unknown': 'No ha funcionado. Inténtalo de nuevo.',

  // ── Plantillas y bloques frecuentes ─────────────────────────────────────
  'tpl.start': 'Empezar con una plantilla',
  'tpl.start.hint': 'Un viaje típico en un clic: los bloques ya están puestos y los precios vacíos.',
  'tpl.create': 'Crear viaje',
  'tpl.fill': 'Montar la opción',
  'tpl.page.note': 'La plantilla crea un viaje nuevo aparte. Tus viajes actuales no cambian.',
  'tpl.board.note': 'La plantilla añade una opción con estos bloques. Lo que ya has montado se queda como está.',
  'tpl.blocks': 'Bloques frecuentes',
  'tpl.blocks.hint': 'Un toque añade el bloque a la biblioteca con precio cero: solo queda escribir el importe.',
  'tpl.added': 'Bloque «{name}» añadido a la biblioteca',
  'tpl.weekend.name': 'Fin de semana de dos días',
  'tpl.weekend.desc': 'Dos noches en una ciudad: trayecto desde el aeropuerto, alojamiento, comida y taxis.',
  'tpl.beach.name': 'Una semana en la playa',
  'tpl.beach.desc': 'Siete noches en el mismo sitio: traslado al hotel, comida y excursiones.',
  'tpl.business.name': 'Viaje de trabajo',
  'tpl.business.desc': 'Tres noches de trabajo: alojamiento, taxis, comida y conexión.',
  'tpl.twoCities.name': 'Dos ciudades en un viaje',
  'tpl.twoCities.desc': 'Tres noches en cada una y el trayecto entre ellas.',
  'tpl.mountains.name': 'Montaña y esquí',
  'tpl.mountains.desc': 'Cinco noches junto a la pista: forfait, alquiler de equipo y comida.',
  'tpl.place.airport': 'Aeropuerto',
  'tpl.place.city': 'Ciudad',
  'tpl.place.hotel': 'Hotel',
  'tpl.place.city1': 'Primera ciudad',
  'tpl.place.city2': 'Segunda ciudad',
  'tpl.block.taxi': 'Taxis por la ciudad',
  'tpl.block.food': 'Comida',
  'tpl.block.insurance': 'Seguro',
  'tpl.block.esim': 'Datos móviles',
  'tpl.block.tickets': 'Excursiones y entradas',
  'tpl.block.visa': 'Visado y tasas',
  'tpl.block.skipass': 'Forfait',
  'tpl.block.gear': 'Alquiler de equipo',
  'share.tip': 'Un enlace al viaje para quien no tiene cuenta',
  'share.hint': 'El enlace muestra una copia del viaje tal como estaba al publicarlo: solo lectura, sin edición. Los cambios posteriores no se ven hasta que pulses «Actualizar».',
  'share.create': 'Crear enlace',
  'share.update': 'Actualizar',
  'share.revoke': 'Desactivar el enlace',
  'share.copy': 'Copiar',
  'share.copied': 'Copiado ✓',
  'share.link': 'Enlace del viaje',
  'share.look': 'El enlace se abre con tu tema, tamaño e idioma: quien lo reciba verá el viaje tal como lo armaste.',
  'share.updated': 'Actualizado: {date}',
  'share.views': 'visitas: {count}',
  'share.failed': 'No se pudo crear el enlace.',
  'share.readonly': 'Solo lectura',
  'share.travelers': 'Viajeros: {count}',
  'share.cheapest': 'El más barato',
  'share.segments': 'De qué se compone el precio',
  'share.noPrice': 'Aún no hay precios',
  'share.notFound': 'El enlace no existe o fue desactivado.',
  'share.empty': 'Este viaje todavía no tiene opciones.',
  'share.makeYours': 'Planifica tu propio viaje',
  'share.preview.variants.one': '{count} opción',
  'share.preview.variants.few': '{count} opciones',
  'share.preview.variants.many': '{count} opciones',
  'share.preview.variants.other': '{count} opciones',
  'share.preview.from': 'desde {price}',
  'share.preview.tail': 'Compara precio y tiempo en Plan Your Trip.',
  'share.preview.empty': 'El viaje aún se está armando: todavía no hay opciones.',

  'library.new': 'new',
  'library.new.title': 'Añadido hace poco',
  'library.new.count': 'Nuevos: {count}',
  'library.new.dismiss': 'Marcar como vistos',
  'library.fromSource': 'Desde una fuente',
  'library.fromSource.desc': 'Puedes reconocer un vuelo a partir de texto, un enlace o una captura de pantalla.',

  'recog.title': 'Añadir desde una fuente',
  'recog.tab.text': 'Descripción',
  'recog.tab.url': 'Enlace',
  'recog.tab.screenshot': 'Captura',
  'recog.text.label': 'Resultados de búsqueda o una breve descripción del vuelo',
  'recog.text.placeholder': '27 de diciembre, Wizz Air Lárnaca 11:55 → Ereván 16:00, 149 $',
  'recog.url.label': 'Enlace al vuelo o a la reserva',
  'recog.url.placeholder': 'https://…',
  'recog.url.invalid': 'Hace falta un enlace http:// o https://',
  'recog.shot.label': 'Capturas de la búsqueda, del correo o del billete',
  'recog.shot.drop': 'Arrastra imágenes, pégalas (Ctrl+V) o elige archivos',
  'recog.shot.pick': 'Elegir archivos',
  'recog.shot.add': 'Añadir más',
  'recog.shot.remove': 'Quitar todas',
  'recog.shot.removeOne': 'Quitar la captura {num}',
  'recog.shot.tooBig': 'La imagen supera {limit} MB',
  'recog.shot.tooMany': 'No más de {limit} imágenes a la vez: sin añadir {count}',
  'recog.shot.tooBigTotal': 'Juntas las imágenes no deben superar {limit} MB: sin añadir {count}',
  'recog.shot.badType': 'No parece una imagen legible: sirven PNG, JPEG, WebP o HEIC',
  'recog.shot.ready': 'Imágenes: {count} de {limit} · {size}',
  'recog.shot.multi': 'Varias imágenes viajan en una sola petición: los vuelos de todas van a una misma lista.',
  'recog.run': 'Reconocer',
  'recog.running': 'Reconociendo…',
  'recog.review': 'Revisa y corrige antes de añadir',
  'recog.model': 'Modelo: {model}',
  'recog.local': 'Analizado en local, sin IA',
  'recog.fallback': 'La IA no respondió: se muestra el resultado del análisis local.',
  'recog.privacy.ai': 'El texto, el enlace o la imagen van al modelo de IA elegido. Nada se guarda solo: todo se puede revisar y corregir antes de añadirlo.',
  'recog.privacy.local': 'Sin peticiones de red: solo se analiza el texto pegado. Todo se puede revisar y corregir antes de añadirlo.',
  'recog.off': 'El administrador desactivó el reconocimiento con IA: queda el análisis local del texto.',
  'recog.off.guest': 'El reconocimiento con IA está disponible al iniciar sesión.',
  'recog.nothing': 'No se encontraron vuelos: prueba con otro texto, enlace o captura.',
  'recog.notes': 'Cómo se analizó',
  'recog.err.unauthorized': 'Hay que iniciar sesión.',
  'recog.err.disabled': 'El administrador desactivó el reconocimiento.',
  'recog.err.no_model': 'No hay modelo elegido para esta fuente: configúralo en el panel de administración.',
  'recog.err.no_key': 'Falta la clave del proveedor de IA: configúrala en el panel de administración.',
  'recog.err.provider': 'El proveedor devolvió un error: {message}',
  'recog.err.timeout': 'El modelo no respondió a tiempo. Inténtalo de nuevo.',
  'recog.err.rate_limited': 'Demasiadas peticiones. Inténtalo más tarde.',
  'recog.err.too_large': 'La petición es demasiado grande.',
  'recog.err.bad_response': 'El modelo respondió fuera de formato y no hay nada que interpretar. Inténtalo de nuevo o envía menos imágenes a la vez.',
  'recog.err.fetch_url': 'No se pudo abrir el enlace: pega el texto o una captura.',
  'recog.err.bad_request': 'No hay nada que reconocer.',
  'recog.err.unknown': 'El reconocimiento falló. Inténtalo de nuevo.',

  // ── Magia de variantes ──────────────────────────────────────────────────
  'magic.title': 'Magia de variantes',
  'magic.action': 'Magia',
  'magic.tip': 'Crear las mejores variantes a partir de la biblioteca',
  'magic.from': 'Origen',
  'magic.from.placeholder': 'LCA o Lárnaca',
  'magic.to': 'Destino',
  'magic.to.placeholder': 'EVN o Ereván',
  'magic.roundTrip': 'Ida y vuelta',
  'magic.stay': 'Estancia, días',
  'magic.stay.from': 'de',
  'magic.stay.to': 'a',
  'magic.stay.hint': 'Días entre la llegada al destino y el vuelo de vuelta.',
  'magic.wishes': 'Preferencias',
  'magic.wishes.placeholder': 'Sin escalas nocturnas, menos de 500 $ por persona, como máximo una escala',
  'magic.count': 'Cuántas variantes',
  'magic.count.hint': 'Huecos libres en el tablero: {count}',
  'magic.run': 'Hacer magia',
  'magic.running': 'Conjurando…',
  'magic.library': 'A la petición van todos los bloques de la biblioteca: {count}',
  'magic.library.empty': 'La biblioteca no tiene ningún vuelo: la magia no tiene con qué montar variantes.',
  'magic.library.trimmed': 'La biblioteca tiene {count} bloques y en el modelo elegido caben {limit}: el resto no se enviará.',
  'magic.privacy': 'La lista de bloques de la biblioteca y tus preferencias se envían al modelo de IA elegido. Las variantes llegan al tablero solo tras tu confirmación.',
  'magic.review': 'Revisa las variantes antes de añadirlas',
  'magic.model': 'Modelo: {model}',
  'magic.notes': 'Observaciones',
  'magic.nothing': 'No se pudo montar ninguna variante: concreta las preferencias o añade más vuelos a la biblioteca.',
  'magic.add': 'Añadir variantes ({count})',
  'magic.back': 'Atrás',
  'magic.stayDays': 'Estancia: {days} d.',
  'magic.segments': 'Bloques: {count}',
  'magic.noRoom': 'No hay hueco libre en el tablero: borra alguna variante.',
  'magic.tooMany': 'Has elegido más variantes de las que caben en el tablero: desmarca las que sobran.',
  'magic.off': 'La magia está desactivada por el administrador.',
  'magic.off.guest': 'La magia está disponible tras iniciar sesión.',
  'magic.err.unauthorized': 'Hace falta iniciar sesión.',
  'magic.err.disabled': 'Las funciones de IA están desactivadas por el administrador.',
  'magic.err.no_model': 'No hay modelo elegido para la magia: configúralo en la administración.',
  'magic.err.no_key': 'No hay clave del proveedor de IA: configúrala en la administración.',
  'magic.err.bad_request': 'Petición rechazada: {message}',
  'magic.err.too_large': 'La biblioteca es demasiado grande para una sola petición.',
  'magic.err.rate_limited': 'Demasiadas peticiones. Inténtalo más tarde.',
  'magic.err.timeout': 'El modelo no respondió a tiempo. Inténtalo de nuevo.',
  'magic.err.provider': 'El proveedor devolvió un error: {message}',
  'magic.err.bad_response': 'No se pudo interpretar la respuesta del modelo.',
  'magic.err.unknown': 'La magia falló. Inténtalo de nuevo.',

  'admin.tab.users': 'Usuarios',
  'admin.tab.ai': 'IA',
  'admin.tab.logs': 'Registro',
  'admin.tab.chats': 'Chats',
  'admin.tab.pricing': 'Precios',
  'admin.tab.trips': 'Viajes',
  'admin.action.enter': 'Entrar como',
  'admin.action.limits': 'Límites',
  'admin.limits.title': 'Límites del usuario',
  'admin.limits.hint': 'Campo vacío: valor por defecto.',
  'admin.limits.default': 'por defecto',
  'admin.limits.maxVariants': 'Variantes en el tablero',
  'admin.limits.aiDaily': 'Peticiones de IA al día',
  'admin.limits.magicDaily': 'Usos de magia al día',
  'admin.limits.maxTrips': 'Viajes',
  'admin.logs.filter.problems': 'Errores y avisos',
  'admin.logs.filter.ai': 'Peticiones de IA',
  'admin.logs.filter.all': 'Todos',
  'admin.logs.clear': 'Vaciar registro',
  'admin.logs.clear.confirm': '¿Eliminar todas las entradas del registro?',
  'admin.logs.empty': 'No hay entradas',
  'admin.logs.retention': 'Se guarda 7 días, rotación diaria',
  'admin.logs.noDetails': 'Esta entrada no tiene detalles',
  'admin.logs.details.meta': 'Resumen',
  'admin.logs.details.request': 'Petición al modelo',
  'admin.logs.details.response': 'Respuesta del modelo',
  'admin.chats.new': 'Nueva conversación',
  'admin.chats.search': 'Correo del usuario…',
  'admin.chats.empty': 'Aún no hay conversaciones',
  'admin.chats.pick': 'Elige una conversación a la izquierda',
  'admin.chats.start': 'Aún no hay mensajes: escribe primero',
  'admin.chats.placeholder': 'Respuesta al usuario…',
  'admin.chats.send': 'Enviar',
  'admin.chats.clear': 'Eliminar conversación',
  'admin.chats.clear.confirm': '¿Eliminar toda la conversación con {email}?',
  'admin.chats.close': 'Finalizar conversación',
  'admin.chats.close.confirm': '¿Finalizar la conversación con {email}? El historial quedará visible solo para administradores.',
  'admin.chats.lang': 'Idioma del usuario',
  'admin.chats.client': 'Cliente del usuario',
  'admin.chats.files': 'Archivos en el chat',
  'hero.title': 'Planifica tu viaje en variantes',
  'hero.lead': 'Este es un viaje de demostración: tres rutas lado a lado — precios, conexiones y avisos en un solo tablero. Pruébalo o crea el tuyo; una cuenta lo guarda todo y desbloquea la magia de la IA.',
  'hero.signup': 'Crear una cuenta',
  'hero.try': 'Solo probar',
  'support.title': 'Soporte',
  'support.close': 'Cerrar',
  'support.minimize': 'Minimizar',
  'support.closed': 'La conversación ha finalizado',
  'support.attach': 'Adjuntar archivo',
  'support.attach.bad': 'Archivo no válido: imagen o PDF de hasta 3 MB',
  'support.empty': 'Escríbenos: responderemos aquí',
  'support.placeholder': 'Mensaje…',
  'support.send': 'Enviar',
  'support.team': 'Soporte',
  'support.err': 'No se envió: inténtalo de nuevo',
  'admin.stats.trips': 'Viajes',
  'admin.stats.tripsShared': 'Con colaboradores',
  'admin.stats.tripsPublished': 'Publicados',
  'admin.trips.search': 'Buscar por nombre o propietario',
  'admin.trips.col.trip': 'Viaje',
  'admin.trips.col.owner': 'Propietario',
  'admin.trips.col.members': 'Colaboradores',
  'admin.trips.col.share': 'Enlace',
  'admin.trips.col.updated': 'Modificado',
  'admin.trips.col.created': 'Creado',
  'admin.trips.published': 'Abrir',
  'admin.trips.enter': 'Entrar como propietario',
  'admin.trips.empty': 'No se encontraron viajes',
  'admin.trips.view.title': 'Viaje: {name}',
  'admin.trips.view.empty': 'El viaje todavía está vacío.',
  'impersonate.confirm': '¿Entrar en la cuenta de {email}? A partir de ahí actúas en su nombre: todo lo que cambies, cambia para esa persona. La acción queda registrada.',
  'impersonate.bar': 'Estás actuando en nombre de {email}. Tu cuenta: {admin}.',
  'impersonate.stop': 'Volver a mi cuenta',
  'auth.err.impersonating': 'Esta acción no está disponible en el modo «en nombre de».',
  'admin.pricing.source': 'Aquí se edita el texto en ruso y la estructura de la página. Los demás idiomas están en la pestaña «Traducciones».',
  'admin.tr.note': 'El original a la izquierda, la traducción a la derecha. Un campo vacío significa que el visitante verá el original.',
  'admin.tr.target': 'Idioma de destino',
  'admin.tr.progress': '{done} de {total} traducidos',
  'admin.tr.done': 'Traducido por completo',
  'admin.tr.onlyEmpty': 'Solo lo no traducido',
  'admin.tr.source': 'Original',
  'admin.tr.translation': 'Traducción',
  'admin.pricing.save': 'Guardar',
  'admin.pricing.saved': 'Guardado',
  'admin.pricing.lang': 'Idioma',
  'admin.pricing.reset': 'Restaurar lo incorporado',
  'admin.pricing.reset.title': '¿Restaurar el contenido incorporado?',
  'admin.pricing.reset.text': 'Se borrarán todos los cambios de la página de precios y la página volverá a construirse con lo que hay en el código. Esto no se puede deshacer.',
  'admin.pricing.updated': 'editado {date}, {name}',
  'admin.pricing.never': 'aún sin cambios',
  'admin.pricing.section.price': 'Precios',
  'admin.pricing.section.texts': 'Textos de la página',
  'admin.pricing.section.sections': 'Secciones de la tabla',
  'admin.pricing.section.features': 'Filas de los planes',
  'admin.pricing.section.highlights': 'En las tarjetas de plan',
  'admin.pricing.section.extras': 'Compras únicas',
  'admin.pricing.section.soon': 'En preparación',
  'admin.pricing.section.faq': 'Preguntas',
  'admin.pricing.currency': 'Moneda',
  'admin.pricing.monthly': 'Al mes',
  'admin.pricing.yearly': 'Al año',
  'admin.pricing.discount': '{percent}% de descuento',
  'admin.pricing.discount.none': 'el año no sale más barato que el mes',
  'admin.pricing.id': 'Identificador',
  'admin.pricing.name': 'Nombre',
  'admin.pricing.hint': 'Aclaración',
  'admin.pricing.question': 'Pregunta',
  'admin.pricing.answer': 'Respuesta',
  'admin.pricing.price': 'Precio',
  'admin.pricing.parent': 'Sección',
  'admin.pricing.free': 'Free',
  'admin.pricing.pro': 'Pro',
  'admin.pricing.shown': 'Mostrar',
  'admin.pricing.add': 'Añadir',
  'admin.pricing.remove': 'Eliminar',
  'admin.pricing.up': 'Subir',
  'admin.pricing.down': 'Bajar',
  'admin.pricing.kind.count': 'Número',
  'admin.pricing.kind.unlimited': 'Sin límite',
  'admin.pricing.kind.yes': 'Sí',
  'admin.pricing.kind.none': 'No',
  'admin.pricing.kind.text': 'Texto propio',
  'admin.pricing.per.total': 'en total',
  'admin.pricing.per.month': 'al mes',
  'admin.pricing.key': 'Clave del diccionario',
  'admin.pricing.highlights.hint': 'Las filas marcadas aparecen en la tarjeta del plan, arriba de la página. Cinco es el límite de legibilidad.',
  'admin.pricing.empty': 'Todavía no hay nada',
  'admin.ai.title': 'Funciones de IA',
  'admin.ai.hint': 'El reconocimiento de fuentes, la magia de variantes y la comprobación de precios funcionan con un proveedor de IA. Cada tarea tiene su modelo: la captura necesita uno que acepte imágenes; la magia, uno que razone; los precios, uno con búsqueda web.',
  'admin.ai.enabled': 'Activar las funciones de IA',
  'admin.ai.enabled.hint': 'Desactivado: solo queda el análisis local del texto y la magia de variantes no está disponible.',
  'admin.ai.key': 'Clave del proveedor de IA',
  'admin.ai.key.present': 'Clave configurada (…{tail})',
  'admin.ai.key.env': 'La clave se toma de una variable de entorno del servidor',
  'admin.ai.key.missing': 'Sin clave',
  'admin.ai.key.placeholder': 'sk-…',
  'admin.ai.key.hint': 'La clave se queda en el servidor y nunca llega al navegador.',
  'admin.ai.key.clear': 'Borrar la clave',
  'admin.ai.source.screenshot': 'Captura',
  'admin.ai.source.url': 'Enlace',
  'admin.ai.source.text': 'Descripción de texto',
  'admin.ai.source.screenshot.hint': 'El modelo debe aceptar imágenes.',
  'admin.ai.source.url.hint': 'El servidor abre la página y entrega su texto al modelo.',
  'admin.ai.source.text.hint': 'Resultados de búsqueda pegados o un par de líneas a mano.',
  'admin.ai.source.magic': 'Magia: montaje de variantes',
  'admin.ai.source.magic.hint': 'Monta variantes de viaje listas con los bloques de la biblioteca: elige vuelos, los ordena en el tiempo y evita que se solapen.',
  'admin.ai.source.prices': 'Precios: precio actual del vuelo',
  'admin.ai.source.prices.hint': 'Busca en los sitios de reservas el precio de un vuelo concreto y devuelve las ofertas encontradas con un enlace a la fuente.',
  'admin.ai.source.settings': 'Ajustes de esta tarea',
  'admin.ai.source.settings.hint': 'Un campo vacío significa que se aplica el valor común de abajo. El límite diario de aquí se cuenta aparte del común: gana el más estricto.',
  'admin.ai.override.inherit': 'común: {value}',
  'admin.ai.advanced.hint': 'Valores comunes para todas las tareas. Cada tarea puede cambiarlos en su propia tarjeta.',
  'admin.ai.req.title': 'Requisitos del modelo para la magia',
  'admin.ai.req.context': 'Contexto desde 128K tokens: toda la biblioteca va en una sola petición.',
  'admin.ai.req.json': 'Soporte de respuesta estructurada (json_schema) y cumplimiento exacto del esquema.',
  'admin.ai.req.reasoning': 'Razonamiento fuerte: el modelo compara horas en UTC, calcula escalas y planifica la ruta.',
  'admin.ai.req.tokens': 'La respuesta es larga, así que para la magia el servidor sube el límite de tokens a 2000 como mínimo y el tiempo de espera a 60 segundos. Los modelos de razonamiento gastan ese mismo presupuesto en pensar: pon 8000 o la respuesta llegará cortada.',
  'admin.ai.req.vision': 'Las imágenes no hacen falta: aquí solo va texto y un modelo con visión no aporta nada.',
  'admin.ai.req.temp': 'Temperatura 0: el montaje debe ser reproducible. Los modelos de razonamiento (GPT-5) ignoran el parámetro, y no pasa nada.',
  'admin.ai.req.recommend': 'Recomendamos modelos de nivel pro/thinking (Gemini 2.5 Pro, Claude Sonnet 4.5, GPT-5). Los modelos baratos y rápidos confunden la cronología e inventan ids inexistentes: el servidor descarta esas variantes y la persona ve un resultado vacío.',
  'admin.ai.recommend': 'Barato y suficiente',
  'admin.ai.recommend.hint': 'Precios en $ por 1M de tokens a fecha de {date}, del más barato al más fiable. Es una sugerencia, no un límite: puede elegirse cualquier modelo. Pulse uno para aplicarlo.',
  'admin.ai.model': 'Modelo',
  'admin.ai.model.search': 'Buscar modelos…',
  'admin.ai.model.none': 'Sin elegir',
  'admin.ai.model.vision': 'imágenes',
  'admin.ai.model.free': 'gratis',
  'admin.ai.model.context': 'contexto {tokens}',
  'admin.ai.model.price': '{in} / {out} $ por 1M de tokens',
  'admin.ai.model.custom': 'Escribir el id a mano',
  'admin.ai.model.tools': 'herramientas',
  'admin.ai.model.reasoning': 'razonamiento',
  'admin.ai.model.nojson': 'sin JSON estricto',
  'admin.ai.model.warn.json': 'No servirá: el servidor envía un json_schema estricto y este modelo no lo acepta, así que devolverá un error del proveedor en vez de un análisis.',
  'admin.ai.model.warn.vision': 'No servirá: las capturas se envían como imágenes y este modelo solo acepta texto.',
  'admin.ai.model.warn.tools': 'No servirá: la búsqueda web de precios se conecta mediante herramientas, que este modelo no admite.',
  'admin.ai.model.warn.context': 'Contexto insuficiente: toda la biblioteca de bloques va en una sola petición, hacen falta {min} o más.',
  'admin.ai.model.warn.reasoning': 'Este modelo no permite desactivar el razonamiento, y esos tokens cuentan en el mismo límite de respuesta. Ponga el máximo de tokens en {tokens} como mínimo o la respuesta llegará cortada.',
  'admin.ai.tune.recommend': 'Recomendado: {values}',
  'admin.ai.tune.apply': 'Aplicar',
  'admin.ai.model.unknown': 'El modelo {model} no está en la lista del proveedor',
  'admin.ai.models.reload': 'Actualizar la lista',
  'admin.ai.models.loading': 'Cargando la lista de modelos…',
  'admin.ai.models.failed': 'No se pudo obtener la lista de modelos: {message}',
  'admin.ai.models.count': 'Modelos: {count}',
  'admin.ai.advanced': 'Avanzado',
  'admin.ai.maxTokens': 'Máximo de tokens de respuesta',
  'admin.ai.temperature': 'Temperatura',
  'admin.ai.timeout': 'Tiempo de espera, segundos',
  'admin.ai.limit': 'Peticiones por usuario al día',
  'admin.ai.maxImages': 'Capturas por petición',
  'admin.ai.maxImages.hint': 'Cuántas imágenes envía el usuario en una petición de reconocimiento. Más imágenes: respuesta del modelo más larga y más cara.',
  'admin.ai.save': 'Guardar',
  'admin.ai.saved': 'Guardado',
  'admin.ai.save.failed': 'No se pudo guardar: {message}',
  'admin.ai.test': 'Probar',
  'admin.ai.test.running': 'Probando…',
  'admin.ai.test.ok': 'El modelo respondió en {ms} ms',
  'admin.ai.test.failed': 'La prueba falló: {message}',
  'admin.ai.updated': 'Modificado {date}',

  // ── Onboarding ──────────────────────────────────────────────────────────
  'onboarding.title': 'Cómo funciona',
  'onboarding.tip': 'Tour breve: cómo montar y comparar opciones de viaje',
  'onboarding.step': 'Paso {current} de {total}',
  'onboarding.goto': 'Paso {n}',
  'onboarding.skip': 'Omitir',
  'onboarding.back': 'Atrás',
  'onboarding.next': 'Siguiente',
  'onboarding.done': 'Entendido',
  'onboarding.s1.title': 'Primero, adónde',
  'onboarding.s1.text': 'Asia, Europa, América: crea un viaje para cada destino que estés valorando. Conviven uno junto al otro sin mezclarse, y puedes empezar desde una plantilla.',
  'onboarding.s2.title': 'Dentro del viaje',
  'onboarding.s2.text': 'A la izquierda, la biblioteca de bloques: vuelos, noches, traslados y cualquier gasto. A la derecha, las opciones de ruta que montas con ellos.',
  'onboarding.s3.title': 'Un enlace, un texto o una captura',
  'onboarding.s3.text': 'No hace falta teclear el vuelo. Pega un enlace de reserva, los resultados de búsqueda copiados o una captura del correo: la aplicación saca la ruta, los horarios y el precio, y a ti te queda revisarlo.',
  'onboarding.s4.title': 'Dos opciones en paralelo',
  'onboarding.s4.text': 'Los billetes baratos traen escalas, y dormir por el camino cuesta dinero. Los directos son más caros, pero no piden noches. Monta las dos opciones y ponlas una al lado de la otra.',
  'onboarding.s5.title': 'El total lo decide',
  'onboarding.s5.text': 'Cada opción se suma abajo: billetes, alojamiento y todo lo demás. Ahí se descubre que los billetes baratos salieron más caros.',
  'onboarding.s6.title': 'Planificad entre varios',
  'onboarding.s6.text': 'Invita a un coeditor por correo y trabajará sobre el mismo viaje: la biblioteca, las opciones, los precios. Solo el propietario puede renombrarlo e invitar a otros.',
  'onboarding.s7.title': 'Enseñar el resultado',
  'onboarding.s7.text': 'Un enlace público abre el viaje a quien no tiene cuenta: solo lectura, una copia tal como estaba al publicarla. Los cambios posteriores no se ven hasta que pulses «Actualizar».',
  'onboarding.mock.asia': 'Asia',
  'onboarding.mock.europe': 'Europa',
  'onboarding.mock.america': 'América',
  'onboarding.mock.cheap': 'Económica',
  'onboarding.mock.cheap.note': 'billetes baratos, 2 noches por el camino',
  'onboarding.mock.direct': 'Directa',
  'onboarding.mock.direct.note': 'billetes más caros, sin noches',
  'onboarding.mock.night.ath': 'Noche en Atenas',
  'onboarding.mock.night.mxp': 'Noche en Milán',
  'onboarding.mock.total': 'Total',
  'onboarding.mock.win': '{sum} más barata',

  'pricing.title': 'Precios',
  'pricing.subtitle': 'Planificar un viaje es gratis. Pro quita los límites e incluye lo que calcula y vigila por ti.',
  'pricing.seo.title': 'Precios — Free y Pro',
  'pricing.seo.description': 'Qué incluye el plan gratuito y qué añade Pro: límites de viajes, reconocimiento de reservas, consejos por país y seguimiento de precios.',
  'pricing.back': 'Volver al planificador',
  'pricing.billing.monthly': 'Mensual',
  'pricing.billing.yearly': 'Anual',
  'pricing.billing.save': 'ahorras un {percent}%',
  'pricing.price.free': 'Gratis',
  'pricing.price.perMonth': '{amount} / mes',
  'pricing.price.yearlyHint': '{amount} al año, en un solo cargo',
  'pricing.price.monthlyHint': 'Puedes cancelar cuando quieras',
  'pricing.plan.free': 'Free',
  'pricing.plan.free.tagline': 'Montar un viaje y comparar dos rutas.',
  'pricing.plan.pro': 'Pro',
  'pricing.plan.pro.tagline': 'Para quien vuela a menudo y planifica en serio.',
  'pricing.cta.free': 'Empezar gratis',
  'pricing.cta.pro': 'Pasar a Pro',
  'pricing.cta.soon': 'Pagos muy pronto',
  'pricing.cta.note': 'Todavía estamos configurando los pagos: escríbenos si quieres estar entre los cien primeros.',
  'pricing.compare': 'Qué incluye',
  'pricing.compare.more': 'Todas las funciones · {count} más',
  'pricing.compare.less': 'Contraer',
  'pricing.col.feature': 'Función',
  'pricing.value.unlimited': 'sin límite',
  'pricing.value.none': '—',
  'pricing.value.yes': 'sí',
  'pricing.value.perMonth': '{count} / mes',
  'pricing.value.total': '{count}',
  'pricing.value.month1': '1 mes',
  'pricing.value.forever': 'sin caducidad',
  'pricing.section.planner': 'Planificador',
  'pricing.section.collab': 'Trabajo en común',
  'pricing.section.ai': 'Reconocimiento e IA',
  'pricing.section.ai.hint': 'Cada llamada nos cuesta dinero real, por eso aquí hay contadores y no «todo lo que quieras».',
  'pricing.section.advisor': 'Asesor',
  'pricing.section.export': 'Exportación',
  'pricing.section.watch': 'Vigilancia y avisos',
  'pricing.section.watch.hint': 'Funciona mientras la suscripción esté activa: es una comprobación en segundo plano, no una respuesta puntual.',
  'pricing.f.trips': 'Viajes',
  'pricing.f.variants': 'Opciones de ruta por viaje',
  'pricing.f.library': 'Bloques en la biblioteca',
  'pricing.f.snapshots': 'Instantáneas del historial',
  'pricing.f.currencies': 'Monedas en el cálculo',
  'pricing.f.coauthors': 'Coautores del viaje',
  'pricing.f.shareLife': 'Duración del enlace público',
  'pricing.f.voting': 'Votación de la opción',
  'pricing.f.recognize': 'Reconocer una reserva, enlace o captura',
  'pricing.f.agent': 'Conversación con el planificador de IA',
  'pricing.f.alternatives': 'Alternativas para un bloque',
  'pricing.f.connections': 'Revisión inteligente de conexiones',
  'pricing.f.mailIn': 'Reenviar la reserva a la dirección del viaje',
  'pricing.f.summary': 'Resumen del viaje en texto',
  'pricing.f.dossier': 'Dosier del país: SIM, coche, dinero',
  'pricing.f.visa': 'Visado según tu pasaporte',
  'pricing.f.weather': 'Clima y temporada en esas fechas',
  'pricing.f.local': 'Fiestas, huelgas, qué está cerrado',
  'pricing.f.tips': 'Detalles de aeropuertos y aerolíneas',
  'pricing.f.living': 'Presupuesto diario por ciudad',
  'pricing.f.pdf': 'Exportar a PDF sin marca de agua',
  'pricing.f.ics': 'Calendario: archivo .ics y suscripción',
  'pricing.f.csv': 'Exportar a Excel y CSV',
  'pricing.f.sheets': 'Enviar a Google Sheets y Notion',
  'pricing.f.offline': 'Funciona sin internet',
  'pricing.f.priceWatch': 'Seguimiento del precio del vuelo',
  'pricing.f.flightAlerts': 'Avisos si cambian el vuelo',
  'pricing.f.reminders': 'Recordatorios: check-in, visado, pasaporte',
  'pricing.f.deadlines': 'Plazos de cancelación gratuita',
  'pricing.soon.title': 'En preparación',
  'pricing.soon.hint': 'Todavía no está hecho ni entra en ningún plan. Lo anunciaremos aparte cuando llegue.',
  'pricing.soon.optimizer': 'Optimizador de ruta: mover fechas y orden de ciudades',
  'pricing.soon.split': 'Reparto de gastos entre viajeros',
  'pricing.soon.routes': 'Rutas distintas para cada viajero',
  'pricing.soon.roles': 'Permisos de coautor: ver pero no editar',
  'pricing.soon.leaderboard': 'Ranking de rutas de otros autores',
  'pricing.soon.affiliate': 'Comprar el billete desde el propio bloque',
  'pricing.soon.api': 'API para tus viajes',
  'pricing.soon.whitelabel': 'Dominio y logo propios para agencias',
  'pricing.extras.title': 'Sin suscripción',
  'pricing.extras.hint': 'Si vuelas una vez al año no tiene sentido pagar cada mes. Esto se compra una vez y no caduca.',
  'pricing.extras.dossier': 'Dosier de un país',
  'pricing.extras.dossier.hint': 'Visado, conectividad, alquiler de coche, dinero y detalles de un solo destino.',
  'pricing.extras.pack': 'Paquete de 50 reconocimientos',
  'pricing.extras.pack.hint': 'Reservas, enlaces y capturas convertidos en bloques. Sin fecha de caducidad.',
  'pricing.extras.price': '{amount} pago único',
  'pricing.faq.title': 'Preguntas',
  'pricing.faq.q1': '¿Qué pasa con mis viajes si cancelo Pro?',
  'pricing.faq.a1': 'No se borra nada. Los viajes que superen el límite gratuito pasan a solo lectura: siempre podrás abrirlos, verlos y exportarlos, y editarlos de nuevo al volver a Pro o liberar un hueco.',
  'pricing.faq.q2': '¿Por qué una suscripción y no un pago único?',
  'pricing.faq.a2': 'Una parte funciona sin parar: vigila el precio, detecta un vuelo movido, te recuerda el check-in. Eso es un gasto mensual, no puntual. Lo que sí es puntual lo vendemos como tal: mira «Sin suscripción».',
  'pricing.faq.q3': 'El reconocimiento se equivocó, ¿me ha gastado el límite?',
  'pricing.faq.a3': 'Si el análisis no produjo ningún bloque, el intento no cuenta. Solo se cobra el resultado que de verdad acabó en tu viaje.',
  'pricing.faq.q4': '¿Qué se considera un mes?',
  'pricing.faq.a4': 'Treinta días desde la fecha de pago, no el mes natural. Los contadores se reinician ese mismo día.',

  // ── Лайфхаки ────────────────────────────────────────────────────────────
  'lh.title': 'Trucos',
  'lh.subtitle': 'Consejos breves de quienes ya lo han volado. Puede escribir cualquiera que haya entrado; a la página llega lo que se ha revisado.',
  'lh.seo.title': 'Trucos de viajeros',
  'lh.seo.description': 'Consejos sobre aeropuertos y escalas, alojamiento, alquiler de coche, comida y equipaje, de quienes han estado allí.',
  'lh.back': 'Al planificador',
  'lh.loading': 'Cargando…',
  'lh.empty': 'Aún no hay nada publicado. El tuyo puede ser el primero.',
  'lh.empty.mine': 'Todavía no has propuesto nada.',
  'lh.author.anon': 'Sin nombre',
  'lh.delete': 'Eliminar',
  'lh.editorial': 'De la redacción',
  'lh.lang.hint': 'Aún no hay traducción a tu idioma: el consejo se muestra como se escribió.',
  'lh.confirm': 'Confirmar: a mí me funcionó',
  'lh.confirmed': 'Has confirmado este consejo',
  'lh.filter.category': 'Categoría',
  'lh.filter.country': 'País',
  'lh.filter.mine': 'Míos',
  'lh.any.category': 'Todas las categorías',
  'lh.any.country': 'Todos los países',
  'lh.form.none': 'Sin especificar',
  'lh.cat.flight': 'Vuelo',
  'lh.cat.lodging': 'Alojamiento',
  'lh.cat.car': 'Coche',
  'lh.cat.food': 'Comida',
  'lh.cat.luggage': 'Equipaje',
  'lh.status.pending': 'En revisión',
  'lh.status.published': 'Publicado',
  'lh.status.rejected': 'Rechazado',
  'lh.form.text': 'Tu consejo',
  'lh.form.placeholder': 'Por ejemplo: el aeropuerto de Kutaisi es una sola sala, una hora entre vuelos sobra.',
  'lh.form.category': 'Categoría',
  'lh.form.country': 'País',
  'lh.form.submit': 'Proponer',
  'lh.form.sent': 'Enviado a revisión: aparecerá en la página cuando lo vea un editor.',
  'lh.form.hint': 'Un dato mejor que una historia: los consejos cortos se leen, los largos se pasan de largo.',
  'lh.form.guest': 'Cualquiera que haya entrado puede añadir un consejo.',
  'lh.form.left': 'quedan {count}',
  'lh.err.text_short': 'Demasiado corto: así no se entiende.',
  'lh.err.text_long': 'Demasiado largo. Deja un solo dato y quita el resto.',
  'lh.err.bad_country': 'No se ha podido leer ese país.',
  'lh.err.bad_category': 'Esa categoría no existe.',
  'lh.err.too_many_pending': 'Ya tienes varios consejos en revisión. Espera a que se decidan.',
  'lh.err.rate_limited': 'Demasiado seguido. Inténtalo más tarde.',
  'admin.tab.lifehacks': 'Trucos',
  'admin.lh.queue': 'Cola',
  'admin.lh.empty': 'Nada en este estado.',
  'admin.lh.publish': 'Publicar',
  'admin.lh.reject': 'Rechazar',
  'admin.lh.requeue': 'Devolver a la cola',
  'admin.lh.delete': 'Eliminar',

  // ── Actualización de precios de vuelos ──────────────────────────────────
  'prices.badge.cheaper': 'En {site} ahora está {price}: menos que el tuyo',
  'prices.badge.dearer': 'En {site} ahora está {price}: más que el tuyo',
  'prices.badge.same': 'En {site} ahora está {price}: igual que el tuyo',
  'prices.badge.none': 'No encontramos un precio actual',
  'prices.badge.checked': 'Comprobado {when}',
  'prices.action.refresh': 'Comprobar el precio',
  'prices.action.refreshMany': 'Comprobar precios ({count})',
  'prices.action.history': 'Historial de precios',
  'prices.action.apply': 'Poner este precio',
  'prices.busy': 'Comprobando el precio…',
  'prices.guest': 'Inicia sesión para comprobar precios',
  'prices.history.title': 'Historial de precios',
  'prices.history.empty': 'El precio todavía no se ha comprobado.',
  'prices.history.quotes': 'Lo que encontramos ({count})',
  'prices.history.exact': 'El mismo vuelo',
  'prices.history.applied': 'Puesto en el viaje',
  'prices.history.check': 'Comprobación {when}',
  'prices.notfound': 'En esta comprobación no encontramos un precio actual.',
  'prices.trimmed': 'Comprobamos como máximo {count} vuelos a la vez: el resto se queda con su precio anterior.',
  'prices.err.disabled': 'El administrador desactivó la comprobación de precios.',
  'prices.err.no_key': 'Falta la clave del agregador: configúrala en el panel de administración.',
  'prices.err.rate_limited': 'Demasiadas peticiones. Inténtalo más tarde.',
  'prices.err.quota': 'Se acabaron las comprobaciones de precios de hoy.',
  'prices.err.provider': 'El agregador no respondió. Inténtalo más tarde.',
  'prices.err.bad_request': 'Faltan datos del vuelo: no hay nada que buscar.',
  'prices.err.unknown': 'No se pudieron comprobar los precios. Inténtalo de nuevo.',
  'admin.tab.prices': 'Precios',
  'admin.prices.title': 'Comprobación de precios',
  'admin.prices.enabled': 'Activar la comprobación de precios',
  'admin.prices.enabled.hint': 'Desactivado, nadie ve el botón de comprobar, incluidos los invitados.',
  'admin.prices.providers': 'Fuentes de precios',
  'admin.prices.provider.travelpayouts': 'Travelpayouts (Aviasales)',
  'admin.prices.provider.travelpayouts.hint': 'Token de socio gratuito. Devuelve una caché de búsquedas de Aviasales ya hechas, no una búsqueda en vivo, y solo con coincidencia exacta del número de vuelo: una respuesta vacía aquí es normal.',
  'admin.prices.provider.websearch': 'Búsqueda web del modelo',
  'admin.prices.provider.websearch.hint': 'El modelo recorre por su cuenta los sitios de reservas: la cobertura más amplia y la única fuente que cuesta dinero en cada petición.',
  'admin.prices.provider.page': 'Página del propio vuelo',
  'admin.prices.provider.page.hint': 'Vuelve a leer el enlace guardado en el propio vuelo. El campo del enlace es opcional, y los sitios que dibujan el precio con un script no darán nada.',
  'admin.prices.provider.ready': 'listo',
  'admin.prices.provider.notReady': 'sin configurar',
  'admin.prices.token': 'Token de Travelpayouts',
  'admin.prices.token.hint': 'El token se guarda en el servidor y no se entrega al navegador.',
  'admin.prices.token.present': 'Token configurado (…{tail})',
  'admin.prices.token.clear': 'Eliminar el token',
  'admin.prices.token.cleared': 'Token eliminado',
  'admin.prices.marker': 'Marcador de socio',
  'admin.prices.marker.hint': 'Sin marcador los enlaces de afiliado no generan nada.',
  'admin.prices.marker.needsToken': 'Guarda primero un token: el marcador se almacena solo junto con él.',
  'admin.prices.advanced': 'Avanzado',
  'admin.prices.maxFlights': 'Vuelos por petición',
  'admin.prices.maxFlights.hint': 'Los vuelos sobrantes se recortan con una nota en lugar de rechazar toda la petición.',
  'admin.prices.sanity': 'Corredor de cordura',
  'admin.prices.sanity.hint': 'Los precios fuera del rango «base × mín … base × máx» se descartan: así se filtran el precio de un solo tramo en vez del par, una moneda ajena y la invención del modelo.',
  'admin.prices.sanityMin': 'Multiplicador inferior',
  'admin.prices.sanityMax': 'Multiplicador superior',
  'admin.prices.save': 'Guardar',
  'admin.prices.saved': 'Guardado',
  'admin.prices.updated': 'Modificado {when}, {who}',
  'admin.prices.err.load': 'No se pudieron cargar los ajustes de precios.',
  'admin.prices.err.save': 'No se pudieron guardar los ajustes de precios.',
  'admin.prices.err.range': 'El valor está fuera del rango permitido.',
};

/** Греческий. Порядок ключей общий со всеми словарями — см. EN. */
const EL: Record<TranslationKey, string> = {
  'app.brand': 'Σχεδίασε το ταξίδι σου',
  'app.language': 'Γλώσσα διεπαφής',
  'app.menu': 'Μενού',
  'menu.section.trip': 'Ταξίδι',
  'menu.section.trips': 'Ταξίδια',
  'menu.section.view': 'Εμφάνιση',
  'menu.section.account': 'Λογαριασμός',
  'app.library': 'Βιβλιοθήκη μπλοκ',
  'app.board': 'Επιλογές ταξιδιού',
  'app.compare': 'Σύγκριση επιλογών',
  'app.panel.hide': 'Απόκρυψη: {name}',
  'app.panel.show': 'Εμφάνιση: {name}',
  'app.panel.width': 'Πλάτος πίνακα «{name}»',
  'toolbar.travelers': 'Αριθμός ταξιδιωτών',
  'toolbar.travelers.dec': 'Λιγότεροι ταξιδιώτες',
  'toolbar.travelers.inc': 'Περισσότεροι ταξιδιώτες',
  'toolbar.currency': 'Νόμισμα',
  'toolbar.currency.tip': 'Νόμισμα για όλα τα ποσά και τη σύγκριση',
  'toolbar.rates': 'Νομίσματα και ισοτιμίες',
  'toolbar.rates.tip': 'Νομίσματα και ισοτιμίες — ως προς {base}',

  'person.title': 'Εξατομίκευση',
  'person.tip': 'Εξατομίκευση: θέμα χρωμάτων και μέγεθος κειμένου',
  'person.theme': 'Θέμα χρωμάτων',
  'person.mode.light': 'φωτεινό',
  'person.mode.dark': 'σκοτεινό',
  'person.size': 'Μέγεθος κειμένου',
  'person.size.compact': 'Συμπαγές',
  'person.size.dense': 'Πυκνό',
  'person.size.default': 'Κανονικό',
  'person.size.large': 'Μεγάλο',
  'person.size.larger': 'Μεγαλύτερο',
  'person.size.xlarge': 'Πολύ μεγάλο',
  'person.size.huge': 'Μέγιστο',
  'person.space': 'Αποστάσεις',
  'person.space.tight': 'Ελάχιστες',
  'person.space.dense': 'Στενές',
  'person.space.default': 'Κανονικές',
  'person.space.roomy': 'Άνετες',
  'person.space.wide': 'Πολύ άνετες',
  'person.space.max': 'Μέγιστες',
  'person.note': 'Οι ρυθμίσεις εμφάνισης μένουν μόνο σε αυτό το πρόγραμμα περιήγησης: δεν φτάνουν ούτε στην κατάσταση του ταξιδιού ούτε στον διακομιστή.',
  'currencies.title': 'Νομίσματα',
  'currencies.hint': 'Η ισοτιμία δείχνει πόσα {base} αξίζει μία μονάδα. Το βασικό νόμισμα {base} είναι πάντα 1 και δεν διαγράφεται.',
  'currencies.code': 'Κωδικός',
  'currencies.symbol': 'Σύμβολο',
  'currencies.rate': 'Ισοτιμία προς {base}',
  'currencies.actions': 'Ενέργειες',
  'currencies.symbolOf': 'Σύμβολο για {code}',
  'currencies.rateOf': 'Ισοτιμία για {code}',
  'currencies.add': 'Προσθήκη',
  'currencies.remove.base': 'Το βασικό νόμισμα δεν διαγράφεται',
  'currencies.remove.display': 'Το νόμισμα είναι επιλεγμένο για εμφάνιση',
  'currencies.remove.used': 'Χρησιμοποιείται σε τιμές: {count}',
  'currencies.error.rate': 'Η ισοτιμία πρέπει να είναι αριθμός μεγαλύτερος του 0',
  'currencies.add.currency': 'Νόμισμα',
  'currencies.add.rateLabel': '1 {code} = ? {base}',
  'currencies.add.empty': 'Όλα τα νομίσματα της λίστας έχουν ήδη προστεθεί.',
  'currency.name.USD': 'Δολάριο ΗΠΑ',
  'currency.name.EUR': 'Ευρώ',
  'currency.name.GBP': 'Λίρα Στερλίνα',
  'currency.name.CHF': 'Ελβετικό φράγκο',
  'currency.name.JPY': 'Ιαπωνικό γεν',
  'currency.name.CNY': 'Κινεζικό γιουάν',
  'currency.name.TRY': 'Τουρκική λίρα',
  'currency.name.AED': 'Ντιρχάμ ΗΑΕ',
  'currency.name.GEL': 'Λάρι Γεωργίας',
  'currency.name.AMD': 'Ντραμ Αρμενίας',
  'currency.name.RUB': 'Ρωσικό ρούβλι',
  'currency.name.KZT': 'Τένγκε Καζακστάν',
  'currency.name.PLN': 'Πολωνικό ζλότι',
  'currency.name.CZK': 'Τσεχική κορόνα',
  'currency.name.HUF': 'Ουγγρικό φιορίνι',
  'currency.name.RON': 'Ρουμανικό λέου',
  'currency.name.BGN': 'Βουλγαρικό λεβ',
  'currency.name.RSD': 'Σερβικό δηνάριο',
  'currency.name.SEK': 'Σουηδική κορόνα',
  'currency.name.NOK': 'Νορβηγική κορόνα',
  'currency.name.DKK': 'Δανική κορόνα',
  'currency.name.ILS': 'Νέο σέκελ Ισραήλ',
  'currency.name.THB': 'Ταϊλανδικό μπατ',
  'currency.name.VND': 'Βιετναμικό ντονγκ',
  'currency.name.INR': 'Ινδική ρουπία',
  'currency.name.IDR': 'Ινδονησιακή ρουπία',
  'currency.name.MYR': 'Μαλαισιανό ρίνγκιτ',
  'currency.name.SGD': 'Δολάριο Σιγκαπούρης',
  'currency.name.EGP': 'Αιγυπτιακή λίρα',
  'currency.name.MAD': 'Μαροκινό ντιρχάμ',
  'library.title': 'Βιβλιοθήκη',
  'library.count.one': '{count} μπλοκ',
  'library.count.few': '{count} μπλοκ',
  'library.count.many': '{count} μπλοκ',
  'library.count.other': '{count} μπλοκ',
  'library.add': '+ Μπλοκ',
  'library.search': 'Αναζήτηση στη βιβλιοθήκη',
  'library.search.placeholder': 'Αναζήτηση στη βιβλιοθήκη…',
  'library.empty': 'Η βιβλιοθήκη είναι άδεια',
  'library.empty.hint': 'Πρόσθεσε το πρώτο μπλοκ με το «+ Μπλοκ» ή ανάλυσε έναν σύνδεσμο.',
  'library.nothingFound': 'Δεν βρέθηκε τίποτα',
  'library.converted': 'μετατράπηκε',
  'library.edit': 'Επεξεργασία',
  'library.delete': 'Διαγραφή',
  'library.clone': 'Κλωνοποίηση',
  'library.delete.title': 'Διαγραφή αυτού του μπλοκ;',
  'library.delete.used.one': 'Θα αφαιρεθεί από {count} επιλογή. Η ενέργεια δεν αναιρείται.',
  'library.delete.used.few': 'Θα αφαιρεθεί από {count} επιλογές. Η ενέργεια δεν αναιρείται.',
  'library.delete.used.many': 'Θα αφαιρεθεί από {count} επιλογές. Η ενέργεια δεν αναιρείται.',
  'library.delete.used.other': 'Θα αφαιρεθεί από {count} επιλογές. Η ενέργεια δεν αναιρείται.',
  'library.delete.unused': 'Αυτό το μπλοκ δεν χρησιμοποιείται σε καμία επιλογή. Η ενέργεια δεν αναιρείται.',
  'library.locked': 'Το μπλοκ ανήκει σε κλειδωμένη επιλογή',
  'library.addTo': 'Προσθήκη σε…',
  'library.addTo.pick': 'Διάλεξε επιλογή',
  'library.addTo.none': 'Δημιούργησε πρώτα μια επιλογή',
  'library.addTo.locked': 'Η επιλογή είναι κλειδωμένη',
  'board.title': 'Επιλογές',
  'variant.add': '+ Επιλογή',
  'variant.add.short': '+',
  'variant.add.max': 'Το πολύ {count} επιλογές',
  'variant.empty.title': 'Καμία επιλογή ακόμη',
  'variant.empty.hint': 'Δημιούργησε μια επιλογή για να αρχίσεις να συνθέτεις τη διαδρομή.',
  'variant.name': 'Όνομα επιλογής',
  'variant.rename': 'Μετονομασία',
  'variant.collapse': 'Σύμπτυξη επιλογής',
  'variant.expand': 'Ανάπτυξη επιλογής',
  'variant.order': 'Σειρά επιλογής',
  'variant.moveUp': 'Μετακίνηση επιλογής πάνω',
  'variant.moveDown': 'Μετακίνηση επιλογής κάτω',
  'variant.lock': 'Κλείδωμα της επιλογής από αλλαγές',
  'variant.unlock': 'Η επιλογή είναι κλειδωμένη — πάτησε για να επιτρέψεις αλλαγές',
  'variant.locked': 'Η επιλογή είναι κλειδωμένη',
  'variant.menu': 'Μενού επιλογής',
  'variant.color': 'Χρώμα επιλογής',
  'variant.colorOf': 'Χρώμα {color}',
  'variant.clone': 'Αντιγραφή επιλογής',
  'variant.cloneName': '{name} — αντίγραφο',
  'variant.delete': 'Διαγραφή επιλογής',
  'variant.delete.title': 'Διαγραφή αυτής της επιλογής;',
  'variant.delete.body': 'Η «{name}» θα διαγραφεί. Τα μπλοκ της μένουν στη βιβλιοθήκη. Η ενέργεια δεν αναιρείται.',
  'variant.defaultName': 'Επιλογή {n}',
  'timeline.empty.title': 'Άφησε εδώ το πρώτο τμήμα',
  'timeline.empty.hint': 'Ή χρησιμοποίησε το «Προσθήκη σε…» σε μια κάρτα μπλοκ στη βιβλιοθήκη αριστερά.',
  'timeline.copyHere': 'αντιγραφή στην επιλογή',
  'timeline.remove': 'Αφαίρεση',
  'timeline.moveUp': 'Μετακίνηση πάνω',
  'timeline.moveDown': 'Μετακίνηση κάτω',
  'timeline.lockedNote': 'κλειδωμένο',
  'timeline.lockedNote.tip': 'Η επιλογή είναι κλειδωμένη — άνοιξε το λουκέτο στην κεφαλίδα της',
  'timeline.pairPrice': 'στην τιμή του ζεύγους',
  'timeline.dropToRemove': 'Άφησε εδώ για να βγάλεις το μπλοκ από την επιλογή',
  'timeline.layover': 'ανταπόκριση {duration}',
  'timeline.layover.unknown': 'άγνωστη ανταπόκριση: λείπει η ζώνη ώρας',
  'timeline.layover.transferCounted': 'η μεταφορά μετρήθηκε',
  'timeline.layover.lodgingCounted': 'η διανυκτέρευση μετρήθηκε',
  'timeline.layover.bothCounted': 'μεταφορά και διανυκτέρευση μετρήθηκαν',
  'timeline.stay': '{duration} στον προορισμό — ξεκούραση, ταξίδι ή δουλειές',
  'summary.rides.one': '{count} μεταφορά',
  'summary.rides.few': '{count} μεταφορές',
  'summary.rides.many': '{count} μεταφορές',
  'summary.rides.other': '{count} μεταφορές',
  'summary.nights.one': '{count} διανυκτέρευση',
  'summary.nights.few': '{count} διανυκτερεύσεις',
  'summary.nights.many': '{count} διανυκτερεύσεις',
  'summary.nights.other': '{count} διανυκτερεύσεις',
  'totals.withBaggage': 'με αποσκευές',
  'totals.perTraveler': 'ανά άτομο',
  'totals.flightTime': 'Στον αέρα',
  'totals.doorToDoor': 'Πόρτα σε πόρτα',
  'totals.nights': 'Διανυκτερεύσεις',
  'totals.breakdown': 'Ανάλυση ανά τύπο',
  'totals.converted': 'μετατράπηκε',
  'totals.warnings.one': '{count} προειδοποίηση',
  'totals.warnings.few': '{count} προειδοποιήσεις',
  'totals.warnings.many': '{count} προειδοποιήσεις',
  'totals.warnings.other': '{count} προειδοποιήσεις',
  'labels.hour': 'ώ',
  'labels.minute': 'λ',
  'labels.perNight': '/νύχτα',
  'field.fromIata': 'Από (IATA)',
  'field.toIata': 'Προς (IATA)',
  'field.departLocal': 'Αναχώρηση (τοπική ώρα)',
  'field.arriveLocal': 'Άφιξη (τοπική ώρα)',
  'field.priceScope': 'Τύπος τιμής',
  'field.linked': 'Συνδεδεμένο σκέλος μετ’ επιστροφής',
  'form.saveChanges': 'Αποθήκευση',
  'form.create': 'Δημιουργία',
  'form.none': '— κανένα —',
  'form.pickFlight': '— διάλεξε πτήση —',
  'baggage.noFlights': 'Δεν υπάρχουν ακόμη πτήσεις στη βιβλιοθήκη — πρόσθεσε πρώτα μία.',
  'compare.best': '{value}, καλύτερη τιμή',
  'compare.title': 'Σύγκριση επιλογών',
  'compare.count.one': '{count} επιλογή',
  'compare.count.few': '{count} επιλογές',
  'compare.count.many': '{count} επιλογές',
  'compare.count.other': '{count} επιλογές',
  'compare.metric': 'Δείκτης',
  'compare.total': 'Σύνολο',
  'compare.totalWithBaggage': 'Σύνολο με αποσκευές',
  'compare.flightTime': 'Καθαρός χρόνος πτήσης',
  'compare.doorToDoor': 'Χρόνος πόρτα σε πόρτα',
  'compare.nights': 'Διανυκτερεύσεις',
  'compare.warnings': 'Προειδοποιήσεις',
  'compare.empty': 'Πρόσθεσε τουλάχιστον μία επιλογή για να δεις τη σύγκριση.',
  'form.close': 'Κλείσιμο',
  'form.cancel': 'Άκυρο',
  'form.save': 'Αποθήκευση',
  'form.confirm': 'Επιβεβαίωση',
  'form.optional': 'προαιρετικό',
  'form.new': 'Νέο μπλοκ',
  'form.edit': 'Επεξεργασία',
  'form.notFound': 'Το μπλοκ δεν βρέθηκε',
  'form.gone': 'Αυτό το μπλοκ δεν υπάρχει πια στη βιβλιοθήκη.',
  'form.decrease': 'Μείωση',
  'form.increase': 'Αύξηση',
  'kind.flight': 'Πτήση',
  'kind.transfer': 'Μεταφορά',
  'kind.lodging': 'Διαμονή',
  'kind.taxi': 'Ταξί',
  'kind.food': 'Φαγητό',
  'kind.baggage': 'Αποσκευές',
  'kind.misc': 'Άλλο',
  'kind.flight.desc': 'Σκέλος μεταξύ αεροδρομίων',
  'kind.transfer.desc': 'Λεωφορείο, τρένο ή πούλμαν μεταξύ πόλεων',
  'kind.lodging.desc': 'Ξενοδοχείο ή διαμέρισμα, με χρέωση ανά νύχτα',
  'kind.taxi.desc': 'Διαδρομή μέσα στην πόλη',
  'kind.food.desc': 'Γεύματα, με χρέωση ανά ημέρα',
  'kind.baggage.desc': 'Πληρωμένες αποσκευές για συγκεκριμένη πτήση',
  'kind.misc.desc': 'Οποιοδήποτε άλλο έξοδο',
  'scope.one_way': 'Τιμή απλής μετάβασης',
  'scope.round_trip_half': 'Μισή τιμή μετ’ επιστροφής',
  'scope.round_trip_full': 'Πλήρης τιμή μετ’ επιστροφής',
  'scope.pairNote': 'Η τιμή μετράει μία φορά — το συνδεδεμένο σκέλος βγαίνει 0.',
  'field.from': 'Από',
  'field.to': 'Προς',
  'field.city': 'Πόλη',
  'field.label': 'Όνομα',
  'field.carrier': 'Αεροπορική',
  'field.url': 'Σύνδεσμος πτήσης ή εισιτηρίου',
  'field.url.hint': 'Πού να επιστρέψεις για λεπτομέρειες: η σελίδα της πτήσης, μια αναζήτηση ή το εισιτήριο που αγόρασες.',
  'field.url.short': 'σύνδεσμος',
  'field.flightNumber': 'Αριθμός πτήσης',
  'field.depart': 'Αναχώρηση',
  'field.arrive': 'Άφιξη',
  'field.duration': 'Διάρκεια, λεπτά',
  'field.nights': 'Διανυκτερεύσεις',
  'field.days': 'Ημέρες',
  'field.quantity': 'Ποσότητα',
  'field.price': 'Τιμή',
  'field.pricePerNight': 'Τιμή ανά νύχτα',
  'field.perPerson': 'Τιμή ανά άτομο',
  'field.wholeStay': 'Για όλο το διάστημα',
  'field.wholeStay.hint': 'Οι νύχτες υπολογίζονται αυτόματα: από την άφιξη της προηγούμενης πτήσης ως την αναχώρηση της επόμενης (έως 365).',
  'field.wholeStay.need': 'Διαθέσιμο όταν ακολουθεί άλλο μπλοκ στην εναλλακτική.',
  'labels.wholeStay': 'όλο το διάστημα',
  'field.flight': 'Πτήση',
  'field.note': 'Σημείωση',
  'field.tzFrom': 'Ζώνη ώρας αναχώρησης',
  'field.tzTo': 'Ζώνη ώρας άφιξης',
  'error.iata': 'Ο κωδικός αεροδρομίου είναι ακριβώς 3 λατινικά γράμματα',
  'error.departRequired': 'Συμπλήρωσε την ώρα αναχώρησης',
  'error.arriveRequired': 'Συμπλήρωσε την ώρα άφιξης',
  'error.departDateRequired': 'Συμπλήρωσε ημερομηνία και ώρα αναχώρησης',
  'error.arriveDateRequired': 'Συμπλήρωσε ημερομηνία και ώρα άφιξης',
  'error.arriveBeforeDepart': 'Η άφιξη πρέπει να είναι μετά την αναχώρηση',
  'error.priceNegative': 'Η τιμή πρέπει να είναι αριθμός ≥ 0',
  'error.fromRequired': 'Συμπλήρωσε την αφετηρία',
  'error.toRequired': 'Συμπλήρωσε τον προορισμό',
  'error.durationNegative': 'Η διάρκεια πρέπει να είναι αριθμός ≥ 0',
  'error.cityRequired': 'Συμπλήρωσε την πόλη',
  'error.nightsMin': 'Τουλάχιστον 1 νύχτα',
  'error.labelRequired': 'Συμπλήρωσε ένα όνομα',
  'error.quantityMin': 'Τουλάχιστον 1',
  'error.flightRequired': 'Διάλεξε πτήση',
  'warn.tzMissing': 'Χωρίς ζώνη ώρας — η διάρκεια δεν θα είναι ακριβής',
  'warn.tzMissingParse': 'Δεν εντοπίστηκε ζώνη ώρας — ο χρόνος ταξιδιού δεν θα είναι ακριβής.',
  'parse.missing.from': 'αεροδρόμιο αναχώρησης',
  'parse.missing.to': 'αεροδρόμιο άφιξης',
  'parse.missing.fromTz': 'ζώνη ώρας αναχώρησης',
  'parse.missing.toTz': 'ζώνη ώρας άφιξης',
  'parse.missing.departLocal': 'ημερομηνία αναχώρησης',
  'parse.missing.departTime': 'ώρα αναχώρησης',
  'parse.missing.arriveLocal': 'ημερομηνία άφιξης',
  'parse.missing.arriveTime': 'ώρα άφιξης',
  'parse.missing.carrier': 'αεροπορική',
  'parse.missing.price': 'τιμή',
  'library.empty.text': 'Υπάρχουν δύο τρόποι να προσθέσεις μπλοκ: να το φτιάξεις με το χέρι ή να επικολλήσεις ό,τι αντέγραψες από σελίδα αναζήτησης πτήσεων ή ξενοδοχείων.',
  'library.noResults': 'Καμία αντιστοιχία για «{query}»',
  'library.resetSearch': 'Καθαρισμός αναζήτησης',
  'library.group.flight': 'Πτήσεις',
  'library.group.transfer': 'Μεταφορές',
  'library.group.lodging': 'Διαμονή',
  'library.group.taxi': 'Ταξί',
  'library.group.food': 'Φαγητό',
  'library.group.baggage': 'Αποσκευές',
  'library.group.misc': 'Άλλα',
  'compare.hint': 'Η σύγκριση ξεκινά από δύο επιλογές — πρόσθεσε άλλη μία για να δεις τις καλύτερες τιμές τονισμένες.',
  'compare.caption': 'Σύγκριση επιλογών ταξιδιού: οι γραμμές είναι δείκτες, οι στήλες επιλογές',
  'parse.flightN': 'Πτήση {n}',
  'parse.notRecognised': 'Δεν αναγνωρίστηκαν: {fields}. Έλεγξέ τα και συμπλήρωσέ τα με το χέρι.',
  'parse.back': 'Πίσω',
  'parse.addSelected': 'Προσθήκη επιλεγμένων ({count})',
  'sync.boot': 'φόρτωση…',
  'sync.local': 'τοπικά',
  'sync.saving': 'αποθήκευση…',
  'sync.synced': 'αποθηκεύτηκε',
  'sync.conflict': 'έκδοση διακομιστή',
  'sync.rejected': 'δεν αποθηκεύτηκε',
  'sync.boot.tip': 'Ανάγνωση κατάστασης από τον διακομιστή',
  'sync.local.tip': 'Δουλεύουμε τοπικά',
  'sync.saving.tip': 'Αποστολή αλλαγών στον διακομιστή',
  'sync.synced.tip': 'Η κατάσταση αποθηκεύτηκε στη βάση του διακομιστή',
  'sync.conflict.tip': 'Η κατάσταση άλλαξε στον διακομιστή· εφαρμόστηκε η δική του έκδοση',
  'sync.rejected.tip': 'Ο διακομιστής απέρριψε την κατάσταση',
  'toolbar.snapshots': 'Στιγμιότυπα και ιστορικό',
  'sync.snapshot': 'Στιγμιότυπο',
  'sync.snapshot.tip': 'Αποθήκευση στιγμιότυπου: η τρέχουσα κατάσταση μπαίνει στη βάση του διακομιστή',
  'sync.history.tip': 'Ιστορικό στιγμιότυπων: επιστροφή σε κατάσταση που αποθήκευσες νωρίτερα',
  'sync.history': 'Ιστορικό',
  'sync.snapshots': 'Στιγμιότυπα στον διακομιστή',
  'sync.snapshot.saved': 'Το στιγμιότυπο αποθηκεύτηκε στον διακομιστή',
  'sync.snapshot.failed': 'Δεν ήταν δυνατή η αποθήκευση του στιγμιότυπου',
  'sync.snapshot.notFound': 'Το στιγμιότυπο δεν βρέθηκε',
  'sync.snapshot.restored': 'Η κατάσταση επαναφέρθηκε από το στιγμιότυπο',
  'sync.snapshot.empty': 'Κανένα στιγμιότυπο ακόμη. Το «Στιγμιότυπο» αποθηκεύει την τρέχουσα κατάσταση για να επιστρέψεις σε αυτήν.',
  'sync.snapshot.unnamed': 'χωρίς τίτλο',
  'sync.snapshot.label': 'Στιγμιότυπο {stamp}',
  'calcwarn.need_transfer': 'χρειάζεται μεταφορά {from} → {to}',
  'calcwarn.risky_connection': 'στενή ανταπόκριση {duration}',
  'calcwarn.negative_connection': 'αδύνατο: αναχώρηση πριν την άφιξη',
  'calcwarn.long_layover': 'μεγάλη αναμονή — να προστεθεί διανυκτέρευση;',
  'calcwarn.orphan_baggage': 'αποσκευές χωρίς την πτήση τους σε αυτή την επιλογή',
  'calcwarn.rt_full_unpaired': 'η τιμή καλύπτει το μετ’ επιστροφής',
  'calcwarn.missing_tz': 'άγνωστη ζώνη ώρας — η διάρκεια δεν υπολογίστηκε',

  'auth.signin': 'Σύνδεση',
  'auth.signup': 'Εγγραφή',
  'auth.signout': 'Αποσύνδεση',
  'auth.email': 'Email',
  'auth.password': 'Κωδικός',
  'auth.password.new': 'Νέος κωδικός',
  'auth.password.current': 'Τρέχων κωδικός',
  'auth.password.change': 'Αλλαγή κωδικού',
  'auth.password.changed': 'Ο κωδικός άλλαξε.',
  'auth.name': 'Όνομα',
  'auth.submit.signup': 'Δημιουργία λογαριασμού',
  'auth.forgot.link': 'Ξεχάσατε τον κωδικό;',
  'auth.forgot.title': 'Ανάκτηση κωδικού',
  'auth.forgot.hint': 'Θα στείλουμε σε αυτή τη διεύθυνση σύνδεσμο για νέο κωδικό.',
  'auth.forgot.submit': 'Αποστολή συνδέσμου',
  'auth.forgot.sent': 'Αν υπάρχει λογαριασμός με αυτό το email, ο σύνδεσμος έχει ήδη σταλεί. Ισχύει για μία ώρα.',
  'auth.forgot.back': 'Πίσω στη σύνδεση',
  'auth.reset.title': 'Νέος κωδικός',
  'auth.reset.submit': 'Αποθήκευση κωδικού',
  'auth.reset.done': 'Ο κωδικός άλλαξε. Όλες οι προηγούμενες συνεδρίες έκλεισαν — συνδεθείτε ξανά.',
  'auth.verify.title': 'Επιβεβαίωση email',
  'auth.verify.banner': 'Επιβεβαιώστε το email σας — χωρίς αυτό δεν ανακτάται η πρόσβαση στον λογαριασμό.',
  'auth.verify.resend': 'Νέα αποστολή email',
  'auth.verify.sent': 'Το email στάλθηκε — ελέγξτε τα εισερχόμενα.',
  'auth.verify.ok': 'Το email επιβεβαιώθηκε.',
  'auth.verify.fail': 'Ο σύνδεσμος δεν ισχύει ή έχει λήξει.',
  'auth.guest.hint': 'Χωρίς σύνδεση, τα ταξίδια αποθηκεύονται μόνο σε αυτό το πρόγραμμα περιήγησης.',
  'auth.subtitle': 'Με λογαριασμό τα ταξίδια σας δεν μένουν σε έναν browser — ανοίγουν από κάθε συσκευή.',
  'auth.tabs.label': 'Σύνδεση ή εγγραφή',
  'auth.password.show': 'Εμφάνιση κωδικού',
  'auth.password.hide': 'Απόκρυψη κωδικού',
  'auth.capslock': 'Το Caps Lock είναι ενεργό',
  'auth.busy': 'Μια στιγμή…',
  'auth.toApp': 'Στον σχεδιαστή',
  'auth.pw.rule': 'Τουλάχιστον 10 χαρακτήρες.',
  'auth.pw.short': 'Πολύ σύντομος: τουλάχιστον 10 χαρακτήρες',
  'auth.pw.long': 'Πολύ μεγάλος',
  'auth.pw.common': 'Πολύ κοινός — αυτόν δοκιμάζουν πρώτο',
  'auth.pw.same_as_email': 'Ο κωδικός δεν πρέπει να επαναλαμβάνει το email',
  'auth.err.invalid_credentials': 'Λάθος email ή κωδικός.',
  'auth.err.invalid_email': 'Ελέγξτε τη διεύθυνση email.',
  'auth.err.weak_password': 'Αυτός ο κωδικός δεν κάνει.',
  'auth.err.email_taken': 'Υπάρχει ήδη λογαριασμός με αυτό το email.',
  'auth.err.blocked': 'Ο λογαριασμός είναι αποκλεισμένος.',
  'auth.err.rate_limited': 'Πάρα πολλές προσπάθειες. Δοκιμάστε αργότερα.',
  'auth.err.bad_token': 'Ο σύνδεσμος δεν ισχύει ή έχει λήξει.',
  'auth.err.unauthorized': 'Χρειάζεται σύνδεση.',
  'auth.err.forbidden': 'Ανεπαρκή δικαιώματα.',
  'auth.err.self_change': 'Δεν μπορείτε να αλλάξετε τον δικό σας ρόλο ή κατάσταση.',
  'auth.err.origin': 'Το αίτημα απορρίφθηκε για λόγους ασφαλείας.',
  'auth.err.network': 'Ο διακομιστής δεν απαντά.',
  'auth.err.unknown': 'Κάτι πήγε στραβά.',
  'auth.err.restorable': 'Υπήρχε ήδη λογαριασμός με αυτό το email — μπορείτε να τον επαναφέρετε.',
  'auth.restore.title': 'Επαναφορά λογαριασμού',
  'auth.restore.hint': 'Υπήρχε ήδη λογαριασμός με αυτό το email — εισαγάγετε τον παλιό κωδικό πρόσβασης για να τον επαναφέρετε μαζί με τα ταξίδια του.',
  'auth.restore.submit': 'Επαναφορά',
  'auth.restore.back': 'Πίσω στην εγγραφή',

  'account.title': 'Λογαριασμός',
  'account.section.account': 'Λογαριασμός',
  'account.appearance': 'Εμφάνιση',
  'account.guest.title': 'Δεν έχετε συνδεθεί',
  'account.emailState': 'Κατάσταση email',
  'account.verified': 'επιβεβαιωμένο',
  'account.unverified': 'μη επιβεβαιωμένο',
  'account.save': 'Αποθήκευση',
  'account.saved': 'Αποθηκεύτηκε',
  'account.admin': 'Διαχείριση',
  'account.menu.admin.hint': 'Χρήστες και ρυθμίσεις αναγνώρισης',
  'account.password.warning': 'Η αλλαγή κωδικού κλείνει όλες τις άλλες συνεδρίες.',
  'account.menu.language': 'Γλώσσα',
  'account.menu.language.hint': 'Γλώσσα διεπαφής',
  'account.menu.trips.hint': 'Όλα τα ταξίδια σας',
  'account.menu.trips.hint.guest': 'Τα ταξίδια είναι διαθέσιμα μετά τη σύνδεση',
  'account.menu.pricing': 'Τιμές',
  'account.menu.pricing.hint': 'Πλάνα, τιμές και πρόσθετες επιλογές',
  'account.menu.settings': 'Ρυθμίσεις',
  'account.menu.settings.hint': 'Κωδικός, email, διαγραφή λογαριασμού',
  'account.menu.person.hint': 'Παλέτα, μέγεθος κειμένου και αποστάσεις — προσαρμόστε τη διεπαφή στα μέτρα σας',
  'account.menu.signout.hint': 'Τερματισμός της συνεδρίας σε αυτή τη συσκευή',
  'account.delete.title': 'Διαγραφή λογαριασμού',
  'account.delete.hint': 'Ο λογαριασμός και όλα τα ταξίδια του θα γίνουν απρόσιτα αμέσως μετά τη διαγραφή.',
  'account.delete.action': 'Διαγραφή λογαριασμού',
  'account.delete.confirm': 'Ο λογαριασμός και όλα τα ταξίδια του θα πάψουν να είναι διαθέσιμα. Θα μπορέσετε να τα ανακτήσετε μόνο εγγραφόμενοι ξανά με το ίδιο email.',


  'admin.title': 'Χρήστες',
  'admin.back': 'Στον σχεδιαστή',
  'admin.search': 'Αναζήτηση με email ή όνομα',
  'admin.col.user': 'Χρήστης',
  'admin.col.role': 'Ρόλος',
  'admin.col.status': 'Κατάσταση',
  'admin.col.created': 'Εγγραφή',
  'admin.col.lastLogin': 'Τελευταία σύνδεση',
  'admin.col.data': 'Δεδομένα',
  'admin.col.actions': 'Ενέργειες',
  'admin.role.user': 'Χρήστης',
  'admin.role.admin': 'Διαχειριστής',
  'admin.status.active': 'Ενεργός',
  'admin.status.blocked': 'Αποκλεισμένος',
  'admin.status.deleted': 'Διαγράφηκε',
  'admin.action.block': 'Αποκλεισμός',
  'admin.action.unblock': 'Άρση αποκλεισμού',
  'admin.action.promote': 'Δικαιώματα διαχειριστή',
  'admin.action.demote': 'Αφαίρεση δικαιωμάτων',
  'admin.action.delete': 'Διαγραφή',
  'admin.action.view': 'Δεδομένα',
  'admin.delete.confirm': 'Διαγραφή του {email} μαζί με όλα τα ταξίδια και τα στιγμιότυπα; Δεν αναιρείται.',
  'admin.empty': 'Δεν βρέθηκε κανείς',
  'admin.prev': 'Προηγούμενα',
  'admin.next': 'Επόμενα',
  'admin.page': '{from}–{to} από {total}',
  'admin.forbidden': 'Απαιτούνται δικαιώματα διαχειριστή.',
  'admin.stats.users': 'Χρήστες',
  'admin.stats.newWeek': 'Νέοι αυτή την εβδομάδα',
  'admin.stats.activeWeek': 'Ενεργοί αυτή την εβδομάδα',
  'admin.stats.verified': 'Επιβεβαιωμένο email',
  'admin.stats.blocked': 'Αποκλεισμένοι',
  'admin.stats.snapshots': 'Στιγμιότυπα',
  'admin.stats.size': 'Όγκος δεδομένων',
  'admin.view.title': 'Δεδομένα: {email}',
  'admin.view.readonly': 'Δεδομένα άλλου χρήστη, μόνο για ανάγνωση. Η προβολή καταγράφεται.',
  'admin.view.empty': 'Ο χρήστης δεν έχει αποθηκεύσει τίποτα ακόμη.',
  'admin.view.variants': 'Επιλογές',
  'admin.view.segments': 'Μπλοκ',
  'admin.view.showRaw': 'Εμφάνιση JSON',
  'admin.view.hideRaw': 'Απόκρυψη JSON',

  'sync.snapshot.beforeLogin': 'Πριν τη σύνδεση',
  'auth.guest.locked': 'Διαθέσιμο μετά τη σύνδεση',
  'variant.add.guest': 'Χωρίς σύνδεση διατίθενται {count} επιλογές. Συνδεθείτε για περισσότερες.',

  'share.title': 'Κοινή χρήση ταξιδιού',
  // ── Ταξίδια ─────────────────────────────────────────────────────────────
  'trips.title': 'Τα ταξίδια μου',
  'trips.all': 'Τα ταξίδια μου ({count})',
  'trips.none': 'δεν έχει δημιουργηθεί ακόμη',
  'trips.untitled': 'Χωρίς όνομα',
  'trips.newName': 'Νέο ταξίδι',
  'trips.create': '+ Νέο ταξίδι',
  'trips.open': 'ανοιχτό',
  'trips.openAction': 'Άνοιγμα',
  'trips.rename': 'Μετονομασία',
  'trips.rename.ownerOnly': 'Μόνο ο ιδιοκτήτης μετονομάζει το ταξίδι',
  'trips.name': 'Όνομα ταξιδιού',
  'trips.copyLink': 'Αντιγραφή συνδέσμου',
  'trips.shared': 'έχει δημόσιο σύνδεσμο',
  'trips.linkClosed': 'χωρίς δημόσιο σύνδεσμο',
  'trips.info.button': 'Λεπτομέρειες ταξιδιού «{name}»',
  'trips.info.title': 'Σχετικά με «{name}»',
  'trips.updated': 'επεξεργάστηκε {date}',
  'trips.created': 'δημιουργήθηκε {date}',
  'trips.delete': 'Διαγραφή',
  'trips.delete.title': 'Διαγραφή ταξιδιού;',
  'trips.delete.text': 'Το ταξίδι «{name}» και το ιστορικό του θα διαγραφούν οριστικά. Ο δημόσιος σύνδεσμός του θα πάψει να λειτουργεί.',
  'trips.empty': 'Δεν υπάρχουν ακόμη ταξίδια. Δημιουργήστε το πρώτο και η δουλειά σας θα αποθηκεύεται στον διακομιστή.',
  'trips.guest.title': 'Απαιτείται λογαριασμός',
  'trips.guest': 'Τα πολλαπλά ταξίδια απαιτούν λογαριασμό: η εργασία επισκέπτη μένει σε αυτό το πρόγραμμα περιήγησης ως ένα ταξίδι.',
  'trips.back': 'Πίσω στον σχεδιαστή',
  'trips.section.mine': 'Τα ταξίδια μου',
  'trips.section.shared': 'Κοινόχρηστα μαζί μου',
  'trips.section.shared.hint': 'Εδώ σας κάλεσαν ως συν-συντάκτη: το περιεχόμενο μπορείτε να το αλλάξετε, το ίδιο το ταξίδι όχι.',
  'trips.byOwner': 'κάτοχος: {name}',
  'trips.members.one': '{count} συν-συντάκτης',
  'trips.members.few': '{count} συν-συντάκτες',
  'trips.members.many': '{count} συν-συντάκτες',
  'trips.members.other': '{count} συν-συντάκτες',
  'trips.leave': 'Αποχώρηση',
  'trips.leave.title': 'Αποχώρηση από το ταξίδι;',
  'trips.leave.text': 'Το ταξίδι «{name}» θα φύγει από τη λίστα σας. Ο κάτοχος το κρατά και μπορεί να σας καλέσει ξανά.',

  // ── Κοινή πρόσβαση ──────────────────────────────────────────────────────
  'trips.access': 'Πρόσβαση',
  'trips.access.title': 'Πρόσβαση στο ταξίδι «{name}»',
  'trips.access.lead': 'Οι συν-συντάκτες δουλεύουν στο ίδιο ακριβώς ταξίδι: βιβλιοθήκη, εκδοχές, τιμές. Μόνο ο κάτοχος το μετονομάζει, δημοσιεύει σύνδεσμο και καλεί άλλους.',
  'trips.access.invite': 'Πρόσκληση με email',
  'trips.access.add': 'Πρόσκληση',
  'trips.access.hint': 'Με λογαριασμό, το ταξίδι εμφανίζεται αμέσως στη λίστα του. Χωρίς, λαμβάνει πρόσκληση με email και πρόσβαση μετά την εγγραφή.',
  'trips.access.pending': 'Δεν υπάρχει ακόμη λογαριασμός με {email}: στείλαμε πρόσκληση — η πρόσβαση θα εμφανιστεί αμέσως μετά την εγγραφή.',
  'trips.access.empty': 'Προς το παρόν κανείς εκτός από τον κάτοχο.',
  'trips.access.you': 'εσείς',
  'trips.access.remove': 'Αφαίρεση',
  'trips.access.role.owner': 'κάτοχος',
  'trips.access.role.editor': 'συν-συντάκτης',
  'trips.access.err.no_user': 'Δεν υπάρχει ακόμη κανείς με αυτό το email. Ζητήστε του να εγγραφεί και καλέστε τον ξανά.',
  'trips.access.err.already_member': 'Βρίσκεται ήδη στη λίστα.',
  'trips.access.err.is_owner': 'Αυτός είναι ο κάτοχος του ταξιδιού.',
  'trips.access.err.too_many': 'Το ταξίδι έχει ήδη πάρα πολλούς συν-συντάκτες.',
  'trips.access.err.invalid_email': 'Ελέγξτε τη διεύθυνση email.',
  'trips.access.err.blocked': 'Ο λογαριασμός είναι αποκλεισμένος.',
  'trips.access.err.unknown': 'Δεν έγινε. Δοκιμάστε ξανά.',

  // ── Πρότυπα και συχνά μπλοκ ─────────────────────────────────────────────
  'tpl.start': 'Ξεκινήστε από πρότυπο',
  'tpl.start.hint': 'Ένα τυπικό ταξίδι με ένα κλικ: τα μπλοκ είναι έτοιμα, οι τιμές κενές.',
  'tpl.create': 'Δημιουργία ταξιδιού',
  'tpl.fill': 'Σύνθεση επιλογής',
  'tpl.page.note': 'Το πρότυπο δημιουργεί ξεχωριστό νέο ταξίδι. Τα υπάρχοντα ταξίδια δεν αλλάζουν.',
  'tpl.board.note': 'Το πρότυπο προσθέτει μια επιλογή με αυτά τα μπλοκ. Ό,τι έχετε ήδη φτιάξει μένει στη θέση του.',
  'tpl.blocks': 'Συχνά μπλοκ',
  'tpl.blocks.hint': 'Ένα πάτημα βάζει το μπλοκ στη βιβλιοθήκη με μηδενική τιμή — μένει να γράψετε το ποσό.',
  'tpl.added': 'Το μπλοκ «{name}» προστέθηκε στη βιβλιοθήκη',
  'tpl.weekend.name': 'Διήμερο σαββατοκύριακο',
  'tpl.weekend.desc': 'Δύο διανυκτερεύσεις σε πόλη: διαδρομή από το αεροδρόμιο, διαμονή, φαγητό και ταξί.',
  'tpl.beach.name': 'Μια εβδομάδα στη θάλασσα',
  'tpl.beach.desc': 'Επτά διανυκτερεύσεις στο ίδιο μέρος: μεταφορά στο ξενοδοχείο, φαγητό, εκδρομές.',
  'tpl.business.name': 'Επαγγελματικό ταξίδι',
  'tpl.business.desc': 'Τρεις διανυκτερεύσεις για δουλειά: διαμονή, ταξί, φαγητό και σύνδεση.',
  'tpl.twoCities.name': 'Δύο πόλεις σε ένα ταξίδι',
  'tpl.twoCities.desc': 'Τρεις διανυκτερεύσεις σε κάθε μία και η μετακίνηση ανάμεσά τους.',
  'tpl.mountains.name': 'Βουνό και σκι',
  'tpl.mountains.desc': 'Πέντε διανυκτερεύσεις δίπλα στην πίστα: κάρτα αναβατήρα, ενοικίαση εξοπλισμού, φαγητό.',
  'tpl.place.airport': 'Αεροδρόμιο',
  'tpl.place.city': 'Πόλη',
  'tpl.place.hotel': 'Ξενοδοχείο',
  'tpl.place.city1': 'Πρώτη πόλη',
  'tpl.place.city2': 'Δεύτερη πόλη',
  'tpl.block.taxi': 'Ταξί στην πόλη',
  'tpl.block.food': 'Φαγητό',
  'tpl.block.insurance': 'Ασφάλεια',
  'tpl.block.esim': 'Δεδομένα κινητού',
  'tpl.block.tickets': 'Εκδρομές και εισιτήρια',
  'tpl.block.visa': 'Βίζα και τέλη',
  'tpl.block.skipass': 'Κάρτα αναβατήρα',
  'tpl.block.gear': 'Ενοικίαση εξοπλισμού',
  'share.tip': 'Σύνδεσμος για όσους δεν έχουν λογαριασμό',
  'share.hint': 'Ο σύνδεσμος δείχνει αντίγραφο του ταξιδιού όπως ήταν κατά τη δημοσίευση — μόνο ανάγνωση. Οι επόμενες αλλαγές δεν φαίνονται μέχρι να πατήσετε «Ενημέρωση».',
  'share.create': 'Δημιουργία συνδέσμου',
  'share.update': 'Ενημέρωση',
  'share.revoke': 'Απενεργοποίηση συνδέσμου',
  'share.copy': 'Αντιγραφή',
  'share.copied': 'Αντιγράφηκε ✓',
  'share.link': 'Σύνδεσμος ταξιδιού',
  'share.look': 'Ο σύνδεσμος ανοίγει με το δικό σου θέμα, μέγεθος και γλώσσα — ο αναγνώστης βλέπει το ταξίδι όπως ακριβώς το έφτιαξες.',
  'share.updated': 'Ενημερώθηκε: {date}',
  'share.views': 'προβολές: {count}',
  'share.failed': 'Δεν ήταν δυνατή η δημιουργία συνδέσμου.',
  'share.readonly': 'Μόνο ανάγνωση',
  'share.travelers': 'Ταξιδιώτες: {count}',
  'share.cheapest': 'Το φθηνότερο',
  'share.segments': 'Από τι προκύπτει η τιμή',
  'share.noPrice': 'Δεν έχουν μπει ακόμη τιμές',
  'share.notFound': 'Ο σύνδεσμος δεν βρέθηκε ή απενεργοποιήθηκε.',
  'share.empty': 'Αυτό το ταξίδι δεν έχει ακόμη επιλογές.',
  'share.makeYours': 'Φτιάξτε το δικό σας ταξίδι',
  'share.preview.variants.one': '{count} επιλογή',
  'share.preview.variants.few': '{count} επιλογές',
  'share.preview.variants.many': '{count} επιλογές',
  'share.preview.variants.other': '{count} επιλογές',
  'share.preview.from': 'από {price}',
  'share.preview.tail': 'Σύγκριση τιμής και χρόνου στο Plan Your Trip.',
  'share.preview.empty': 'Το ταξίδι ετοιμάζεται: δεν υπάρχουν ακόμη επιλογές.',

  'library.new': 'new',
  'library.new.title': 'Προστέθηκε πρόσφατα',
  'library.new.count': 'Νέα: {count}',
  'library.new.dismiss': 'Σήμανση ως ιδωμένα',
  'library.fromSource': 'Από πηγή',
  'library.fromSource.desc': 'Αναγνώρισε μια πτήση από κείμενο, σύνδεσμο ή στιγμιότυπο οθόνης.',

  'recog.title': 'Προσθήκη από πηγή',
  'recog.tab.text': 'Περιγραφή',
  'recog.tab.url': 'Σύνδεσμος',
  'recog.tab.screenshot': 'Στιγμιότυπο',
  'recog.text.label': 'Αποτελέσματα αναζήτησης ή σύντομη περιγραφή πτήσης',
  'recog.text.placeholder': '27 Δεκεμβρίου, Wizz Air Λάρνακα 11:55 → Ερεβάν 16:00, 149 $',
  'recog.url.label': 'Σύνδεσμος πτήσης ή κράτησης',
  'recog.url.placeholder': 'https://…',
  'recog.url.invalid': 'Χρειάζεται σύνδεσμος http:// ή https://',
  'recog.shot.label': 'Στιγμιότυπα αναζήτησης, email ή εισιτηρίου',
  'recog.shot.drop': 'Σύρε εικόνες, επικόλλησέ τες (Ctrl+V) ή διάλεξε αρχεία',
  'recog.shot.pick': 'Επιλογή αρχείων',
  'recog.shot.add': 'Πρόσθεσε κι άλλες',
  'recog.shot.remove': 'Αφαίρεση όλων',
  'recog.shot.removeOne': 'Αφαίρεση στιγμιότυπου {num}',
  'recog.shot.tooBig': 'Η εικόνα ξεπερνά τα {limit} MB',
  'recog.shot.tooMany': 'Το πολύ {limit} εικόνες κάθε φορά — δεν προστέθηκαν: {count}',
  'recog.shot.tooBigTotal': 'Όλες μαζί οι εικόνες δεν πρέπει να ξεπερνούν τα {limit} MB — δεν προστέθηκαν: {count}',
  'recog.shot.badType': 'Δεν μοιάζει με αναγνώσιμη εικόνα: κάνουν PNG, JPEG, WebP ή HEIC',
  'recog.shot.ready': 'Εικόνες: {count} από {limit} · {size}',
  'recog.shot.multi': 'Πολλές εικόνες φεύγουν με ένα αίτημα — οι πτήσεις απ’ όλες μπαίνουν σε μία λίστα.',
  'recog.run': 'Αναγνώριση',
  'recog.running': 'Γίνεται αναγνώριση…',
  'recog.review': 'Έλεγξε και διόρθωσε πριν την προσθήκη',
  'recog.model': 'Μοντέλο: {model}',
  'recog.local': 'Αναλύθηκε τοπικά, χωρίς AI',
  'recog.fallback': 'Το AI δεν απάντησε — εμφανίζεται το αποτέλεσμα της τοπικής ανάλυσης.',
  'recog.privacy.ai': 'Το κείμενο, ο σύνδεσμος ή η εικόνα φεύγουν στο επιλεγμένο μοντέλο AI. Τίποτα δεν αποθηκεύεται από μόνο του: όλα ελέγχονται και διορθώνονται πριν την προσθήκη.',
  'recog.privacy.local': 'Καμία δικτυακή κλήση: αναλύεται μόνο το κείμενο που επικόλλησες. Όλα ελέγχονται και διορθώνονται πριν την προσθήκη.',
  'recog.off': 'Ο διαχειριστής απενεργοποίησε την αναγνώριση με AI — μένει η τοπική ανάλυση κειμένου.',
  'recog.off.guest': 'Η αναγνώριση με AI γίνεται διαθέσιμη μετά τη σύνδεση.',
  'recog.nothing': 'Δεν βρέθηκαν πτήσεις — δοκίμασε άλλο κείμενο, σύνδεσμο ή στιγμιότυπο.',
  'recog.notes': 'Πώς αναλύθηκε',
  'recog.err.unauthorized': 'Χρειάζεται σύνδεση.',
  'recog.err.disabled': 'Ο διαχειριστής απενεργοποίησε την αναγνώριση.',
  'recog.err.no_model': 'Δεν έχει επιλεγεί μοντέλο για αυτή την πηγή — όρισέ το στη διαχείριση.',
  'recog.err.no_key': 'Λείπει το κλειδί του παρόχου AI — όρισέ το στη διαχείριση.',
  'recog.err.provider': 'Ο πάροχος επέστρεψε σφάλμα: {message}',
  'recog.err.timeout': 'Το μοντέλο δεν απάντησε εγκαίρως. Δοκίμασε ξανά.',
  'recog.err.rate_limited': 'Πάρα πολλά αιτήματα. Δοκίμασε αργότερα.',
  'recog.err.too_large': 'Το αίτημα είναι πολύ μεγάλο.',
  'recog.err.bad_response': 'Το μοντέλο απάντησε εκτός μορφής — δεν υπάρχει τι να διαβαστεί. Δοκίμασε ξανά ή στείλε λιγότερες εικόνες κάθε φορά.',
  'recog.err.fetch_url': 'Ο σύνδεσμος δεν άνοιξε — επικόλλησε κείμενο ή στιγμιότυπο.',
  'recog.err.bad_request': 'Δεν υπάρχει τίποτα προς αναγνώριση.',
  'recog.err.unknown': 'Η αναγνώριση απέτυχε. Δοκίμασε ξανά.',

  // ── Μαγεία παραλλαγών ───────────────────────────────────────────────────
  'magic.title': 'Μαγεία παραλλαγών',
  'magic.action': 'Μαγεία',
  'magic.tip': 'Φτιάξε τις καλύτερες παραλλαγές από τη βιβλιοθήκη',
  'magic.from': 'Από',
  'magic.from.placeholder': 'LCA ή Λάρνακα',
  'magic.to': 'Προς',
  'magic.to.placeholder': 'EVN ή Ερεβάν',
  'magic.roundTrip': 'Μετ’ επιστροφής',
  'magic.stay': 'Διαμονή, ημέρες',
  'magic.stay.from': 'από',
  'magic.stay.to': 'έως',
  'magic.stay.hint': 'Ημέρες ανάμεσα στην άφιξη στον προορισμό και την πτήση επιστροφής.',
  'magic.wishes': 'Επιθυμίες',
  'magic.wishes.placeholder': 'Χωρίς νυχτερινές ανταποκρίσεις, έως 500 $ το άτομο, το πολύ μία ανταπόκριση',
  'magic.count': 'Πόσες παραλλαγές',
  'magic.count.hint': 'Ελεύθερες θέσεις στον πίνακα: {count}',
  'magic.run': 'Κάνε τη μαγεία',
  'magic.running': 'Μαγεύουμε…',
  'magic.library': 'Στο αίτημα πηγαίνουν όλα τα μπλοκ της βιβλιοθήκης: {count}',
  'magic.library.empty': 'Η βιβλιοθήκη δεν έχει καμία πτήση — η μαγεία δεν έχει από τι να φτιάξει παραλλαγές.',
  'magic.library.trimmed': 'Η βιβλιοθήκη έχει {count} μπλοκ, στο επιλεγμένο μοντέλο χωρούν {limit} — τα υπόλοιπα δεν θα σταλούν.',
  'magic.privacy': 'Η λίστα των μπλοκ της βιβλιοθήκης και οι επιθυμίες σας πηγαίνουν στο επιλεγμένο μοντέλο AI. Οι παραλλαγές μπαίνουν στον πίνακα μόνο μετά την επιβεβαίωσή σας.',
  'magic.review': 'Ελέγξτε τις παραλλαγές πριν τις προσθέσετε',
  'magic.model': 'Μοντέλο: {model}',
  'magic.notes': 'Παρατηρήσεις',
  'magic.nothing': 'Δεν βγήκε καμία παραλλαγή — συγκεκριμενοποιήστε τις επιθυμίες ή προσθέστε περισσότερες πτήσεις στη βιβλιοθήκη.',
  'magic.add': 'Προσθήκη παραλλαγών ({count})',
  'magic.back': 'Πίσω',
  'magic.stayDays': 'Διαμονή: {days} ημ.',
  'magic.segments': 'Μπλοκ: {count}',
  'magic.noRoom': 'Δεν υπάρχει ελεύθερη θέση στον πίνακα — διαγράψτε κάποια παραλλαγή.',
  'magic.tooMany': 'Επιλέξατε περισσότερες παραλλαγές απ’ όσες χωρούν στον πίνακα: ξεμαρκάρετε τις περιττές.',
  'magic.off': 'Η μαγεία είναι απενεργοποιημένη από τον διαχειριστή.',
  'magic.off.guest': 'Η μαγεία είναι διαθέσιμη μετά τη σύνδεση.',
  'magic.err.unauthorized': 'Χρειάζεται σύνδεση στον λογαριασμό.',
  'magic.err.disabled': 'Οι λειτουργίες AI είναι απενεργοποιημένες από τον διαχειριστή.',
  'magic.err.no_model': 'Δεν έχει επιλεγεί μοντέλο για τη μαγεία — ορίστε το στη διαχείριση.',
  'magic.err.no_key': 'Δεν έχει οριστεί κλειδί παρόχου AI — ορίστε το στη διαχείριση.',
  'magic.err.bad_request': 'Το αίτημα δεν έγινε δεκτό: {message}',
  'magic.err.too_large': 'Η βιβλιοθήκη είναι πολύ μεγάλη για ένα αίτημα.',
  'magic.err.rate_limited': 'Πάρα πολλά αιτήματα. Δοκιμάστε αργότερα.',
  'magic.err.timeout': 'Το μοντέλο δεν απάντησε εγκαίρως. Δοκιμάστε ξανά.',
  'magic.err.provider': 'Ο πάροχος επέστρεψε σφάλμα: {message}',
  'magic.err.bad_response': 'Η απάντηση του μοντέλου δεν διαβάστηκε.',
  'magic.err.unknown': 'Η μαγεία δεν πέτυχε. Δοκιμάστε ξανά.',

  'admin.tab.users': 'Χρήστες',
  'admin.tab.ai': 'AI',
  'admin.tab.logs': 'Αρχείο καταγραφής',
  'admin.tab.chats': 'Συνομιλίες',
  'admin.tab.pricing': 'Τιμές',
  'admin.tab.trips': 'Ταξίδια',
  'admin.action.enter': 'Σύνδεση ως',
  'admin.action.limits': 'Όρια',
  'admin.limits.title': 'Όρια χρήστη',
  'admin.limits.hint': 'Κενό πεδίο — προεπιλεγμένη τιμή.',
  'admin.limits.default': 'προεπιλογή',
  'admin.limits.maxVariants': 'Εναλλακτικές στον πίνακα',
  'admin.limits.aiDaily': 'Αιτήματα ΤΝ την ημέρα',
  'admin.limits.magicDaily': 'Εκτελέσεις μαγείας την ημέρα',
  'admin.limits.maxTrips': 'Ταξίδια',
  'admin.logs.filter.problems': 'Σφάλματα και προειδοποιήσεις',
  'admin.logs.filter.ai': 'Αιτήματα ΤΝ',
  'admin.logs.filter.all': 'Όλα',
  'admin.logs.clear': 'Καθαρισμός αρχείου',
  'admin.logs.clear.confirm': 'Να διαγραφούν όλες οι εγγραφές του αρχείου;',
  'admin.logs.empty': 'Δεν υπάρχουν εγγραφές',
  'admin.logs.retention': 'Διατηρείται 7 ημέρες, καθημερινή εναλλαγή',
  'admin.logs.noDetails': 'Η εγγραφή δεν έχει λεπτομέρειες',
  'admin.logs.details.meta': 'Σύνοψη',
  'admin.logs.details.request': 'Αίτημα προς το μοντέλο',
  'admin.logs.details.response': 'Απάντηση μοντέλου',
  'admin.chats.new': 'Νέα συνομιλία',
  'admin.chats.search': 'Email χρήστη…',
  'admin.chats.empty': 'Δεν υπάρχουν συνομιλίες ακόμη',
  'admin.chats.pick': 'Επιλέξτε συνομιλία αριστερά',
  'admin.chats.start': 'Δεν υπάρχουν μηνύματα — γράψτε πρώτοι',
  'admin.chats.placeholder': 'Απάντηση στον χρήστη…',
  'admin.chats.send': 'Αποστολή',
  'admin.chats.clear': 'Διαγραφή συνομιλίας',
  'admin.chats.clear.confirm': 'Να διαγραφεί όλη η συνομιλία με {email};',
  'admin.chats.close': 'Ολοκλήρωση συνομιλίας',
  'admin.chats.close.confirm': 'Να ολοκληρωθεί η συνομιλία με {email}; Το ιστορικό θα παραμείνει ορατό μόνο στους διαχειριστές.',
  'admin.chats.lang': 'Γλώσσα χρήστη',
  'admin.chats.client': 'Πρόγραμμα χρήστη',
  'admin.chats.files': 'Αρχεία στη συνομιλία',
  'hero.title': 'Σχεδιάστε το ταξίδι σας σε εναλλακτικές',
  'hero.lead': 'Αυτό είναι ένα ταξίδι επίδειξης: τρεις διαδρομές δίπλα-δίπλα — τιμές, ανταποκρίσεις και προειδοποιήσεις σε έναν πίνακα. Δοκιμάστε το ή φτιάξτε το δικό σας· ο λογαριασμός τα αποθηκεύει όλα και ξεκλειδώνει τη μαγεία AI.',
  'hero.signup': 'Δημιουργία λογαριασμού',
  'hero.try': 'Απλώς δοκιμάστε',
  'support.title': 'Υποστήριξη',
  'support.close': 'Κλείσιμο',
  'support.minimize': 'Ελαχιστοποίηση',
  'support.closed': 'Η συνομιλία ολοκληρώθηκε',
  'support.attach': 'Επισύναψη αρχείου',
  'support.attach.bad': 'Μη αποδεκτό αρχείο: εικόνα ή PDF έως 3 MB',
  'support.empty': 'Γράψτε μας — θα απαντήσουμε εδώ',
  'support.placeholder': 'Μήνυμα…',
  'support.send': 'Αποστολή',
  'support.team': 'Υποστήριξη',
  'support.err': 'Δεν στάλθηκε — δοκιμάστε ξανά',
  'admin.stats.trips': 'Ταξίδια',
  'admin.stats.tripsShared': 'Με συνεργάτες',
  'admin.stats.tripsPublished': 'Δημοσιευμένα',
  'admin.trips.search': 'Αναζήτηση κατά όνομα ή ιδιοκτήτη',
  'admin.trips.col.trip': 'Ταξίδι',
  'admin.trips.col.owner': 'Ιδιοκτήτης',
  'admin.trips.col.members': 'Συνεργάτες',
  'admin.trips.col.share': 'Σύνδεσμος',
  'admin.trips.col.updated': 'Ενημερώθηκε',
  'admin.trips.col.created': 'Δημιουργήθηκε',
  'admin.trips.published': 'Άνοιγμα',
  'admin.trips.enter': 'Σύνδεση ως ιδιοκτήτης',
  'admin.trips.empty': 'Δεν βρέθηκαν ταξίδια',
  'admin.trips.view.title': 'Ταξίδι: {name}',
  'admin.trips.view.empty': 'Το ταξίδι είναι ακόμη άδειο.',
  'impersonate.confirm': 'Σύνδεση στον λογαριασμό {email}; Από εκεί ενεργείτε ως αυτός: ό,τι αλλάξετε, αλλάζει για εκείνον. Η ενέργεια καταγράφεται.',
  'impersonate.bar': 'Εργάζεστε ως {email}. Ο λογαριασμός σας: {admin}.',
  'impersonate.stop': 'Επιστροφή στον λογαριασμό μου',
  'auth.err.impersonating': 'Σε λειτουργία «ως άλλος χρήστης» η ενέργεια δεν είναι διαθέσιμη.',
  'admin.pricing.source': 'Εδώ αλλάζει το ρωσικό κείμενο και η δομή της σελίδας. Οι υπόλοιπες γλώσσες βρίσκονται στην καρτέλα «Μεταφράσεις».',
  'admin.tr.note': 'Αριστερά το πρωτότυπο, δεξιά η μετάφραση. Κενό πεδίο σημαίνει ότι ο επισκέπτης θα δει το πρωτότυπο.',
  'admin.tr.target': 'Γλώσσα μετάφρασης',
  'admin.tr.progress': 'μεταφράστηκαν {done} από {total}',
  'admin.tr.done': 'Μεταφράστηκε πλήρως',
  'admin.tr.onlyEmpty': 'Μόνο τα αμετάφραστα',
  'admin.tr.source': 'Πρωτότυπο',
  'admin.tr.translation': 'Μετάφραση',
  'admin.pricing.save': 'Αποθήκευση',
  'admin.pricing.saved': 'Αποθηκεύτηκε',
  'admin.pricing.lang': 'Γλώσσα',
  'admin.pricing.reset': 'Επαναφορά ενσωματωμένου',
  'admin.pricing.reset.title': 'Επαναφορά του ενσωματωμένου περιεχομένου;',
  'admin.pricing.reset.text': 'Όλες οι αλλαγές στη σελίδα τιμών θα διαγραφούν και η σελίδα θα ξαναχτιστεί από όσα υπάρχουν στον κώδικα. Η ενέργεια δεν αναιρείται.',
  'admin.pricing.updated': 'αλλαγή {date}, {name}',
  'admin.pricing.never': 'δεν έχει αλλάξει ακόμη',
  'admin.pricing.section.price': 'Τιμές',
  'admin.pricing.section.texts': 'Κείμενα σελίδας',
  'admin.pricing.section.sections': 'Ενότητες πίνακα',
  'admin.pricing.section.features': 'Γραμμές πακέτων',
  'admin.pricing.section.highlights': 'Στις κάρτες πακέτων',
  'admin.pricing.section.extras': 'Εφάπαξ αγορές',
  'admin.pricing.section.soon': 'Σε εξέλιξη',
  'admin.pricing.section.faq': 'Ερωτήσεις',
  'admin.pricing.currency': 'Νόμισμα',
  'admin.pricing.monthly': 'Τον μήνα',
  'admin.pricing.yearly': 'Τον χρόνο',
  'admin.pricing.discount': 'έκπτωση {percent}%',
  'admin.pricing.discount.none': 'ο χρόνος δεν είναι φθηνότερος από τον μήνα',
  'admin.pricing.id': 'Αναγνωριστικό',
  'admin.pricing.name': 'Όνομα',
  'admin.pricing.hint': 'Διευκρίνιση',
  'admin.pricing.question': 'Ερώτηση',
  'admin.pricing.answer': 'Απάντηση',
  'admin.pricing.price': 'Τιμή',
  'admin.pricing.parent': 'Ενότητα',
  'admin.pricing.free': 'Free',
  'admin.pricing.pro': 'Pro',
  'admin.pricing.shown': 'Εμφάνιση',
  'admin.pricing.add': 'Προσθήκη',
  'admin.pricing.remove': 'Αφαίρεση',
  'admin.pricing.up': 'Πάνω',
  'admin.pricing.down': 'Κάτω',
  'admin.pricing.kind.count': 'Αριθμός',
  'admin.pricing.kind.unlimited': 'Χωρίς όριο',
  'admin.pricing.kind.yes': 'Ναι',
  'admin.pricing.kind.none': 'Όχι',
  'admin.pricing.kind.text': 'Δικό σας κείμενο',
  'admin.pricing.per.total': 'συνολικά',
  'admin.pricing.per.month': 'τον μήνα',
  'admin.pricing.key': 'Κλειδί λεξικού',
  'admin.pricing.highlights.hint': 'Οι επιλεγμένες γραμμές εμφανίζονται στην κάρτα πακέτου στην κορυφή. Πέντε είναι το όριο αναγνωσιμότητας.',
  'admin.pricing.empty': 'Δεν υπάρχει τίποτα ακόμη',
  'admin.ai.title': 'Λειτουργίες AI',
  'admin.ai.hint': 'Η αναγνώριση πηγών, η μαγεία παραλλαγών και ο έλεγχος τιμών περνούν από πάροχο AI. Κάθε εργασία έχει το δικό της μοντέλο: το στιγμιότυπο θέλει μοντέλο με εικόνες, η μαγεία μοντέλο με συλλογισμό, οι τιμές μοντέλο με αναζήτηση στο web.',
  'admin.ai.enabled': 'Ενεργοποίηση λειτουργιών AI',
  'admin.ai.enabled.hint': 'Απενεργοποιημένο — μένει μόνο η τοπική ανάλυση κειμένου και η μαγεία παραλλαγών δεν είναι διαθέσιμη.',
  'admin.ai.key': 'Κλειδί παρόχου AI',
  'admin.ai.key.present': 'Το κλειδί ορίστηκε (…{tail})',
  'admin.ai.key.env': 'Το κλειδί έρχεται από μεταβλητή περιβάλλοντος του διακομιστή',
  'admin.ai.key.missing': 'Χωρίς κλειδί',
  'admin.ai.key.placeholder': 'sk-…',
  'admin.ai.key.hint': 'Το κλειδί μένει στον διακομιστή και δεν φτάνει ποτέ στον browser.',
  'admin.ai.key.clear': 'Διαγραφή κλειδιού',
  'admin.ai.source.screenshot': 'Στιγμιότυπο',
  'admin.ai.source.url': 'Σύνδεσμος',
  'admin.ai.source.text': 'Περιγραφή κειμένου',
  'admin.ai.source.screenshot.hint': 'Το μοντέλο πρέπει να δέχεται εικόνες.',
  'admin.ai.source.url.hint': 'Ο διακομιστής ανοίγει μόνος του τη σελίδα και δίνει το κείμενό της στο μοντέλο.',
  'admin.ai.source.text.hint': 'Επικολλημένα αποτελέσματα αναζήτησης ή δυο γραμμές στο χέρι.',
  'admin.ai.source.magic': 'Μαγεία: σύνθεση παραλλαγών',
  'admin.ai.source.magic.hint': 'Συνθέτει έτοιμες παραλλαγές ταξιδιού από τα μπλοκ της βιβλιοθήκης: επιλέγει πτήσεις, τις βάζει σε σειρά στον χρόνο και δεν τις αφήνει να επικαλύπτονται.',
  'admin.ai.source.prices': 'Τιμές: τρέχουσα τιμή πτήσης',
  'admin.ai.source.prices.hint': 'Ψάχνει στους ιστότοπους κρατήσεων την τιμή μιας συγκεκριμένης πτήσης και επιστρέφει τις προσφορές που βρήκε με σύνδεσμο στην πηγή.',
  'admin.ai.source.settings': 'Ρυθμίσεις αυτής της εργασίας',
  'admin.ai.source.settings.hint': 'Κενό πεδίο σημαίνει ότι ισχύει η κοινή τιμή από κάτω. Το ημερήσιο όριο εδώ μετριέται ξεχωριστά από το κοινό: υπερισχύει το αυστηρότερο.',
  'admin.ai.override.inherit': 'κοινή: {value}',
  'admin.ai.advanced.hint': 'Κοινές τιμές για όλες τις εργασίες. Κάθε εργασία μπορεί να τις αλλάξει στη δική της κάρτα.',
  'admin.ai.req.title': 'Απαιτήσεις του μοντέλου για τη μαγεία',
  'admin.ai.req.context': 'Πλαίσιο από 128K tokens: όλη η βιβλιοθήκη πηγαίνει σε ένα αίτημα.',
  'admin.ai.req.json': 'Υποστήριξη δομημένης απάντησης (json_schema) και ακριβής τήρηση του σχήματος.',
  'admin.ai.req.reasoning': 'Ισχυρός συλλογισμός: το μοντέλο συγκρίνει ώρες σε UTC, μετρά ανταποκρίσεις και σχεδιάζει τη διαδρομή.',
  'admin.ai.req.tokens': 'Η απάντηση είναι μεγάλη, γι’ αυτό για τη μαγεία ο διακομιστής ανεβάζει το όριο tokens τουλάχιστον στα 2000 και το χρονικό όριο στα 60 δευτερόλεπτα. Τα μοντέλα συλλογισμού ξοδεύουν το ίδιο όριο στη σκέψη — βάλτε 8000, αλλιώς η απάντηση έρχεται κομμένη.',
  'admin.ai.req.vision': 'Οι εικόνες δεν χρειάζονται: εδώ πηγαίνει μόνο κείμενο και ένα μοντέλο με όραση δεν προσφέρει κάτι.',
  'admin.ai.req.temp': 'Θερμοκρασία 0: η σύνθεση πρέπει να είναι αναπαραγώγιμη. Τα μοντέλα συλλογισμού (GPT-5) αγνοούν την παράμετρο — δεν πειράζει.',
  'admin.ai.req.recommend': 'Προτείνουμε μοντέλα επιπέδου pro/thinking (Gemini 2.5 Pro, Claude Sonnet 4.5, GPT-5). Τα φθηνά γρήγορα μοντέλα μπερδεύουν τη χρονολογική σειρά και επινοούν ανύπαρκτα id — ο διακομιστής απορρίπτει τέτοιες παραλλαγές και ο χρήστης βλέπει άδειο αποτέλεσμα.',
  'admin.ai.recommend': 'Φθηνά και επαρκή',
  'admin.ai.recommend.hint': 'Τιμές σε $ ανά 1M tokens στις {date}, από το φθηνότερο προς το πιο αξιόπιστο. Πρόταση, όχι περιορισμός: μπορείτε να επιλέξετε οποιοδήποτε μοντέλο. Πατήστε ένα για να συμπληρωθεί.',
  'admin.ai.model': 'Μοντέλο',
  'admin.ai.model.search': 'Αναζήτηση μοντέλων…',
  'admin.ai.model.none': 'Δεν επιλέχθηκε',
  'admin.ai.model.vision': 'εικόνες',
  'admin.ai.model.free': 'δωρεάν',
  'admin.ai.model.context': 'συμφραζόμενα {tokens}',
  'admin.ai.model.price': '{in} / {out} $ ανά 1M tokens',
  'admin.ai.model.custom': 'Χειροκίνητο id',
  'admin.ai.model.tools': 'εργαλεία',
  'admin.ai.model.reasoning': 'συλλογιστική',
  'admin.ai.model.nojson': 'χωρίς αυστηρό JSON',
  'admin.ai.model.warn.json': 'Δεν θα λειτουργήσει: ο διακομιστής στέλνει αυστηρό json_schema και αυτό το μοντέλο δεν το δέχεται, οπότε επιστρέφει σφάλμα παρόχου αντί για ανάλυση.',
  'admin.ai.model.warn.vision': 'Δεν θα λειτουργήσει: τα στιγμιότυπα στέλνονται ως εικόνες και αυτό το μοντέλο δέχεται μόνο κείμενο.',
  'admin.ai.model.warn.tools': 'Δεν θα λειτουργήσει: η αναζήτηση τιμών στον ιστό συνδέεται μέσω εργαλείων, τα οποία το μοντέλο δεν υποστηρίζει.',
  'admin.ai.model.warn.context': 'Μικρό πλαίσιο: όλη η βιβλιοθήκη μπλοκ πηγαίνει σε ένα αίτημα, χρειάζονται {min} ή περισσότερα.',
  'admin.ai.model.warn.reasoning': 'Αυτό το μοντέλο δεν απενεργοποιεί τη συλλογιστική, και αυτά τα tokens μετρούν στο ίδιο όριο απάντησης. Ορίστε μέγιστα tokens τουλάχιστον {tokens}, αλλιώς η απάντηση θα έρθει κομμένη.',
  'admin.ai.tune.recommend': 'Προτείνεται: {values}',
  'admin.ai.tune.apply': 'Εφαρμογή',
  'admin.ai.model.unknown': 'Το μοντέλο {model} δεν είναι στη λίστα του παρόχου',
  'admin.ai.models.reload': 'Ανανέωση λίστας',
  'admin.ai.models.loading': 'Φορτώνεται η λίστα μοντέλων…',
  'admin.ai.models.failed': 'Η λίστα μοντέλων δεν ήρθε: {message}',
  'admin.ai.models.count': 'Μοντέλα: {count}',
  'admin.ai.advanced': 'Για προχωρημένους',
  'admin.ai.maxTokens': 'Μέγιστα tokens απάντησης',
  'admin.ai.temperature': 'Θερμοκρασία',
  'admin.ai.timeout': 'Χρονικό όριο, δευτερόλεπτα',
  'admin.ai.limit': 'Αιτήματα ανά χρήστη την ημέρα',
  'admin.ai.maxImages': 'Στιγμιότυπα ανά αίτημα',
  'admin.ai.maxImages.hint': 'Πόσες εικόνες στέλνει ο χρήστης σε ένα αίτημα αναγνώρισης. Περισσότερες εικόνες σημαίνουν μακρύτερη και ακριβότερη απάντηση.',
  'admin.ai.save': 'Αποθήκευση',
  'admin.ai.saved': 'Αποθηκεύτηκε',
  'admin.ai.save.failed': 'Δεν αποθηκεύτηκε: {message}',
  'admin.ai.test': 'Δοκιμή',
  'admin.ai.test.running': 'Γίνεται δοκιμή…',
  'admin.ai.test.ok': 'Το μοντέλο απάντησε σε {ms} ms',
  'admin.ai.test.failed': 'Η δοκιμή απέτυχε: {message}',
  'admin.ai.updated': 'Άλλαξε {date}',

  // ── Ξενάγηση ────────────────────────────────────────────────────────────
  'onboarding.title': 'Πώς λειτουργεί',
  'onboarding.tip': 'Σύντομη ξενάγηση: πώς φτιάχνετε και συγκρίνετε εκδοχές ταξιδιού',
  'onboarding.step': 'Βήμα {current} από {total}',
  'onboarding.goto': 'Βήμα {n}',
  'onboarding.skip': 'Παράλειψη',
  'onboarding.back': 'Πίσω',
  'onboarding.next': 'Επόμενο',
  'onboarding.done': 'Το κατάλαβα',
  'onboarding.s1.title': 'Πρώτα το πού',
  'onboarding.s1.text': 'Ασία, Ευρώπη, Αμερική: φτιάξτε ένα ταξίδι για κάθε προορισμό που εξετάζετε. Στέκονται δίπλα δίπλα χωρίς να μπερδεύονται, και μπορείτε να ξεκινήσετε από έτοιμο πρότυπο.',
  'onboarding.s2.title': 'Μέσα στο ταξίδι',
  'onboarding.s2.text': 'Αριστερά η βιβλιοθήκη με τα μπλοκ: πτήσεις, διανυκτερεύσεις, μεταφορές, κάθε έξοδο. Δεξιά οι εκδοχές διαδρομής που συναρμολογείτε από αυτά.',
  'onboarding.s3.title': 'Σύνδεσμος, κείμενο ή στιγμιότυπο',
  'onboarding.s3.text': 'Δεν χρειάζεται να πληκτρολογήσετε την πτήση. Επικολλήστε σύνδεσμο κράτησης, αντιγραμμένα αποτελέσματα αναζήτησης ή στιγμιότυπο από email — η εφαρμογή βγάζει τη διαδρομή, τις ώρες και την τιμή, κι εσείς απλώς ελέγχετε.',
  'onboarding.s4.title': 'Δύο εκδοχές δίπλα δίπλα',
  'onboarding.s4.text': 'Τα φθηνά εισιτήρια έχουν ανταποκρίσεις, και ο ύπνος στον δρόμο κοστίζει. Οι απευθείας πτήσεις είναι ακριβότερες, αλλά δεν θέλουν διανυκτερεύσεις. Φτιάξτε και τις δύο εκδοχές και βάλτε τις δίπλα δίπλα.',
  'onboarding.s5.title': 'Το σύνολο αποφασίζει',
  'onboarding.s5.text': 'Κάθε εκδοχή αθροίζεται από κάτω: εισιτήρια, διαμονή και όλα τα υπόλοιπα. Εκεί φαίνεται ότι τα φθηνά εισιτήρια βγήκαν ακριβότερα.',
  'onboarding.s6.title': 'Σχεδιάστε μαζί',
  'onboarding.s6.text': 'Καλέστε συνεργάτη με email και δουλεύει στο ίδιο ακριβώς ταξίδι: τη βιβλιοθήκη, τις εκδοχές, τις τιμές. Μόνο ο ιδιοκτήτης μπορεί να το μετονομάσει και να καλέσει άλλους.',
  'onboarding.s7.title': 'Δείξτε το αποτέλεσμα',
  'onboarding.s7.text': 'Ο δημόσιος σύνδεσμος ανοίγει το ταξίδι σε όσους δεν έχουν λογαριασμό: μόνο ανάγνωση, αντίγραφο όπως ήταν κατά τη δημοσίευση. Οι επόμενες αλλαγές δεν φαίνονται μέχρι να πατήσετε «Ενημέρωση».',
  'onboarding.mock.asia': 'Ασία',
  'onboarding.mock.europe': 'Ευρώπη',
  'onboarding.mock.america': 'Αμερική',
  'onboarding.mock.cheap': 'Οικονομική',
  'onboarding.mock.cheap.note': 'φθηνά εισιτήρια, 2 διανυκτερεύσεις στον δρόμο',
  'onboarding.mock.direct': 'Απευθείας',
  'onboarding.mock.direct.note': 'ακριβότερα εισιτήρια, χωρίς διανυκτερεύσεις',
  'onboarding.mock.night.ath': 'Διανυκτέρευση στην Αθήνα',
  'onboarding.mock.night.mxp': 'Διανυκτέρευση στο Μιλάνο',
  'onboarding.mock.total': 'Σύνολο',
  'onboarding.mock.win': 'φθηνότερη κατά {sum}',

  'pricing.title': 'Τιμές',
  'pricing.subtitle': 'Ο σχεδιασμός ταξιδιού είναι δωρεάν. Το Pro αίρει τα όρια και προσθέτει όσα υπολογίζουν και παρακολουθούν για εσάς.',
  'pricing.seo.title': 'Τιμές — Free και Pro',
  'pricing.seo.description': 'Τι καλύπτει το δωρεάν πακέτο και τι προσθέτει το Pro: όρια ταξιδιών, αναγνώριση κρατήσεων, οδηγίες ανά χώρα και παρακολούθηση τιμών.',
  'pricing.back': 'Πίσω στον σχεδιαστή',
  'pricing.billing.monthly': 'Μηνιαία',
  'pricing.billing.yearly': 'Ετήσια',
  'pricing.billing.save': 'κερδίζετε {percent}%',
  'pricing.price.free': 'Δωρεάν',
  'pricing.price.perMonth': '{amount} / μήνα',
  'pricing.price.yearlyHint': '{amount} τον χρόνο, με μία χρέωση',
  'pricing.price.monthlyHint': 'Ακύρωση οποιαδήποτε στιγμή',
  'pricing.plan.free': 'Free',
  'pricing.plan.free.tagline': 'Ένα ταξίδι και σύγκριση δύο διαδρομών.',
  'pricing.plan.pro': 'Pro',
  'pricing.plan.pro.tagline': 'Για όσους πετούν συχνά και σχεδιάζουν στα σοβαρά.',
  'pricing.cta.free': 'Ξεκινήστε δωρεάν',
  'pricing.cta.pro': 'Αναβάθμιση σε Pro',
  'pricing.cta.soon': 'Πληρωμές σύντομα',
  'pricing.cta.note': 'Οι πληρωμές ετοιμάζονται ακόμη — γράψτε μας αν θέλετε να είστε στους πρώτους εκατό.',
  'pricing.compare': 'Τι περιλαμβάνεται',
  'pricing.compare.more': 'Όλες οι δυνατότητες · ακόμη {count}',
  'pricing.compare.less': 'Σύμπτυξη',
  'pricing.col.feature': 'Δυνατότητα',
  'pricing.value.unlimited': 'χωρίς όριο',
  'pricing.value.none': '—',
  'pricing.value.yes': 'ναι',
  'pricing.value.perMonth': '{count} / μήνα',
  'pricing.value.total': '{count}',
  'pricing.value.month1': '1 μήνας',
  'pricing.value.forever': 'χωρίς λήξη',
  'pricing.section.planner': 'Σχεδιαστής',
  'pricing.section.collab': 'Συνεργασία',
  'pricing.section.ai': 'Αναγνώριση και AI',
  'pricing.section.ai.hint': 'Κάθε κλήση μας κοστίζει πραγματικά χρήματα, γι’ αυτό εδώ υπάρχουν μετρητές και όχι «όσο θέλετε».',
  'pricing.section.advisor': 'Σύμβουλος',
  'pricing.section.export': 'Εξαγωγή',
  'pricing.section.watch': 'Παρακολούθηση και υπενθυμίσεις',
  'pricing.section.watch.hint': 'Λειτουργεί όσο η συνδρομή είναι ενεργή: πρόκειται για έλεγχο στο παρασκήνιο, όχι για μια εφάπαξ απάντηση.',
  'pricing.f.trips': 'Ταξίδια',
  'pricing.f.variants': 'Εναλλακτικές διαδρομές ανά ταξίδι',
  'pricing.f.library': 'Μπλοκ στη βιβλιοθήκη',
  'pricing.f.snapshots': 'Στιγμιότυπα ιστορικού',
  'pricing.f.currencies': 'Νομίσματα στον υπολογισμό',
  'pricing.f.coauthors': 'Συνεργάτες στο ταξίδι',
  'pricing.f.shareLife': 'Διάρκεια δημόσιου συνδέσμου',
  'pricing.f.voting': 'Ψηφοφορία για την επιλογή',
  'pricing.f.recognize': 'Αναγνώριση κράτησης, συνδέσμου ή στιγμιότυπου',
  'pricing.f.agent': 'Συνομιλία με τον σχεδιαστή AI',
  'pricing.f.alternatives': 'Εναλλακτικές για ένα μπλοκ',
  'pricing.f.connections': 'Έξυπνος έλεγχος ανταποκρίσεων',
  'pricing.f.mailIn': 'Προώθηση κράτησης στη διεύθυνση του ταξιδιού',
  'pricing.f.summary': 'Σύντομη περιγραφή του ταξιδιού',
  'pricing.f.dossier': 'Φάκελος χώρας: SIM, ενοικίαση, χρήματα',
  'pricing.f.visa': 'Βίζα για το διαβατήριό σας',
  'pricing.f.weather': 'Καιρός και εποχή στις ημερομηνίες',
  'pricing.f.local': 'Αργίες, απεργίες, τι είναι κλειστό',
  'pricing.f.tips': 'Λεπτομέρειες αεροδρομίων και αεροπορικών',
  'pricing.f.living': 'Ημερήσιος προϋπολογισμός ανά πόλη',
  'pricing.f.pdf': 'Εξαγωγή PDF χωρίς υδατογράφημα',
  'pricing.f.ics': 'Ημερολόγιο: αρχείο .ics και συνδρομή',
  'pricing.f.csv': 'Εξαγωγή σε Excel και CSV',
  'pricing.f.sheets': 'Αποστολή σε Google Sheets και Notion',
  'pricing.f.offline': 'Λειτουργία χωρίς ίντερνετ',
  'pricing.f.priceWatch': 'Παρακολούθηση τιμής πτήσης',
  'pricing.f.flightAlerts': 'Ειδοποιήσεις για αλλαγή πτήσης',
  'pricing.f.reminders': 'Υπενθυμίσεις: check-in, βίζα, διαβατήριο',
  'pricing.f.deadlines': 'Προθεσμίες δωρεάν ακύρωσης',
  'pricing.soon.title': 'Σε εξέλιξη',
  'pricing.soon.hint': 'Δεν έχει γίνει ακόμη και δεν περιλαμβάνεται σε κανένα πακέτο. Θα το ανακοινώσουμε ξεχωριστά.',
  'pricing.soon.optimizer': 'Βελτιστοποιητής διαδρομής: μετακίνηση ημερομηνιών και σειράς πόλεων',
  'pricing.soon.split': 'Μοιρασιά εξόδων μεταξύ ταξιδιωτών',
  'pricing.soon.routes': 'Διαφορετικές διαδρομές ανά ταξιδιώτη',
  'pricing.soon.roles': 'Δικαιώματα συνεργατών: προβολή χωρίς επεξεργασία',
  'pricing.soon.leaderboard': 'Κατάταξη διαδρομών από άλλους συντάκτες',
  'pricing.soon.affiliate': 'Αγορά εισιτηρίου απευθείας από το μπλοκ',
  'pricing.soon.api': 'API για τα ταξίδια σας',
  'pricing.soon.whitelabel': 'Δικός σας τομέας και λογότυπο για πρακτορεία',
  'pricing.extras.title': 'Χωρίς συνδρομή',
  'pricing.extras.hint': 'Αν πετάτε μία φορά τον χρόνο, δεν έχει νόημα να πληρώνετε κάθε μήνα. Αυτά αγοράζονται μία φορά και δεν λήγουν.',
  'pricing.extras.dossier': 'Φάκελος για μία χώρα',
  'pricing.extras.dossier.hint': 'Βίζα, συνδεσιμότητα, ενοικίαση αυτοκινήτου, χρήματα και λεπτομέρειες για έναν προορισμό.',
  'pricing.extras.pack': 'Πακέτο 50 αναγνωρίσεων',
  'pricing.extras.pack.hint': 'Κρατήσεις, σύνδεσμοι και στιγμιότυπα σε μπλοκ ταξιδιού. Χωρίς ημερομηνία λήξης.',
  'pricing.extras.price': '{amount} εφάπαξ',
  'pricing.faq.title': 'Ερωτήσεις',
  'pricing.faq.q1': 'Τι γίνεται με τα ταξίδια μου αν ακυρώσω το Pro;',
  'pricing.faq.a1': 'Δεν διαγράφεται τίποτα. Τα ταξίδια πάνω από το δωρεάν όριο γίνονται μόνο για ανάγνωση: μπορείτε πάντα να τα ανοίξετε, να τα δείτε και να τα εξαγάγετε, και να τα επεξεργαστείτε ξανά όταν επιστρέψετε στο Pro ή ελευθερώσετε θέση.',
  'pricing.faq.q2': 'Γιατί συνδρομή και όχι εφάπαξ πληρωμή;',
  'pricing.faq.a2': 'Ένα μέρος δουλεύει συνεχώς: παρακολουθεί την τιμή, εντοπίζει αλλαγή πτήσης, θυμίζει το check-in. Αυτό είναι μηνιαίο κόστος, όχι εφάπαξ. Ό,τι είναι πραγματικά εφάπαξ το πουλάμε εφάπαξ — δείτε «Χωρίς συνδρομή».',
  'pricing.faq.q3': 'Η αναγνώριση έκανε λάθος — χάθηκε το όριό μου;',
  'pricing.faq.a3': 'Αν η ανάλυση δεν έβγαλε κανένα μπλοκ, η προσπάθεια δεν μετράει. Χρεώνεται μόνο το αποτέλεσμα που πράγματι μπήκε στο ταξίδι σας.',
  'pricing.faq.q4': 'Τι θεωρείται μήνας;',
  'pricing.faq.a4': 'Τριάντα ημέρες από την ημερομηνία πληρωμής, όχι ο ημερολογιακός μήνας. Οι μετρητές μηδενίζουν την ίδια ημέρα.',

  // ── Лайфхаки ────────────────────────────────────────────────────────────
  'lh.title': 'Συμβουλές',
  'lh.subtitle': 'Σύντομες συμβουλές από όσους το έχουν ήδη πετάξει. Γράφει όποιος έχει συνδεθεί· στη σελίδα φτάνει ό,τι έχει ελεγχθεί.',
  'lh.seo.title': 'Συμβουλές ταξιδιωτών',
  'lh.seo.description': 'Συμβουλές για αεροδρόμια και ανταποκρίσεις, διαμονή, ενοικίαση αυτοκινήτου, φαγητό και αποσκευές, από όσους έχουν πάει.',
  'lh.back': 'Στον σχεδιαστή',
  'lh.loading': 'Φόρτωση…',
  'lh.empty': 'Δεν έχει δημοσιευτεί τίποτα ακόμη. Η δική σου μπορεί να είναι η πρώτη.',
  'lh.empty.mine': 'Δεν έχεις προτείνει τίποτα ακόμη.',
  'lh.author.anon': 'Χωρίς όνομα',
  'lh.delete': 'Διαγραφή',
  'lh.editorial': 'Από τη σύνταξη',
  'lh.lang.hint': 'Δεν υπάρχει ακόμη μετάφραση στη γλώσσα σου — η συμβουλή εμφανίζεται όπως γράφτηκε.',
  'lh.confirm': 'Επιβεβαίωση: σε μένα δούλεψε',
  'lh.confirmed': 'Έχεις επιβεβαιώσει αυτή τη συμβουλή',
  'lh.filter.category': 'Κατηγορία',
  'lh.filter.country': 'Χώρα',
  'lh.filter.mine': 'Δικές μου',
  'lh.any.category': 'Όλες οι κατηγορίες',
  'lh.any.country': 'Όλες οι χώρες',
  'lh.form.none': 'Δεν ορίστηκε',
  'lh.cat.flight': 'Πτήση',
  'lh.cat.lodging': 'Διαμονή',
  'lh.cat.car': 'Αυτοκίνητο',
  'lh.cat.food': 'Φαγητό',
  'lh.cat.luggage': 'Αποσκευές',
  'lh.status.pending': 'Σε έλεγχο',
  'lh.status.published': 'Δημοσιευμένη',
  'lh.status.rejected': 'Απορρίφθηκε',
  'lh.form.text': 'Η συμβουλή σου',
  'lh.form.placeholder': 'Για παράδειγμα: το αεροδρόμιο του Κουτάισι είναι μία μόνο αίθουσα, μία ώρα ανάμεσα στις πτήσεις φτάνει και περισσεύει.',
  'lh.form.category': 'Κατηγορία',
  'lh.form.country': 'Χώρα',
  'lh.form.submit': 'Πρότεινε',
  'lh.form.sent': 'Στάλθηκε για έλεγχο — θα εμφανιστεί στη σελίδα μόλις τη δει συντάκτης.',
  'lh.form.hint': 'Ένα γεγονός αντί για ιστορία: τις σύντομες συμβουλές τις διαβάζουν, τις μεγάλες τις προσπερνούν.',
  'lh.form.guest': 'Συμβουλή μπορεί να προσθέσει όποιος έχει συνδεθεί.',
  'lh.form.left': 'απομένουν {count}',
  'lh.err.text_short': 'Πολύ σύντομο — έτσι δεν βγάζει νόημα.',
  'lh.err.text_long': 'Πολύ μεγάλο. Κράτα ένα γεγονός και κόψε τα υπόλοιπα.',
  'lh.err.bad_country': 'Δεν αναγνωρίστηκε η χώρα.',
  'lh.err.bad_category': 'Δεν υπάρχει τέτοια κατηγορία.',
  'lh.err.too_many_pending': 'Έχεις ήδη αρκετές συμβουλές σε έλεγχο. Περίμενε απόφαση για αυτές.',
  'lh.err.rate_limited': 'Πολύ συχνά. Δοκίμασε αργότερα.',
  'admin.tab.lifehacks': 'Συμβουλές',
  'admin.lh.queue': 'Ουρά',
  'admin.lh.empty': 'Τίποτα σε αυτή την κατάσταση.',
  'admin.lh.publish': 'Δημοσίευση',
  'admin.lh.reject': 'Απόρριψη',
  'admin.lh.requeue': 'Πίσω στην ουρά',
  'admin.lh.delete': 'Διαγραφή',

  // ── Ενημέρωση τιμών πτήσεων ─────────────────────────────────────────────
  'prices.badge.cheaper': 'Στο {site} τώρα είναι {price} — λιγότερο από τη δική σου',
  'prices.badge.dearer': 'Στο {site} τώρα είναι {price} — περισσότερο από τη δική σου',
  'prices.badge.same': 'Στο {site} τώρα είναι {price} — όσο και η δική σου',
  'prices.badge.none': 'Δεν βρήκαμε τρέχουσα τιμή',
  'prices.badge.checked': 'Ελέγχθηκε {when}',
  'prices.action.refresh': 'Έλεγχος τιμής',
  'prices.action.refreshMany': 'Έλεγχος τιμών ({count})',
  'prices.action.history': 'Ιστορικό τιμών',
  'prices.action.apply': 'Βάλε αυτή την τιμή',
  'prices.busy': 'Ελέγχουμε την τιμή…',
  'prices.guest': 'Συνδεθείτε για να ελέγχετε τιμές',
  'prices.history.title': 'Ιστορικό τιμών',
  'prices.history.empty': 'Η τιμή δεν έχει ελεγχθεί ακόμη.',
  'prices.history.quotes': 'Τι βρήκαμε ({count})',
  'prices.history.exact': 'Η ίδια πτήση',
  'prices.history.applied': 'Μπήκε στο ταξίδι',
  'prices.history.check': 'Έλεγχος {when}',
  'prices.notfound': 'Σε αυτόν τον έλεγχο δεν βρήκαμε τρέχουσα τιμή.',
  'prices.trimmed': 'Ελέγχουμε το πολύ {count} πτήσεις τη φορά — οι υπόλοιπες μένουν με την προηγούμενη τιμή τους.',
  'prices.err.disabled': 'Ο διαχειριστής απενεργοποίησε τον έλεγχο τιμών.',
  'prices.err.no_key': 'Λείπει το κλειδί του συγκεντρωτή — όρισέ το στη διαχείριση.',
  'prices.err.rate_limited': 'Πάρα πολλά αιτήματα. Δοκίμασε αργότερα.',
  'prices.err.quota': 'Οι σημερινοί έλεγχοι τιμών εξαντλήθηκαν.',
  'prices.err.provider': 'Ο συγκεντρωτής δεν απάντησε. Δοκίμασε αργότερα.',
  'prices.err.bad_request': 'Λείπουν στοιχεία της πτήσης — δεν υπάρχει τι να αναζητηθεί.',
  'prices.err.unknown': 'Δεν έγινε ο έλεγχος τιμών. Δοκίμασε ξανά.',
  'admin.tab.prices': 'Τιμές',
  'admin.prices.title': 'Έλεγχος τιμών',
  'admin.prices.enabled': 'Ενεργοποίηση ελέγχου τιμών',
  'admin.prices.enabled.hint': 'Απενεργοποιημένο, το κουμπί ελέγχου δεν το βλέπει κανείς, ούτε οι επισκέπτες.',
  'admin.prices.providers': 'Πηγές τιμών',
  'admin.prices.provider.travelpayouts': 'Travelpayouts (Aviasales)',
  'admin.prices.provider.travelpayouts.hint': 'Δωρεάν συνεργατικό token. Επιστρέφει την κρυφή μνήμη αναζητήσεων που έχουν ήδη γίνει στο Aviasales, όχι ζωντανή αναζήτηση, και μόνο με ακριβή ταύτιση αριθμού πτήσης — η κενή απάντηση εδώ είναι φυσιολογική.',
  'admin.prices.provider.websearch': 'Αναζήτηση στο web από το μοντέλο',
  'admin.prices.provider.websearch.hint': 'Το μοντέλο περνάει μόνο του από τους ιστότοπους κρατήσεων: η ευρύτερη κάλυψη — και η μόνη πηγή που κοστίζει χρήματα σε κάθε αίτημα.',
  'admin.prices.provider.page': 'Η σελίδα της ίδιας της πτήσης',
  'admin.prices.provider.page.hint': 'Ξαναδιαβάζει τον σύνδεσμο που είναι αποθηκευμένος στην ίδια την πτήση. Το πεδίο του συνδέσμου είναι προαιρετικό, και οι ιστότοποι που σχεδιάζουν την τιμή με script δεν θα δώσουν τίποτα.',
  'admin.prices.provider.ready': 'έτοιμο',
  'admin.prices.provider.notReady': 'μη ρυθμισμένο',
  'admin.prices.token': 'Token Travelpayouts',
  'admin.prices.token.hint': 'Το token μένει στον διακομιστή και δεν δίνεται στον browser.',
  'admin.prices.token.present': 'Το token ορίστηκε (…{tail})',
  'admin.prices.token.clear': 'Διαγραφή του token',
  'admin.prices.token.cleared': 'Το token διαγράφηκε',
  'admin.prices.marker': 'Συνεργατικός marker',
  'admin.prices.marker.hint': 'Χωρίς marker οι συνεργατικοί σύνδεσμοι δεν αποφέρουν τίποτα.',
  'admin.prices.marker.needsToken': 'Αποθήκευσε πρώτα ένα token — ο marker αποθηκεύεται μόνο μαζί του.',
  'admin.prices.advanced': 'Για προχωρημένους',
  'admin.prices.maxFlights': 'Πτήσεις ανά αίτημα',
  'admin.prices.maxFlights.hint': 'Οι πτήσεις που περισσεύουν κόβονται με σημείωση αντί να απορρίπτεται όλο το αίτημα.',
  'admin.prices.sanity': 'Διάδρομος λογικών τιμών',
  'admin.prices.sanity.hint': 'Οι τιμές εκτός του εύρους «βάση × ελάχ. … βάση × μέγ.» απορρίπτονται: έτσι πιάνονται η τιμή ενός σκέλους αντί για το ζεύγος, το ξένο νόμισμα και η επινόηση του μοντέλου.',
  'admin.prices.sanityMin': 'Κάτω πολλαπλασιαστής',
  'admin.prices.sanityMax': 'Άνω πολλαπλασιαστής',
  'admin.prices.save': 'Αποθήκευση',
  'admin.prices.saved': 'Αποθηκεύτηκε',
  'admin.prices.updated': 'Άλλαξε {when}, {who}',
  'admin.prices.err.load': 'Δεν φορτώθηκαν οι ρυθμίσεις τιμών.',
  'admin.prices.err.save': 'Δεν αποθηκεύτηκαν οι ρυθμίσεις τιμών.',
  'admin.prices.err.range': 'Η τιμή είναι εκτός του επιτρεπτού εύρους.',
};

const ZH: Record<TranslationKey, string> = {
  'app.brand': '规划你的旅程',
  'app.language': '界面语言',
  'app.menu': '菜单',
  'menu.section.trip': '行程',
  'menu.section.trips': '行程列表',
  'menu.section.view': '显示',
  'menu.section.account': '账户',
  'app.library': '模块库',
  'app.board': '行程方案',
  'app.compare': '方案对比',
  'app.panel.hide': '隐藏：{name}',
  'app.panel.show': '显示：{name}',
  'app.panel.width': '「{name}」面板宽度',

  'toolbar.travelers': '出行人数',
  'toolbar.travelers.dec': '减少人数',
  'toolbar.travelers.inc': '增加人数',
  'toolbar.currency': '货币',
  'toolbar.currency.tip': '所有金额与对比所用货币',
  'toolbar.rates': '货币与汇率',
  'toolbar.rates.tip': '货币与汇率 — 以 {base} 计价',

  'person.title': '个性化',
  'person.tip': '个性化：配色主题与文字大小',
  'person.theme': '配色主题',
  'person.mode.light': '浅色',
  'person.mode.dark': '深色',
  'person.size': '文字大小',
  'person.size.compact': '紧凑',
  'person.size.dense': '密集',
  'person.size.default': '默认',
  'person.size.large': '较大',
  'person.size.larger': '更大',
  'person.size.xlarge': '特大',
  'person.size.huge': '最大',
  'person.space': '间距',
  'person.space.tight': '最紧凑',
  'person.space.dense': '紧凑',
  'person.space.default': '默认',
  'person.space.roomy': '宽松',
  'person.space.wide': '很宽松',
  'person.space.max': '最宽松',
  'person.note': '外观设置仅保存在此浏览器中，不会进入行程状态，也不会上传到服务器。',

  'currencies.title': '货币',
  'currencies.hint': '汇率表示一个单位折合多少 {base}。基准货币 {base} 恒为 1，且不可删除。',
  'currencies.code': '代码',
  'currencies.symbol': '符号',
  'currencies.rate': '对 {base} 汇率',
  'currencies.actions': '操作',
  'currencies.symbolOf': '{code} 的符号',
  'currencies.rateOf': '{code} 的汇率',
  'currencies.add': '添加',
  'currencies.remove.base': '基准货币不可删除',
  'currencies.remove.display': '该货币正用于显示',
  'currencies.remove.used': '已用于价格：{count}',
  'currencies.error.rate': '汇率须为大于 0 的数字',
  'currencies.add.currency': '货币',
  'currencies.add.rateLabel': '1 {code} = ? {base}',
  'currencies.add.empty': '列表中的货币都已添加。',
  'currency.name.USD': '美元',
  'currency.name.EUR': '欧元',
  'currency.name.GBP': '英镑',
  'currency.name.CHF': '瑞士法郎',
  'currency.name.JPY': '日元',
  'currency.name.CNY': '人民币',
  'currency.name.TRY': '土耳其里拉',
  'currency.name.AED': '阿联酋迪拉姆',
  'currency.name.GEL': '格鲁吉亚拉里',
  'currency.name.AMD': '亚美尼亚德拉姆',
  'currency.name.RUB': '俄罗斯卢布',
  'currency.name.KZT': '哈萨克斯坦坚戈',
  'currency.name.PLN': '波兰兹罗提',
  'currency.name.CZK': '捷克克朗',
  'currency.name.HUF': '匈牙利福林',
  'currency.name.RON': '罗马尼亚列伊',
  'currency.name.BGN': '保加利亚列弗',
  'currency.name.RSD': '塞尔维亚第纳尔',
  'currency.name.SEK': '瑞典克朗',
  'currency.name.NOK': '挪威克朗',
  'currency.name.DKK': '丹麦克朗',
  'currency.name.ILS': '以色列新谢克尔',
  'currency.name.THB': '泰铢',
  'currency.name.VND': '越南盾',
  'currency.name.INR': '印度卢比',
  'currency.name.IDR': '印尼盾',
  'currency.name.MYR': '马来西亚林吉特',
  'currency.name.SGD': '新加坡元',
  'currency.name.EGP': '埃及镑',
  'currency.name.MAD': '摩洛哥迪拉姆',

  'library.title': '模块库',
  'library.count.one': '{count} 个模块',
  'library.count.few': '{count} 个模块',
  'library.count.many': '{count} 个模块',
  'library.count.other': '{count} 个模块',
  'library.add': '+ 模块',
  'library.search': '搜索模块库',
  'library.search.placeholder': '搜索模块库…',
  'library.empty': '模块库为空',
  'library.empty.hint': '用「+ 模块」添加第一个模块，或解析一个链接。',
  'library.nothingFound': '没有找到',
  'library.converted': '已换算',
  'library.edit': '编辑',
  'library.delete': '删除',
  'library.clone': '克隆',
  'library.delete.title': '删除该模块？',
  'library.delete.used.one': '它将从 {count} 个方案中移除。此操作无法撤销。',
  'library.delete.used.few': '它将从 {count} 个方案中移除。此操作无法撤销。',
  'library.delete.used.many': '它将从 {count} 个方案中移除。此操作无法撤销。',
  'library.delete.used.other': '它将从 {count} 个方案中移除。此操作无法撤销。',
  'library.delete.unused': '该模块未被任何方案使用。此操作无法撤销。',
  'library.locked': '该模块属于已锁定的方案',
  'library.addTo': '添加到…',
  'library.addTo.pick': '选择方案',
  'library.addTo.none': '请先创建方案',
  'library.addTo.locked': '方案已锁定',

  'board.title': '方案',
  'variant.add': '+ 方案',
  'variant.add.short': '+',
  'variant.add.max': '最多 {count} 个方案',
  'variant.empty.title': '还没有方案',
  'variant.empty.hint': '创建一个方案，开始拼装路线。',
  'variant.name': '方案名称',
  'variant.rename': '重命名',
  'variant.collapse': '收起方案',
  'variant.expand': '展开方案',
  'variant.order': '方案顺序',
  'variant.moveUp': '上移方案',
  'variant.moveDown': '下移方案',
  'variant.lock': '锁定方案，禁止修改',
  'variant.unlock': '方案已锁定 — 点击以允许修改',
  'variant.locked': '方案已锁定',
  'variant.menu': '方案菜单',
  'variant.color': '方案颜色',
  'variant.colorOf': '颜色 {color}',
  'variant.clone': '复制方案',
  'variant.cloneName': '{name} — 副本',
  'variant.delete': '删除方案',
  'variant.delete.title': '删除该方案？',
  'variant.delete.body': '「{name}」将被删除，其模块仍留在模块库中。此操作无法撤销。',
  'variant.defaultName': '方案 {n}',

  'timeline.empty.title': '把第一段拖到这里',
  'timeline.empty.hint': '也可以用左侧模块卡片上的「添加到…」。',
  'timeline.copyHere': '复制到该方案',
  'timeline.remove': '移出',
  'timeline.moveUp': '上移',
  'timeline.moveDown': '下移',
  'timeline.lockedNote': '已锁定',
  'timeline.lockedNote.tip': '方案已锁定 — 请在标题栏解锁',
  'timeline.pairPrice': '含在往返价内',
  'timeline.dropToRemove': '放到这里可把模块移出方案',
  'timeline.layover': '中转 {duration}',
  'timeline.layover.unknown': '中转时长未知：缺少时区',
  'timeline.layover.transferCounted': '已计入交通',
  'timeline.layover.lodgingCounted': '已计入住宿',
  'timeline.layover.bothCounted': '已计入交通与住宿',
  'timeline.stay': '停留 {duration} — 休闲、旅行或商务',

  'summary.rides.one': '{count} 段接送',
  'summary.rides.few': '{count} 段接送',
  'summary.rides.many': '{count} 段接送',
  'summary.rides.other': '{count} 段接送',
  'summary.nights.one': '{count} 晚',
  'summary.nights.few': '{count} 晚',
  'summary.nights.many': '{count} 晚',
  'summary.nights.other': '{count} 晚',

  'totals.withBaggage': '含行李',
  'totals.perTraveler': '每人',
  'totals.flightTime': '飞行时长',
  'totals.doorToDoor': '门到门',
  'totals.nights': '住宿晚数',
  'totals.breakdown': '按类型拆分',
  'totals.converted': '已换算',
  'totals.warnings.one': '{count} 条提示',
  'totals.warnings.few': '{count} 条提示',
  'totals.warnings.many': '{count} 条提示',
  'totals.warnings.other': '{count} 条提示',

  'labels.hour': '小时',
  'labels.minute': '分',
  'labels.perNight': '/晚',
  'field.fromIata': '出发地（IATA）',
  'field.toIata': '目的地（IATA）',
  'field.departLocal': '出发（当地时间）',
  'field.arriveLocal': '到达（当地时间）',
  'field.priceScope': '价格类型',
  'field.linked': '配对的往返航段',
  'form.saveChanges': '保存',
  'form.create': '创建',
  'form.none': '— 无 —',
  'form.pickFlight': '— 选择航班 —',
  'baggage.noFlights': '模块库中还没有航班 — 请先添加一个。',
  'compare.best': '{value}，最优值',
  'compare.title': '方案对比',
  'compare.count.one': '{count} 个方案',
  'compare.count.few': '{count} 个方案',
  'compare.count.many': '{count} 个方案',
  'compare.count.other': '{count} 个方案',
  'compare.metric': '指标',
  'compare.total': '合计',
  'compare.totalWithBaggage': '含行李合计',
  'compare.flightTime': '纯飞行时长',
  'compare.doorToDoor': '门到门时长',
  'compare.nights': '住宿晚数',
  'compare.warnings': '提示数量',
  'compare.empty': '至少添加一个方案才能对比。',

  'form.close': '关闭',
  'form.cancel': '取消',
  'form.save': '保存',
  'form.confirm': '确认',
  'form.optional': '选填',
  'form.new': '新建模块',
  'form.edit': '编辑',
  'form.notFound': '未找到模块',
  'form.gone': '该模块已不在模块库中。',
  'form.decrease': '减少',
  'form.increase': '增加',

  'kind.flight': '航班',
  'kind.transfer': '城际交通',
  'kind.lodging': '住宿',
  'kind.taxi': '打车',
  'kind.food': '餐饮',
  'kind.baggage': '行李',
  'kind.misc': '其他',

  'kind.flight.desc': '机场之间的航段',
  'kind.transfer.desc': '城际班车、火车或大巴',
  'kind.lodging.desc': '酒店或公寓，按晚计价',
  'kind.taxi.desc': '市内出行',
  'kind.food.desc': '餐饮，按天计价',
  'kind.baggage.desc': '绑定到具体航班的付费行李',
  'kind.misc.desc': '其他任何开销',

  'scope.one_way': '单程价',
  'scope.round_trip_half': '往返价的一半',
  'scope.round_trip_full': '往返全价',
  'scope.pairNote': '价格只计一次 — 配对航段合计为 0。',

  'field.from': '出发地',
  'field.to': '目的地',
  'field.city': '城市',
  'field.label': '名称',
  'field.carrier': '承运人',
  'field.url': '航班或机票链接',
  'field.url.hint': '需要细节时回到哪里：航班页面、搜索结果或已购机票。',
  'field.url.short': '链接',
  'field.flightNumber': '航班号',
  'field.depart': '出发',
  'field.arrive': '到达',
  'field.duration': '时长（分钟）',
  'field.nights': '晚数',
  'field.days': '天数',
  'field.quantity': '数量',
  'field.price': '价格',
  'field.pricePerNight': '每晚价格',
  'field.perPerson': '按人计价',
  'field.wholeStay': '覆盖整个间隔',
  'field.wholeStay.hint': '住宿夜数自动计算：从上一航班到达至下一航班出发（最多 365 晚）。',
  'field.wholeStay.need': '当方案中该区块后面还有其他区块时可用。',
  'labels.wholeStay': '整个间隔',
  'field.flight': '航班',
  'field.note': '备注',
  'field.tzFrom': '出发时区',
  'field.tzTo': '到达时区',

  'error.iata': '机场代码须为 3 个拉丁字母',
  'error.departRequired': '请填写出发时间',
  'error.arriveRequired': '请填写到达时间',
  'error.departDateRequired': '请填写出发日期与时间',
  'error.arriveDateRequired': '请填写到达日期与时间',
  'error.arriveBeforeDepart': '到达须晚于出发',
  'error.priceNegative': '价格须为 ≥ 0 的数字',
  'error.fromRequired': '请填写出发地',
  'error.toRequired': '请填写目的地',
  'error.durationNegative': '时长须为 ≥ 0 的数字',
  'error.cityRequired': '请填写城市',
  'error.nightsMin': '至少 1 晚',
  'error.labelRequired': '请填写名称',
  'error.quantityMin': '至少 1',
  'error.flightRequired': '请选择航班',
  'warn.tzMissing': '未填时区 — 时长将不精确',
  'warn.tzMissingParse': '未识别时区 — 行程时长将不精确。',

  'parse.missing.from': '出发机场',
  'parse.missing.to': '到达机场',
  'parse.missing.fromTz': '出发时区',
  'parse.missing.toTz': '到达时区',
  'parse.missing.departLocal': '出发日期',
  'parse.missing.departTime': '出发时间',
  'parse.missing.arriveLocal': '到达日期',
  'parse.missing.arriveTime': '到达时间',
  'parse.missing.carrier': '承运人',
  'parse.missing.price': '价格',

  'library.empty.text': '有两种方式添加模块：手动填写，或粘贴从机票、酒店搜索网站复制的内容。',
  'library.noResults': '没有匹配「{query}」的结果',
  'library.resetSearch': '清除搜索',
  'library.group.flight': '航班',
  'library.group.transfer': '城际交通',
  'library.group.lodging': '住宿',
  'library.group.taxi': '打车',
  'library.group.food': '餐饮',
  'library.group.baggage': '行李',
  'library.group.misc': '其他',
  'compare.hint': '至少两个方案才能对比 — 再加一个即可看到最优值高亮。',
  'compare.caption': '行程方案对比：行为指标，列为方案',

  'parse.flightN': '航班 {n}',
  'parse.notRecognised': '未识别：{fields}。请检查并手动填写。',
  'parse.back': '返回',
  'parse.addSelected': '添加所选（{count}）',

  'sync.boot': '加载中…',
  'sync.local': '本地',
  'sync.saving': '保存中…',
  'sync.synced': '已保存',
  'sync.conflict': '服务器版本',
  'sync.rejected': '未保存',
  'sync.boot.tip': '正在从服务器读取状态',
  'sync.local.tip': '正在本地工作',
  'sync.saving.tip': '正在向服务器发送更改',
  'sync.synced.tip': '状态已存入服务器数据库',
  'sync.conflict.tip': '服务器上的状态已变化，已采用服务器版本',
  'sync.rejected.tip': '服务器拒绝了该状态',
  'toolbar.snapshots': '快照与历史',
  'sync.snapshot': '快照',
  'sync.snapshot.tip': '保存快照：当前状态将存入服务器数据库',
  'sync.history.tip': '快照历史：回到此前保存的状态',
  'sync.history': '历史',
  'sync.snapshots': '服务器上的快照',
  'sync.snapshot.saved': '快照已保存到服务器',
  'sync.snapshot.failed': '快照保存失败',
  'sync.snapshot.notFound': '未找到快照',
  'sync.snapshot.restored': '已从快照恢复状态',
  'sync.snapshot.empty': '还没有快照。「快照」会保存当前状态，方便日后回到这里。',
  'sync.snapshot.unnamed': '未命名',
  'sync.snapshot.label': '快照 {stamp}',

  'calcwarn.need_transfer': '需要城际交通 {from} → {to}',
  'calcwarn.risky_connection': '中转时间紧张 {duration}',
  'calcwarn.negative_connection': '无法衔接：起飞早于到达',
  'calcwarn.long_layover': '等待时间长 — 要加一晚住宿吗？',
  'calcwarn.orphan_baggage': '该方案中没有对应航班的行李',
  'calcwarn.rt_full_unpaired': '此价格为往返价',
  'calcwarn.missing_tz': '时区未知 — 未计算时长',

  'auth.signin': '登录',
  'auth.signup': '注册',
  'auth.signout': '退出',
  'auth.email': '邮箱',
  'auth.password': '密码',
  'auth.password.new': '新密码',
  'auth.password.current': '当前密码',
  'auth.password.change': '修改密码',
  'auth.password.changed': '密码已修改。',
  'auth.name': '姓名',
  'auth.submit.signup': '创建账户',
  'auth.forgot.link': '忘记密码？',
  'auth.forgot.title': '找回密码',
  'auth.forgot.hint': '我们会把设置新密码的链接发送到这个邮箱。',
  'auth.forgot.submit': '发送链接',
  'auth.forgot.sent': '如果该邮箱已注册，链接已经发出。链接一小时内有效。',
  'auth.forgot.back': '返回登录',
  'auth.reset.title': '新密码',
  'auth.reset.submit': '保存密码',
  'auth.reset.done': '密码已修改。之前的登录状态全部失效，请重新登录。',
  'auth.verify.title': '邮箱验证',
  'auth.verify.banner': '请验证邮箱 — 否则无法找回账户。',
  'auth.verify.resend': '再次发送邮件',
  'auth.verify.sent': '邮件已发送 — 请查收。',
  'auth.verify.ok': '邮箱已验证。',
  'auth.verify.fail': '链接无效或已过期。',
  'auth.guest.hint': '未登录时，行程只保存在这个浏览器里。',
  'auth.subtitle': '有了账户，行程就不只留在这个浏览器里，任何设备都能打开。',
  'auth.tabs.label': '登录或注册',
  'auth.password.show': '显示密码',
  'auth.password.hide': '隐藏密码',
  'auth.capslock': 'Caps Lock 已开启',
  'auth.busy': '请稍候…',
  'auth.toApp': '返回规划器',
  'auth.pw.rule': '至少 10 个字符。',
  'auth.pw.short': '太短：至少需要 10 个字符',
  'auth.pw.long': '太长',
  'auth.pw.common': '太常见 — 这是最先被试的密码',
  'auth.pw.same_as_email': '密码不能与邮箱相同',
  'auth.err.invalid_credentials': '邮箱或密码不正确。',
  'auth.err.invalid_email': '请检查邮箱地址。',
  'auth.err.weak_password': '这个密码不行。',
  'auth.err.email_taken': '该邮箱已注册。',
  'auth.err.blocked': '账户已被封禁。',
  'auth.err.rate_limited': '尝试次数过多，请稍后再试。',
  'auth.err.bad_token': '链接无效或已过期。',
  'auth.err.unauthorized': '请先登录。',
  'auth.err.forbidden': '权限不足。',
  'auth.err.self_change': '不能修改自己的角色或状态。',
  'auth.err.origin': '出于安全原因，请求被拒绝。',
  'auth.err.network': '服务器无法访问。',
  'auth.err.unknown': '出了点问题。',
  'auth.err.restorable': '这个邮箱曾经有过账号——可以恢复。',
  'auth.restore.title': '恢复账号',
  'auth.restore.hint': '这个邮箱曾经有过账号——输入之前的密码即可连同行程一起找回。',
  'auth.restore.submit': '恢复',
  'auth.restore.back': '返回注册',

  'account.title': '账户',
  'account.section.account': '账户',
  'account.appearance': '外观',
  'account.guest.title': '未登录',
  'account.emailState': '邮箱状态',
  'account.verified': '已验证',
  'account.unverified': '未验证',
  'account.save': '保存',
  'account.saved': '已保存',
  'account.admin': '管理后台',
  'account.menu.admin.hint': '用户与识别设置',
  'account.password.warning': '修改密码会退出其他所有登录。',
  'account.menu.language': '语言',
  'account.menu.language.hint': '界面语言',
  'account.menu.trips.hint': '您的全部行程',
  'account.menu.trips.hint.guest': '登录后可使用行程',
  'account.menu.pricing': '价格',
  'account.menu.pricing.hint': '套餐、价格与附加选项',
  'account.menu.settings': '设置',
  'account.menu.settings.hint': '密码、邮箱、注销账户',
  'account.menu.person.hint': '配色、字号与间距——按自己的喜好调整界面',
  'account.menu.signout.hint': '在本设备上结束登录',
  'account.delete.title': '注销账户',
  'account.delete.hint': '注销后，账户及其全部行程将立即无法访问。',
  'account.delete.action': '注销账户',
  'account.delete.confirm': '账户及其全部行程将无法访问。只有用同一邮箱重新注册才能找回。',


  'admin.title': '用户',
  'admin.back': '返回规划器',
  'admin.search': '按邮箱或姓名搜索',
  'admin.col.user': '用户',
  'admin.col.role': '角色',
  'admin.col.status': '状态',
  'admin.col.created': '注册时间',
  'admin.col.lastLogin': '最近登录',
  'admin.col.data': '数据',
  'admin.col.actions': '操作',
  'admin.role.user': '用户',
  'admin.role.admin': '管理员',
  'admin.status.active': '正常',
  'admin.status.blocked': '已封禁',
  'admin.status.deleted': '已删除',
  'admin.action.block': '封禁',
  'admin.action.unblock': '解除封禁',
  'admin.action.promote': '设为管理员',
  'admin.action.demote': '取消管理员',
  'admin.action.delete': '删除',
  'admin.action.view': '数据',
  'admin.delete.confirm': '删除 {email} 及其全部行程和快照？此操作无法撤销。',
  'admin.empty': '没有找到用户',
  'admin.prev': '上一页',
  'admin.next': '下一页',
  'admin.page': '{from}–{to} / 共 {total}',
  'admin.forbidden': '需要管理员权限。',
  'admin.stats.users': '用户总数',
  'admin.stats.newWeek': '本周新增',
  'admin.stats.activeWeek': '本周活跃',
  'admin.stats.verified': '已验证邮箱',
  'admin.stats.blocked': '已封禁',
  'admin.stats.snapshots': '快照',
  'admin.stats.size': '数据体积',
  'admin.view.title': '数据：{email}',
  'admin.view.readonly': '他人的数据，仅可查看。本次查看已记入日志。',
  'admin.view.empty': '该用户还没有保存任何内容。',
  'admin.view.variants': '方案',
  'admin.view.segments': '模块',
  'admin.view.showRaw': '显示 JSON',
  'admin.view.hideRaw': '隐藏 JSON',

  'sync.snapshot.beforeLogin': '登录前',
  'auth.guest.locked': '登录后可用',
  'variant.add.guest': '未登录时可创建 {count} 个方案。登录后可添加更多。',

  'share.title': '分享行程',
  // ── 行程 ────────────────────────────────────────────────────────────────
  'trips.title': '我的行程',
  'trips.all': '我的行程（{count}）',
  'trips.none': '尚未创建',
  'trips.untitled': '未命名',
  'trips.newName': '新行程',
  'trips.create': '+ 新行程',
  'trips.open': '已打开',
  'trips.openAction': '打开',
  'trips.rename': '重命名',
  'trips.rename.ownerOnly': '只有行程的所有者可以重命名',
  'trips.name': '行程名称',
  'trips.copyLink': '复制链接',
  'trips.shared': '已有公开链接',
  'trips.linkClosed': '没有公开链接',
  'trips.info.button': '行程「{name}」的详情',
  'trips.info.title': '关于「{name}」',
  'trips.updated': '{date} 修改',
  'trips.created': '{date} 创建',
  'trips.delete': '删除',
  'trips.delete.title': '删除该行程？',
  'trips.delete.text': '行程「{name}」及其历史将被永久删除，其公开链接也将失效。',
  'trips.empty': '还没有行程。创建第一个，之后的改动就会保存到服务器。',
  'trips.guest.title': '需要账号',
  'trips.guest': '多个行程需要登录：访客的数据只保存在本浏览器，且只有一个行程。',
  'trips.back': '返回规划器',
  'trips.section.mine': '我的行程',
  'trips.section.shared': '共享给我的',
  'trips.section.shared.hint': '你被邀请成为协作者：内容可以修改，但行程本身归所有者管理。',
  'trips.byOwner': '所有者：{name}',
  'trips.members.one': '{count} 位协作者',
  'trips.members.few': '{count} 位协作者',
  'trips.members.many': '{count} 位协作者',
  'trips.members.other': '{count} 位协作者',
  'trips.leave': '退出',
  'trips.leave.title': '退出该行程？',
  'trips.leave.text': '行程「{name}」将从你的列表中消失。所有者仍然保留它，并可以再次邀请你。',

  // ── 共享访问 ─────────────────────────────────────────────────────────────
  'trips.access': '访问权限',
  'trips.access.title': '行程「{name}」的访问权限',
  'trips.access.lead': '协作者编辑的是同一个行程：素材库、方案和价格。只有所有者可以重命名行程、发布链接和邀请他人。',
  'trips.access.invite': '通过邮箱邀请',
  'trips.access.add': '邀请',
  'trips.access.hint': '已有账户：行程会立刻出现在对方列表中。没有账户：会收到邀请邮件，注册后即获得访问权限。',
  'trips.access.pending': '{email} 还没有账户：我们已发送邀请邮件 — 注册后即可访问。',
  'trips.access.empty': '目前只有所有者。',
  'trips.access.you': '这是你',
  'trips.access.remove': '移除',
  'trips.access.role.owner': '所有者',
  'trips.access.role.editor': '协作者',
  'trips.access.err.no_user': '还没有使用该邮箱的用户。请对方先注册，然后再邀请。',
  'trips.access.err.already_member': '此人已在列表中。',
  'trips.access.err.is_owner': '这是行程的所有者。',
  'trips.access.err.too_many': '该行程的协作者已经太多了。',
  'trips.access.err.invalid_email': '请检查邮箱地址。',
  'trips.access.err.blocked': '该账户已被封禁。',
  'trips.access.err.unknown': '操作失败，请再试一次。',

  // ── 模板与常用模块 ────────────────────────────────────────────────────────
  'tpl.start': '从模板开始',
  'tpl.start.hint': '一键生成常见行程：模块已排好，价格留空。',
  'tpl.create': '创建行程',
  'tpl.fill': '生成方案',
  'tpl.page.note': '模板会新建一个独立行程，已有行程不会改动。',
  'tpl.board.note': '模板会新增一个包含这些模块的方案，已经搭好的内容保持不变。',
  'tpl.blocks': '常用模块',
  'tpl.blocks.hint': '点一下就把模块以零价加入模块库，填上金额即可。',
  'tpl.added': '模块“{name}”已加入模块库',
  'tpl.weekend.name': '两天周末游',
  'tpl.weekend.desc': '在城市住两晚：机场往返、住宿、餐饮和打车。',
  'tpl.beach.name': '海边一周',
  'tpl.beach.desc': '在一个地方住七晚：接送到酒店、餐饮和当地游玩。',
  'tpl.business.name': '出差',
  'tpl.business.desc': '因公住三晚：住宿、打车、餐饮和上网。',
  'tpl.twoCities.name': '一次两城',
  'tpl.twoCities.desc': '每座城市住三晚，再加上两城之间的交通。',
  'tpl.mountains.name': '雪山滑雪',
  'tpl.mountains.desc': '雪场旁住五晚：缆车票、装备租赁和餐饮。',
  'tpl.place.airport': '机场',
  'tpl.place.city': '市区',
  'tpl.place.hotel': '酒店',
  'tpl.place.city1': '第一座城市',
  'tpl.place.city2': '第二座城市',
  'tpl.block.taxi': '市内打车',
  'tpl.block.food': '餐饮',
  'tpl.block.insurance': '保险',
  'tpl.block.esim': '通信上网',
  'tpl.block.tickets': '游玩与门票',
  'tpl.block.visa': '签证与手续费',
  'tpl.block.skipass': '缆车票',
  'tpl.block.gear': '装备租赁',
  'share.tip': '给没有账户的人分享行程链接',
  'share.hint': '链接展示的是发布那一刻的行程副本，只能查看、不能编辑。之后的修改在你点击“更新”之前不会公开。',
  'share.create': '创建链接',
  'share.update': '更新',
  'share.revoke': '停用链接',
  'share.copy': '复制',
  'share.copied': '已复制 ✓',
  'share.link': '行程链接',
  'share.look': '链接会按你当前的主题、字号和语言打开，对方看到的行程和你看到的完全一样。',
  'share.updated': '更新于：{date}',
  'share.views': '浏览：{count}',
  'share.failed': '链接创建失败。',
  'share.readonly': '仅查看',
  'share.travelers': '出行人数：{count}',
  'share.cheapest': '最便宜',
  'share.segments': '价格由什么构成',
  'share.noPrice': '还没有填价格',
  'share.notFound': '链接不存在或已停用。',
  'share.empty': '该行程还没有方案。',
  'share.makeYours': '规划你自己的行程',
  'share.preview.variants.one': '{count} 个方案',
  'share.preview.variants.few': '{count} 个方案',
  'share.preview.variants.many': '{count} 个方案',
  'share.preview.variants.other': '{count} 个方案',
  'share.preview.from': '{price} 起',
  'share.preview.tail': '在 Plan Your Trip 比较价格与时间。',
  'share.preview.empty': '行程还在筹备中：暂无方案。',

  'library.new': 'new',
  'library.new.title': '最近添加',
  'library.new.count': '新增：{count}',
  'library.new.dismiss': '标记为已看',
  'library.fromSource': '来自来源',
  'library.fromSource.desc': '可以从文本、链接或截图中识别航班。',

  'recog.title': '从来源添加',
  'recog.tab.text': '描述',
  'recog.tab.url': '链接',
  'recog.tab.screenshot': '截图',
  'recog.text.label': '搜索结果或简短的航班描述',
  'recog.text.placeholder': '12月27日 Wizz Air 拉纳卡 11:55 → 埃里温 16:00，149 美元',
  'recog.url.label': '航班或订单链接',
  'recog.url.placeholder': 'https://…',
  'recog.url.invalid': '需要 http:// 或 https:// 链接',
  'recog.shot.label': '搜索结果、邮件或机票的截图',
  'recog.shot.drop': '拖入图片、粘贴（Ctrl+V）或选择文件',
  'recog.shot.pick': '选择文件',
  'recog.shot.add': '再添加',
  'recog.shot.remove': '全部移除',
  'recog.shot.removeOne': '移除第 {num} 张截图',
  'recog.shot.tooBig': '图片超过 {limit} MB',
  'recog.shot.tooMany': '每次最多 {limit} 张图片，未添加 {count} 张',
  'recog.shot.tooBigTotal': '所有图片合计不超过 {limit} MB，未添加 {count} 张',
  'recog.shot.badType': '这看起来不是可读的图片：可用 PNG、JPEG、WebP 或 HEIC',
  'recog.shot.ready': '图片：{count} / {limit} · {size}',
  'recog.shot.multi': '多张图片会在一次请求中一起发送，所有航班汇总到同一个列表。',
  'recog.run': '识别',
  'recog.running': '识别中…',
  'recog.review': '添加前请检查并修改',
  'recog.model': '模型：{model}',
  'recog.local': '本地解析，未用 AI',
  'recog.fallback': 'AI 没有响应，显示的是本地解析结果。',
  'recog.privacy.ai': '文本、链接或图片会发送到你选定的 AI 模型。不会自动保存任何内容：添加前都可以检查和修改。',
  'recog.privacy.local': '不发起任何网络请求：只解析粘贴的文本。添加前都可以检查和修改。',
  'recog.off': '管理员关闭了 AI 识别，仅保留本地文本解析。',
  'recog.off.guest': '登录后才能使用 AI 识别。',
  'recog.nothing': '没有找到航班，换一段文本、链接或截图试试。',
  'recog.notes': '解析说明',
  'recog.err.unauthorized': '需要先登录。',
  'recog.err.disabled': '管理员关闭了识别功能。',
  'recog.err.no_model': '该来源还没有选择模型，请在管理后台设置。',
  'recog.err.no_key': '缺少 AI 服务商密钥，请在管理后台设置。',
  'recog.err.provider': '服务商返回错误：{message}',
  'recog.err.timeout': '模型没有及时响应，请再试一次。',
  'recog.err.rate_limited': '请求过于频繁，请稍后再试。',
  'recog.err.too_large': '请求过大。',
  'recog.err.bad_response': '模型的回复不符合格式，无法解析。请重试，或每次少发几张图片。',
  'recog.err.fetch_url': '打不开该链接，请改用文本或截图。',
  'recog.err.bad_request': '没有可识别的内容。',
  'recog.err.unknown': '识别失败，请再试一次。',

  // ── 魔法方案 ─────────────────────────────────────────────────────────────
  'magic.title': '魔法方案',
  'magic.action': '魔法',
  'magic.tip': '用素材库生成最佳方案',
  'magic.from': '出发地',
  'magic.from.placeholder': 'LCA 或 拉纳卡',
  'magic.to': '目的地',
  'magic.to.placeholder': 'EVN 或 埃里温',
  'magic.roundTrip': '往返',
  'magic.stay': '停留天数',
  'magic.stay.from': '从',
  'magic.stay.to': '到',
  'magic.stay.hint': '抵达目的地到返程起飞之间的天数。',
  'magic.wishes': '偏好',
  'magic.wishes.placeholder': '不要夜间中转，每人不超过 500 美元，最多一次转机',
  'magic.count': '生成几个方案',
  'magic.count.hint': '面板剩余位置：{count}',
  'magic.run': '开始施法',
  'magic.running': '施法中…',
  'magic.library': '素材库的全部区块都会随请求发送：{count}',
  'magic.library.empty': '素材库里没有航班，魔法无从生成方案。',
  'magic.library.trimmed': '素材库有 {count} 个区块，所选模型能容纳 {limit} 个——其余不会发送。',
  'magic.privacy': '素材库区块列表和你的偏好会发送给所选的 AI 模型。方案只有在你确认后才会进入面板。',
  'magic.review': '添加前请检查方案',
  'magic.model': '模型：{model}',
  'magic.notes': '备注',
  'magic.nothing': '没能拼出方案——请细化偏好或往素材库里添加更多航班。',
  'magic.add': '添加方案（{count}）',
  'magic.back': '返回',
  'magic.stayDays': '停留：{days} 天',
  'magic.segments': '区块：{count}',
  'magic.noRoom': '面板没有空位了——请先删除多余方案。',
  'magic.tooMany': '选中的方案超过面板容量：请取消多余的勾选。',
  'magic.off': '管理员已关闭魔法。',
  'magic.off.guest': '登录后即可使用魔法。',
  'magic.err.unauthorized': '需要登录账户。',
  'magic.err.disabled': '管理员已关闭 AI 功能。',
  'magic.err.no_model': '魔法尚未选择模型——请在管理后台设置。',
  'magic.err.no_key': '未设置 AI 服务商密钥——请在管理后台设置。',
  'magic.err.bad_request': '请求未被接受：{message}',
  'magic.err.too_large': '素材库太大，单次请求装不下。',
  'magic.err.rate_limited': '请求过于频繁，请稍后再试。',
  'magic.err.timeout': '模型未能及时响应，请再试一次。',
  'magic.err.provider': '服务商返回错误：{message}',
  'magic.err.bad_response': '无法解析模型的回复。',
  'magic.err.unknown': '魔法失败了，请再试一次。',

  'admin.tab.users': '用户',
  'admin.tab.ai': 'AI',
  'admin.tab.logs': '日志',
  'admin.tab.chats': '聊天',
  'admin.tab.pricing': '价格',
  'admin.tab.trips': '行程',
  'admin.action.enter': '以其身份登录',
  'admin.action.limits': '限额',
  'admin.limits.title': '用户限额',
  'admin.limits.hint': '留空表示默认值。',
  'admin.limits.default': '默认',
  'admin.limits.maxVariants': '看板上的方案数',
  'admin.limits.aiDaily': '每日 AI 请求数',
  'admin.limits.magicDaily': '每日魔法次数',
  'admin.limits.maxTrips': '行程数',
  'admin.logs.filter.problems': '错误与警告',
  'admin.logs.filter.ai': 'AI 请求',
  'admin.logs.filter.all': '全部',
  'admin.logs.clear': '清空日志',
  'admin.logs.clear.confirm': '删除所有日志记录？',
  'admin.logs.empty': '暂无记录',
  'admin.logs.retention': '保留 7 天，每日轮换',
  'admin.logs.noDetails': '该记录没有详细信息',
  'admin.logs.details.meta': '摘要',
  'admin.logs.details.request': '发送给模型的请求',
  'admin.logs.details.response': '模型响应',
  'admin.chats.new': '新对话',
  'admin.chats.search': '用户邮箱…',
  'admin.chats.empty': '暂无对话',
  'admin.chats.pick': '在左侧选择一个对话',
  'admin.chats.start': '还没有消息 — 先写一条吧',
  'admin.chats.placeholder': '回复用户…',
  'admin.chats.send': '发送',
  'admin.chats.clear': '删除对话',
  'admin.chats.clear.confirm': '删除与 {email} 的全部对话？',
  'admin.chats.close': '结束对话',
  'admin.chats.close.confirm': '结束与 {email} 的对话？历史记录将仅对管理员可见。',
  'admin.chats.lang': '用户语言',
  'admin.chats.client': '用户客户端',
  'admin.chats.files': '聊天附件',
  'hero.title': '用多个方案规划旅行',
  'hero.lead': '这是演示行程：三条路线并排对比 — 价格、中转和提醒都在同一块看板上。随意试用或创建自己的行程；注册账户可保存一切并解锁 AI 魔法。',
  'hero.signup': '创建账户',
  'hero.try': '直接试用',
  'support.title': '客服支持',
  'support.close': '关闭',
  'support.minimize': '收起',
  'support.closed': '对话已结束',
  'support.attach': '添加附件',
  'support.attach.bad': '文件不符合要求：图片或 PDF，最大 3 MB',
  'support.empty': '给我们留言 — 我们会在这里回复',
  'support.placeholder': '消息…',
  'support.send': '发送',
  'support.team': '客服',
  'support.err': '发送失败 — 请重试',
  'admin.stats.trips': '行程',
  'admin.stats.tripsShared': '有协作者',
  'admin.stats.tripsPublished': '已发布',
  'admin.trips.search': '按名称或所有者搜索',
  'admin.trips.col.trip': '行程',
  'admin.trips.col.owner': '所有者',
  'admin.trips.col.members': '协作者',
  'admin.trips.col.share': '链接',
  'admin.trips.col.updated': '更新时间',
  'admin.trips.col.created': '创建时间',
  'admin.trips.published': '打开',
  'admin.trips.enter': '以所有者身份登录',
  'admin.trips.empty': '未找到行程',
  'admin.trips.view.title': '行程：{name}',
  'admin.trips.view.empty': '该行程还是空的。',
  'impersonate.confirm': '登录 {email} 的账号？之后你以其身份操作：你改的一切都会改在他那里。此操作会记入日志。',
  'impersonate.bar': '你正在以 {email} 的身份操作。你的账号：{admin}。',
  'impersonate.stop': '返回我的账号',
  'auth.err.impersonating': '在「以他人身份」模式下无法执行该操作。',
  'admin.pricing.source': '这里改的是俄语文案和页面结构。其他语言在「翻译」标签页。',
  'admin.tr.note': '左边是原文，右边是译文。留空表示访客看到的是原文。',
  'admin.tr.target': '目标语言',
  'admin.tr.progress': '已翻译 {done} / {total}',
  'admin.tr.done': '已全部翻译',
  'admin.tr.onlyEmpty': '只看未翻译的',
  'admin.tr.source': '原文',
  'admin.tr.translation': '译文',
  'admin.pricing.save': '保存',
  'admin.pricing.saved': '已保存',
  'admin.pricing.lang': '语言',
  'admin.pricing.reset': '恢复内置内容',
  'admin.pricing.reset.title': '恢复内置内容？',
  'admin.pricing.reset.text': '价格页的全部改动都会删除，页面重新按代码里的内容生成。此操作无法撤销。',
  'admin.pricing.updated': '{date} 由 {name} 修改',
  'admin.pricing.never': '尚未修改过',
  'admin.pricing.section.price': '价格',
  'admin.pricing.section.texts': '页面文案',
  'admin.pricing.section.sections': '表格分区',
  'admin.pricing.section.features': '套餐条目',
  'admin.pricing.section.highlights': '套餐卡片上显示',
  'admin.pricing.section.extras': '一次性购买',
  'admin.pricing.section.soon': '正在做',
  'admin.pricing.section.faq': '常见问题',
  'admin.pricing.currency': '货币',
  'admin.pricing.monthly': '每月',
  'admin.pricing.yearly': '每年',
  'admin.pricing.discount': '省 {percent}%',
  'admin.pricing.discount.none': '按年并不比按月便宜',
  'admin.pricing.id': '标识',
  'admin.pricing.name': '名称',
  'admin.pricing.hint': '说明',
  'admin.pricing.question': '问题',
  'admin.pricing.answer': '回答',
  'admin.pricing.price': '价格',
  'admin.pricing.parent': '分区',
  'admin.pricing.free': 'Free',
  'admin.pricing.pro': 'Pro',
  'admin.pricing.shown': '显示',
  'admin.pricing.add': '添加',
  'admin.pricing.remove': '删除',
  'admin.pricing.up': '上移',
  'admin.pricing.down': '下移',
  'admin.pricing.kind.count': '数字',
  'admin.pricing.kind.unlimited': '不限',
  'admin.pricing.kind.yes': '有',
  'admin.pricing.kind.none': '无',
  'admin.pricing.kind.text': '自定义文本',
  'admin.pricing.per.total': '总计',
  'admin.pricing.per.month': '每月',
  'admin.pricing.key': '词典键',
  'admin.pricing.highlights.hint': '勾选的条目会出现在页面顶部的套餐卡片里。五条是可读性的上限。',
  'admin.pricing.empty': '还没有内容',
  'admin.ai.title': 'AI 功能',
  'admin.ai.hint': '来源识别、方案魔法与价格核查都通过 AI 服务商运行。每项任务各用各的模型：截图需要能读图的模型，魔法需要会推理的模型，价格需要支持联网搜索的模型。',
  'admin.ai.enabled': '启用 AI 功能',
  'admin.ai.enabled.hint': '关闭后只剩本地文本解析，方案魔法不可用。',
  'admin.ai.key': 'AI 服务商密钥',
  'admin.ai.key.present': '已设置密钥（…{tail}）',
  'admin.ai.key.env': '密钥来自服务器的环境变量',
  'admin.ai.key.missing': '未设置密钥',
  'admin.ai.key.placeholder': 'sk-…',
  'admin.ai.key.hint': '密钥保存在服务器上，绝不会发送到浏览器。',
  'admin.ai.key.clear': '删除密钥',
  'admin.ai.source.screenshot': '截图',
  'admin.ai.source.url': '链接',
  'admin.ai.source.text': '文字描述',
  'admin.ai.source.screenshot.hint': '模型必须支持图片输入。',
  'admin.ai.source.url.hint': '服务器自己打开页面，把正文交给模型。',
  'admin.ai.source.text.hint': '粘贴的搜索结果或手写的几行字。',
  'admin.ai.source.magic': '魔法：方案编排',
  'admin.ai.source.magic.hint': '用素材库的区块拼出现成的行程方案：挑选航班、按时间排序，并保证航班互不重叠。',
  'admin.ai.source.prices': '价格：航班的当前价格',
  'admin.ai.source.prices.hint': '在订票网站上查找某一具体航班的价格，返回找到的报价并附上来源链接。',
  'admin.ai.source.settings': '该任务的设置',
  'admin.ai.source.settings.hint': '留空表示沿用下方的通用值。这里的每日上限与通用上限分开计算，以更严格的为准。',
  'admin.ai.override.inherit': '通用：{value}',
  'admin.ai.advanced.hint': '所有任务的通用值。每个任务都可以在自己的卡片里单独设置。',
  'admin.ai.req.title': '魔法模型的要求',
  'admin.ai.req.context': '上下文 128K 起：整个素材库会一次性放进请求。',
  'admin.ai.req.json': '支持结构化输出（json_schema）并严格遵守 schema。',
  'admin.ai.req.reasoning': '较强推理：模型要比较 UTC 时间、计算中转并规划路线。',
  'admin.ai.req.tokens': '回复较长，因此魔法的令牌上限服务器至少提到 2000，超时提到 60 秒。推理模型的思考也占用同一预算——请设为 8000，否则回复会被截断。',
  'admin.ai.req.vision': '不需要图像：这里只发送文本，视觉模型没有优势。',
  'admin.ai.req.temp': '温度 0：编排结果应可复现。GPT-5 这类推理模型会忽略该参数，属正常。',
  'admin.ai.req.recommend': '建议使用 pro/thinking 级别的模型（Gemini 2.5 Pro、Claude Sonnet 4.5、GPT-5）。廉价的快速模型会弄乱时间顺序并编造不存在的 id——服务器会丢弃这类方案，用户只会看到空结果。',
  'admin.ai.recommend': '便宜且够用',
  'admin.ai.recommend.hint': '价格为每 100 万 tokens 的美元数，数据截至 {date}，按从最便宜到最可靠排列。这是建议而非限制：可以选择任何模型。点击即可填入。',
  'admin.ai.model': '模型',
  'admin.ai.model.search': '搜索模型…',
  'admin.ai.model.none': '未选择',
  'admin.ai.model.vision': '图片',
  'admin.ai.model.free': '免费',
  'admin.ai.model.context': '上下文 {tokens}',
  'admin.ai.model.price': '每 100 万 token {in} / {out} 美元',
  'admin.ai.model.custom': '手动填写 id',
  'admin.ai.model.tools': '工具',
  'admin.ai.model.reasoning': '推理',
  'admin.ai.model.nojson': '不支持严格 JSON',
  'admin.ai.model.warn.json': '无法使用：服务器发送严格的 json_schema，而该模型不接受，只会返回供应商错误而非解析结果。',
  'admin.ai.model.warn.vision': '无法使用：截图以图片形式发送，而该模型只接受文本。',
  'admin.ai.model.warn.tools': '无法使用：比价的网络搜索通过工具接入，该模型不支持工具。',
  'admin.ai.model.warn.context': '上下文不足：整个素材库要一次性放进请求，至少需要 {min}。',
  'admin.ai.model.warn.reasoning': '该模型无法关闭推理，推理 tokens 会占用同一个回复上限。请将最大 tokens 设为不低于 {tokens}，否则回复会被截断。',
  'admin.ai.tune.recommend': '建议：{values}',
  'admin.ai.tune.apply': '填入',
  'admin.ai.model.unknown': '服务商列表里没有模型 {model}',
  'admin.ai.models.reload': '刷新列表',
  'admin.ai.models.loading': '正在加载模型列表…',
  'admin.ai.models.failed': '获取模型列表失败：{message}',
  'admin.ai.models.count': '模型数：{count}',
  'admin.ai.advanced': '高级',
  'admin.ai.maxTokens': '回复最大 token 数',
  'admin.ai.temperature': '温度',
  'admin.ai.timeout': '超时（秒）',
  'admin.ai.limit': '每人每天请求数',
  'admin.ai.maxImages': '每次请求的截图数',
  'admin.ai.maxImages.hint': '识别截图时用户一次请求可发送多少张图片。图片越多，模型的回答越长、越贵。',
  'admin.ai.save': '保存',
  'admin.ai.saved': '已保存',
  'admin.ai.save.failed': '保存失败：{message}',
  'admin.ai.test': '测试',
  'admin.ai.test.running': '测试中…',
  'admin.ai.test.ok': '模型在 {ms} 毫秒内响应',
  'admin.ai.test.failed': '测试失败：{message}',
  'admin.ai.updated': '修改于 {date}',

  // ── 导览 ────────────────────────────────────────────────────────────────
  'onboarding.title': '这是怎么用的',
  'onboarding.tip': '简短导览：如何搭建并比较行程方案',
  'onboarding.step': '第 {current} 步，共 {total} 步',
  'onboarding.goto': '第 {n} 步',
  'onboarding.skip': '跳过',
  'onboarding.back': '上一步',
  'onboarding.next': '下一步',
  'onboarding.done': '知道了',
  'onboarding.s1.title': '先决定去哪',
  'onboarding.s1.text': '亚洲、欧洲、美洲——每个还在考虑的方向都单独建一个行程。它们并排放着，互不干扰，也可以直接从模板起步。',
  'onboarding.s2.title': '行程内部',
  'onboarding.s2.text': '左边是积木库：航班、住宿、接送，以及任何一笔开销。右边是你用它们搭出来的路线方案。',
  'onboarding.s3.title': '链接、文字或截图',
  'onboarding.s3.text': '航班不用手工录入。粘贴预订链接、复制的搜索结果，或是邮件截图——应用会读出航线、时间和价格，你只需核对一遍。',
  'onboarding.s4.title': '两个方案并排放',
  'onboarding.s4.text': '便宜机票带着中转，而路上过夜同样要花钱。直飞更贵，却不用住宿。把两个方案都搭出来，并排比较。',
  'onboarding.s5.title': '总价说了算',
  'onboarding.s5.text': '每个方案底部会算出全部费用：机票、住宿和其余所有。到这里才发现，便宜机票反而更贵。',
  'onboarding.s6.title': '可以一起规划',
  'onboarding.s6.text': '用邮箱邀请协作者，对方编辑的就是同一个行程：积木库、方案、价格。重命名行程和邀请他人只有所有者能做。',
  'onboarding.s7.title': '把结果给别人看',
  'onboarding.s7.text': '公开链接让没有账户的人也能查看行程：只读，且是发布那一刻的副本。在你点击“更新”之前，之后的改动对读者不可见。',
  'onboarding.mock.asia': '亚洲',
  'onboarding.mock.europe': '欧洲',
  'onboarding.mock.america': '美洲',
  'onboarding.mock.cheap': '经济',
  'onboarding.mock.cheap.note': '机票便宜，路上住 2 晚',
  'onboarding.mock.direct': '直飞',
  'onboarding.mock.direct.note': '机票更贵，无需住宿',
  'onboarding.mock.night.ath': '雅典住一晚',
  'onboarding.mock.night.mxp': '米兰住一晚',
  'onboarding.mock.total': '合计',
  'onboarding.mock.win': '便宜 {sum}',

  'pricing.title': '价格',
  'pricing.subtitle': '规划行程免费。Pro 取消限额，并加入替你计算和盯梢的那些功能。',
  'pricing.seo.title': '价格 — Free 与 Pro',
  'pricing.seo.description': '免费方案包含什么，Pro 又多了什么：行程额度、订单识别、国家出行提示和价格盯梢。',
  'pricing.back': '返回规划器',
  'pricing.billing.monthly': '按月',
  'pricing.billing.yearly': '按年',
  'pricing.billing.save': '省 {percent}%',
  'pricing.price.free': '免费',
  'pricing.price.perMonth': '{amount} / 月',
  'pricing.price.yearlyHint': '每年 {amount}，一次扣款',
  'pricing.price.monthlyHint': '随时可以取消',
  'pricing.plan.free': 'Free',
  'pricing.plan.free.tagline': '做一次行程，比较两条线路。',
  'pricing.plan.pro': 'Pro',
  'pricing.plan.pro.tagline': '给经常飞、认真规划的人。',
  'pricing.cta.free': '免费开始',
  'pricing.cta.pro': '升级到 Pro',
  'pricing.cta.soon': '付款功能即将上线',
  'pricing.cta.note': '收款还在配置中——想进首批一百人请给我们留言。',
  'pricing.compare': '包含什么',
  'pricing.compare.more': '全部功能 · 还有 {count} 项',
  'pricing.compare.less': '收起',
  'pricing.col.feature': '功能',
  'pricing.value.unlimited': '不限',
  'pricing.value.none': '—',
  'pricing.value.yes': '有',
  'pricing.value.perMonth': '{count} / 月',
  'pricing.value.total': '{count}',
  'pricing.value.month1': '1 个月',
  'pricing.value.forever': '长期有效',
  'pricing.section.planner': '规划器',
  'pricing.section.collab': '协作',
  'pricing.section.ai': '识别与 AI',
  'pricing.section.ai.hint': '每次调用都要花我们真金白银，所以这里是计数，而不是「随便用」。',
  'pricing.section.advisor': '出行顾问',
  'pricing.section.export': '导出',
  'pricing.section.watch': '盯梢与提醒',
  'pricing.section.watch.hint': '订阅有效期内持续运行：这是后台检查，不是一次性的回答。',
  'pricing.f.trips': '行程数',
  'pricing.f.variants': '每个行程的线路方案',
  'pricing.f.library': '素材库中的模块',
  'pricing.f.snapshots': '历史快照',
  'pricing.f.currencies': '参与计算的货币',
  'pricing.f.coauthors': '行程协作者',
  'pricing.f.shareLife': '公开链接有效期',
  'pricing.f.voting': '为方案投票',
  'pricing.f.recognize': '识别订单、链接或截图',
  'pricing.f.agent': '与 AI 规划助手对话',
  'pricing.f.alternatives': '为某个模块找替代方案',
  'pricing.f.connections': '智能中转检查',
  'pricing.f.mailIn': '把订单邮件转发到行程专属地址',
  'pricing.f.summary': '行程的文字概述',
  'pricing.f.dossier': '国家档案：电话卡、租车、货币',
  'pricing.f.visa': '按你的护照查签证',
  'pricing.f.weather': '所选日期的天气与季节',
  'pricing.f.local': '节日、罢工、哪些地方关门',
  'pricing.f.tips': '机场与航司的门道',
  'pricing.f.living': '按城市的每日预算',
  'pricing.f.pdf': '导出 PDF，无水印',
  'pricing.f.ics': '日历：.ics 文件与订阅',
  'pricing.f.csv': '导出到 Excel 和 CSV',
  'pricing.f.sheets': '发送到 Google Sheets 和 Notion',
  'pricing.f.offline': '无网络也能用',
  'pricing.f.priceWatch': '盯航班价格',
  'pricing.f.flightAlerts': '航班变动提醒',
  'pricing.f.reminders': '提醒：值机、签证、护照',
  'pricing.f.deadlines': '免费取消的截止日',
  'pricing.soon.title': '正在做',
  'pricing.soon.hint': '尚未完成，也不属于任何套餐。做好了我们会单独说明。',
  'pricing.soon.optimizer': '线路优化：调整日期与城市顺序',
  'pricing.soon.split': '同行者之间分摊费用',
  'pricing.soon.routes': '不同同行者走不同路线',
  'pricing.soon.roles': '协作者权限：只看不改',
  'pricing.soon.leaderboard': '他人线路排行榜',
  'pricing.soon.affiliate': '直接从模块里订票',
  'pricing.soon.api': '自有行程的 API',
  'pricing.soon.whitelabel': '旅行社专用域名与标识',
  'pricing.extras.title': '不订阅也能买',
  'pricing.extras.hint': '一年飞一次没必要月月付费。这些一次买断，不过期。',
  'pricing.extras.dossier': '单个国家的档案',
  'pricing.extras.dossier.hint': '签证、通信、租车、货币和门道——只针对一个目的地。',
  'pricing.extras.pack': '50 次识别包',
  'pricing.extras.pack.hint': '把订单、链接和截图变成行程模块。没有有效期。',
  'pricing.extras.price': '{amount} 一次性',
  'pricing.faq.title': '常见问题',
  'pricing.faq.q1': '取消 Pro 之后我的行程会怎样？',
  'pricing.faq.a1': '什么都不会删。超出免费额度的行程转为只读：随时可以打开、查看和导出，等你回到 Pro 或腾出名额后就能再次编辑。',
  'pricing.faq.q2': '为什么是订阅而不是一次性付费？',
  'pricing.faq.a2': '有一部分是持续运行的：盯价格、发现航班改期、提醒值机。这是每月的成本，不是一次性的。真正一次性的东西我们就按一次性卖——见「不订阅也能买」。',
  'pricing.faq.q3': '识别出错了，额度会被扣吗？',
  'pricing.faq.a3': '如果解析没有产出任何模块，这次不计数。只有真正进入行程的结果才计费。',
  'pricing.faq.q4': '一个月怎么算？',
  'pricing.faq.a4': '从付款日算起的三十天，不是自然月。计数在同一天清零。',

  // ── Лайфхаки ────────────────────────────────────────────────────────────
  'lh.title': '经验',
  'lh.subtitle': '来自飞过这条线的人的简短经验。登录后谁都可以写，通过审核的才会出现在这里。',
  'lh.seo.title': '旅行者经验',
  'lh.seo.description': '关于机场与中转、住宿、租车、吃饭和行李的经验，来自真正去过的人。',
  'lh.back': '回到规划器',
  'lh.loading': '加载中…',
  'lh.empty': '还没有发布的内容。你的可以是第一条。',
  'lh.empty.mine': '你还没有提交过内容。',
  'lh.author.anon': '未署名',
  'lh.delete': '删除',
  'lh.editorial': '编辑部',
  'lh.lang.hint': '暂时没有你所用语言的译文，这里显示的是原文。',
  'lh.confirm': '确认：我这边有效',
  'lh.confirmed': '你已确认这条经验',
  'lh.filter.category': '分类',
  'lh.filter.country': '国家',
  'lh.filter.mine': '我的',
  'lh.any.category': '全部分类',
  'lh.any.country': '全部国家',
  'lh.form.none': '未选择',
  'lh.cat.flight': '航班',
  'lh.cat.lodging': '住宿',
  'lh.cat.car': '租车',
  'lh.cat.food': '吃饭',
  'lh.cat.luggage': '行李',
  'lh.status.pending': '待审核',
  'lh.status.published': '已发布',
  'lh.status.rejected': '已拒绝',
  'lh.form.text': '你的经验',
  'lh.form.placeholder': '例如：库塔伊西机场就一个大厅，中转留一小时绰绰有余。',
  'lh.form.category': '分类',
  'lh.form.country': '国家',
  'lh.form.submit': '提交',
  'lh.form.sent': '已提交审核——编辑看过之后就会出现在页面上。',
  'lh.form.hint': '一条事实胜过一段故事：短的有人看，长的被划走。',
  'lh.form.guest': '登录之后谁都可以添加经验。',
  'lh.form.left': '还剩 {count}',
  'lh.err.text_short': '太短了，说不清楚。',
  'lh.err.text_long': '太长了。留一条事实，其余删掉。',
  'lh.err.bad_country': '无法识别这个国家。',
  'lh.err.bad_category': '没有这个分类。',
  'lh.err.too_many_pending': '你已经有几条在审核中了，等有结果再写。',
  'lh.err.rate_limited': '太频繁了，请稍后再试。',
  'admin.tab.lifehacks': '经验',
  'admin.lh.queue': '队列',
  'admin.lh.empty': '这个状态下没有内容。',
  'admin.lh.publish': '发布',
  'admin.lh.reject': '拒绝',
  'admin.lh.requeue': '退回队列',
  'admin.lh.delete': '删除',

  // ── 航班价格更新 ──────────────────────────────────────────────────────────
  'prices.badge.cheaper': '{site} 现在是 {price}，比你的低',
  'prices.badge.dearer': '{site} 现在是 {price}，比你的高',
  'prices.badge.same': '{site} 现在是 {price}，和你的一样',
  'prices.badge.none': '没有找到当前价格',
  'prices.badge.checked': '{when} 查过',
  'prices.action.refresh': '查一下价格',
  'prices.action.refreshMany': '查价格（{count}）',
  'prices.action.history': '价格记录',
  'prices.action.apply': '用这个价格',
  'prices.busy': '正在查价格…',
  'prices.guest': '登录后可查询价格',
  'prices.history.title': '价格记录',
  'prices.history.empty': '还没有查过价格。',
  'prices.history.quotes': '查到的结果（{count}）',
  'prices.history.exact': '同一班航班',
  'prices.history.applied': '已写入行程',
  'prices.history.check': '{when} 的查询',
  'prices.notfound': '这次查询没有找到当前价格。',
  'prices.trimmed': '一次最多查 {count} 个航班，其余的仍是原来的价格。',
  'prices.err.disabled': '管理员关闭了价格查询。',
  'prices.err.no_key': '缺少聚合服务密钥，请在管理后台设置。',
  'prices.err.rate_limited': '请求过于频繁，请稍后再试。',
  'prices.err.quota': '今天的查询次数已用完。',
  'prices.err.provider': '聚合服务没有响应，请稍后再试。',
  'prices.err.bad_request': '航班信息不全，没有可查询的内容。',
  'prices.err.unknown': '价格没能查成，请再试一次。',
  'admin.tab.prices': '价格',
  'admin.prices.title': '价格核查',
  'admin.prices.enabled': '开启价格核查',
  'admin.prices.enabled.hint': '关闭后，谁都看不到核查按钮，访客也一样。',
  'admin.prices.providers': '价格来源',
  'admin.prices.provider.travelpayouts': 'Travelpayouts（Aviasales）',
  'admin.prices.provider.travelpayouts.hint': '免费的合作方 token。它返回的是 Aviasales 已有搜索的缓存，而不是实时搜索，并且只按航班号精确匹配——这里返回空是常事。',
  'admin.prices.provider.websearch': '模型网页搜索',
  'admin.prices.provider.websearch.hint': '模型自己去逐个订票网站查看：覆盖面最广——也是唯一每次请求都要花钱的来源。',
  'admin.prices.provider.page': '航班自己的页面',
  'admin.prices.provider.page.hint': '重新读取航班上保存的链接。链接字段是可选的，而用脚本渲染价格的网站什么也给不出来。',
  'admin.prices.provider.ready': '已就绪',
  'admin.prices.provider.notReady': '未配置',
  'admin.prices.token': 'Travelpayouts token',
  'admin.prices.token.hint': 'token 存在服务器上，不会交给浏览器。',
  'admin.prices.token.present': 'token 已设置（…{tail}）',
  'admin.prices.token.clear': '删除 token',
  'admin.prices.token.cleared': 'token 已删除',
  'admin.prices.marker': '合作方 marker',
  'admin.prices.marker.hint': '没有 marker，联盟链接什么也赚不到。',
  'admin.prices.marker.needsToken': '请先保存 token——marker 只能和它一起保存。',
  'admin.prices.advanced': '高级',
  'admin.prices.maxFlights': '每次请求的航班数',
  'admin.prices.maxFlights.hint': '多出来的航班会被截掉并附上说明，而不是让整个请求失败。',
  'admin.prices.sanity': '合理价格区间',
  'admin.prices.sanity.hint': '超出「基准价 × 下限 … 基准价 × 上限」区间的价格会被丢弃：这样能挡住把单程价当成往返价、货币不对，以及模型的凭空编造。',
  'admin.prices.sanityMin': '下限倍数',
  'admin.prices.sanityMax': '上限倍数',
  'admin.prices.save': '保存',
  'admin.prices.saved': '已保存',
  'admin.prices.updated': '修改于 {when}，{who}',
  'admin.prices.err.load': '价格设置没能加载。',
  'admin.prices.err.save': '价格设置没能保存。',
  'admin.prices.err.range': '数值超出了允许范围。',
};

export const DICTIONARIES: Record<Lang, Record<TranslationKey, string>> = {
  ru: RU,
  en: EN,
  es: ES,
  el: EL,
  zh: ZH,
};

export type TranslateParams = Record<string, string | number>;

/**
 * Перевод по ключу. Если передан params.count, ключ уточняется формой
 * множественного числа: `key.one` / `key.few` / `key.many` / `key.other`
 * (категории берёт Intl.PluralRules — у русского их три, у китайского одна).
 */
export type Translate = (key: TranslationKey | string, params?: TranslateParams) => string;

export function interpolate(template: string, params?: TranslateParams): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (whole, name: string) =>
    name in params ? String(params[name]) : whole,
  );
}

export function createTranslator(lang: Lang): Translate {
  const dict = DICTIONARIES[lang] as Record<string, string | undefined>;
  const plural = new Intl.PluralRules(lang);

  return (key, params) => {
    let resolved = key as string;

    if (params && typeof params.count === 'number') {
      const candidate = `${key}.${plural.select(params.count)}`;
      resolved = dict[candidate] !== undefined ? candidate : `${key}.other`;
    }

    // Отсутствующий перевод показываем как сам ключ: пустая строка выглядит
    // как поломка вёрстки, а ключ сразу указывает, что чинить.
    const template = dict[resolved] ?? dict[key as string] ?? (key as string);
    return interpolate(template, params);
  };
}
